import React, { useEffect, useMemo, useRef, useState, useCallback, useContext } from 'react';
import { Sheet, Group, GROUP_COLORS, Pouch, POUCH_COLORS, PouchColorLegend, HistoryActionType } from '../types';
import ExcelJS from 'exceljs';
import {
  formatTooltip,
  formatTableGuidePouchColorKeysPcHtml,
  formatTableGuideGroupColorKeysPcHtml,
  formatKeyDescLine,
  formatKeysLeftWithDesc,
} from '../utils/tooltipFormatter';
import { resolvePouchColorHotkey, isEditableElement, isTableKeyboardContextActive, type PouchColorHotkeyAction } from '../utils/pouchColorHotkeys';
import { PouchValueEditInput } from './PouchValueEditInput';
import { clearLayerRemoveConfirming } from '../utils/layerRemoveButton';
import { resolveGroupColorHotkey, type GroupColorHotkeyAction } from '../utils/groupColorHotkeys';
import { getModifierKeys } from '../App';
import { shareOrDownloadBlob } from '../utils/shareDownloadFile';
import { GuideContext } from '../contexts/GuideContext';
import { useLocale } from '../contexts/LocaleContext';
import {
  clearSelectedCells,
  copySelectedCells,
  pasteSelectedCells,
  doubleClickEditColumn,
  doubleClickToggleColumn,
  deleteClearColumn,
  getAppCopy,
} from '../i18n';
import './styles/TableComponent.scss';
import { NumberCounter } from './NumberCounterComponent';
import {
  isInMuteGroup,
  isMuteGroup,
  shouldShowTotalLabel,
  calculateGroupSubtotal,
  getGroupIndex,
  getGroupSerialNumber,
  changeGroupColor,
  isInclusiveGtpSelfDisplay,
  getInclusiveGtpDisplayMultiplier,
  groupHasDivisionInclusiveGtp,
  getGroupTotalPouchDisplayBase,
  pouchCountsAsTimeForSheetDisplay,
} from '../logic/GroupLogic';
import { getPouchContributionForTotals } from '../logic/BalancingPouchLogic';
import { handleUnifiedExpression } from '../utils/ExpressionEvaluator';
import { getPouchContributionToSheetCount } from '../logic/StatisticsLogic';
import {
  getSortedLayers,
  getPrimaryLayer,
  getTableColumnLayout,
  resolveActiveLayerId,
  pouchWithLayerValue,
  mirrorPouchesForLayer,
  getLayerValue,
  getPouchEditInitialString,
  setPouchLayerValue,
  getPouchLayerRoundUnit,
  getPouchLayerMultiplier,
  getPouchEffectiveMultiplierForSheet,
  getPouchLayerIsTime,
  getPouchStoredExpression,
  isPouchExpressionOutdatedForSheet,
  LAYER_HEADER_ROW_INDEX,
  isLayerHeaderRow,
} from '../logic/LayerLogic';
import { SheetLayerTableHeader } from './SheetLayerTableHeader';
import { TableCellEditShell } from './TableCellEditShell';
import { LayerSlideRoundUnit } from './LayerSlideRoundUnit';
import { useLayerSwitch } from '../context/LayerSwitchContext';

// 貼り付け時の数式妥当性チェック関数
const isValidFormulaForPaste = (str: string): boolean => {
    // 許可文字: 数字、演算子、空白、ピリオド、コロン等
    const allowedChars = /[0-9+\-*/×÷xX^%:~＋－＊／＝％：～?='().\s]/;
    
    // 許可されていない文字が3文字連続で出現するかチェック
    let consecutiveInvalid = 0;
    for (let i = 0; i < str.length; i++) {
        if (!allowedChars.test(str[i])) {
            consecutiveInvalid++;
            if (consecutiveInvalid >= 3) {
                return false; // 無効な数式
            }
        } else {
            consecutiveInvalid = 0;
        }
    }
    return true; // 有効な数式
};

/** テーブル内ガイド用: ホバー対象が変わったとき tier を初期化するための安定キー */
function getTableGuideHoverAnchorKey(target: EventTarget | null): string | null {
    let el: HTMLElement | null = null;
    if (target instanceof HTMLElement) {
        el = target;
    } else if (target instanceof Text && target.parentElement) {
        el = target.parentElement;
    }
    if (!el) {
        return null;
    }
    const headerBtn = el.closest('.table-header button');
    if (headerBtn) {
        const cls = headerBtn.className.replace(/\s+/g, ' ').trim();
        return `hdr:${cls}`;
    }
    const barTr = el.closest('tr.group-color-bar');
    if (barTr) {
        const gid = barTr.getAttribute('data-group-id');
        return gid ? `bar:${gid}` : 'bar';
    }
    const cell = el.closest('table.pouch-table td[data-cell-index], table.pouch-table th[data-cell-index]');
    if (cell) {
        const tr = cell.closest('tr[data-row-index]');
        if (tr) {
            const ri = tr.getAttribute('data-row-index');
            const ci = cell.getAttribute('data-cell-index');
            if (ri != null && ci != null) {
                return `cell:${ri}:${ci}`;
            }
        }
    }
    if (el.closest('.table-selection-resize-handle')) {
        return 'resize-handle';
    }
    return 'table-misc';
}

/** グループヘッダー行（th）選択時の色・C ローテ・M ミュートを groups に適用（ungrouped はスキップ） */
function applyGroupColorHotkeyToGroups(
    baseGroups: Group[],
    groupIds: string[],
    action: GroupColorHotkeyAction,
): Group[] {
    let result = baseGroups;
    for (const gid of groupIds) {
        if (gid === 'ungrouped') {
            continue;
        }
        if (action.type === 'direct') {
            if (!GROUP_COLORS.some(c => c.id === action.colorId)) {
                continue;
            }
            result = result.map(g =>
                g.id === gid ? { ...g, color: action.colorId, originalColor: undefined } : g,
            );
        } else if (action.type === 'rotate') {
            result = changeGroupColor(gid, result, action.reverse, true);
        }
        // muteToggle はテーブルではキー側で無効化（計算エリアのグループは別経路）
    }
    return result;
}

/** パウチ行選択時の色ホットキーを pouches に適用 */
function applyPouchColorHotkeyToPouches(
    basePouches: Pouch[],
    pouchIds: string[],
    action: PouchColorHotkeyAction,
): Pouch[] {
    if (pouchIds.length === 0) {
        return basePouches;
    }
    const pouchColors: { pouchId: string; color: string }[] = [];
    if (action.type === 'direct') {
        for (const id of pouchIds) {
            pouchColors.push({ pouchId: id, color: action.colorId });
        }
    } else {
        for (const id of pouchIds) {
            const pouch = basePouches.find(p => p.id === id);
            if (!pouch) {
                continue;
            }
            const currentColorIndex = POUCH_COLORS.findIndex(c => c.id === pouch.color);
            let nextColorId: string;
            if (action.reverse) {
                if (currentColorIndex === 0) {
                    nextColorId = POUCH_COLORS[POUCH_COLORS.length - 1].id;
                } else {
                    const prevColorIndex =
                        (currentColorIndex - 1 + POUCH_COLORS.length) % POUCH_COLORS.length;
                    nextColorId = POUCH_COLORS[prevColorIndex].id;
                }
            } else {
                const nextColorIndex = (currentColorIndex + 1) % POUCH_COLORS.length;
                nextColorId =
                    currentColorIndex === -1
                        ? POUCH_COLORS[0].id
                        : POUCH_COLORS[nextColorIndex].id;
            }
            pouchColors.push({ pouchId: id, color: nextColorId });
        }
    }
    if (pouchColors.length === 0) {
        return basePouches;
    }
    return basePouches.map(p => {
        const u = pouchColors.find(pc => pc.pouchId === p.id);
        return u ? { ...p, color: u.color } : p;
    });
}

interface TableComponentProps {
  isOpen: boolean;
  onToggle: () => void;
  groups: Group[]; 
  pouches: Pouch[]; 
  colorLegends?: PouchColorLegend[]; 
  currentSheet?: Sheet; 
  onHoverGroupChange?: (groupId: string | null, pouchId: string | null) => void;
  onSelectionChange?: (groupIds: string[], pouchIds: string[]) => void;
  onUpdateGroupTitle?: (groupId: string, title: string) => void;
  onUpdatePouchTitle?: (pouchId: string, title: string) => void;
  onUpdatePouchValue?: (pouchId: string, value: number, multiplier?: number, historyActionType?: HistoryActionType, forceNewEntry?: boolean, lastExpression?: string, valueWhenLastExpression?: number, multiplyWhenLastExpression?: number, isTime?: boolean, roundUnit?: number | null, roundUnitWhenLastExpression?: number, layerId?: string) => void;
  onUpdatePouchMultiplier?: (pouchId: string, multiplier: number) => void;
  onSetPouchError?: (pouchId: string, hasError: boolean) => void;
  onChangeColor?: (pouchId: string, isShiftPressed?: boolean) => void;
  onChangeGroupColor?: (groupId: string, isShiftPressed?: boolean, isCtrlOption?: boolean, skipMute?: boolean) => void;
  onBulkUpdateTitles?: (updates: { groupIds: string[], pouchIds: string[] }) => void;
  onBulkUpdate?: (updates: {
    groupTitles?: { groupId: string; title: string }[];
    pouchTitles?: { pouchId: string; title: string }[];
    pouchValues?: { pouchId: string; value: number; multiplier: number; lastExpression?: string; valueWhenLastExpression?: number; multiplyWhenLastExpression?: number }[];
    pouchMultipliers?: { pouchId: string; multiplier: number }[];
    pouchErrors?: { pouchId: string; hasError: boolean }[];
    deletedGroups?: string[];
    deletedPouches?: string[];
  }) => void;
  isTouchDevice: boolean;
  isInitialLoading?: boolean; // リロード時とシート切替時のフラグ
  isSheetSwitching?: boolean; // シート切替時のフラグ
  onSetActiveLayer?: (layerId: string) => void | Promise<void>;
  onAddSheetLayer?: () => void;
  onRenameSheetLayer?: (layerId: string, name: string) => void;
  onRemoveSheetLayer?: (layerId: string) => void;
  tableScrollRef?: React.RefObject<HTMLDivElement | null>;
  /** テーブル操作開始時に計算エリアのホバー削除対象をクリアする */
  onClearCanvasHover?: () => void;
}

interface TableRow {
  groupTitle: string;
  pouchTitle: string;
  value: number;
  multiplier: number;
  roundUnit?: number;
  total: number;
  color?: string;
  isNegative: boolean;
  pouchId?: string; // パウチIDを追加
  isGroupTotalPouch?: boolean; // GTPかどうか
  isTime?: boolean;
  layerValues?: Record<string, number>;
  layerRoundUnits?: Record<string, number | undefined>;
}

interface GroupedData {
  id: string;
  groupTitle: string;
  rows: TableRow[];
  count: number;
  total: number;
  color?: string;
  placeholderText?: string;
  pouches?: string[]; // パウチIDの配列を追加
}

// 色IDからラベルを取得する関数
const getColorLabel = (
  colorId: string | undefined,
  colorLegends?: PouchColorLegend[],
  pouches?: Pouch[],
  miscLabel = getAppCopy().table.misc,
): { text: string; isPlaceholder: boolean } => {
    if (!colorId) return { text: 'A', isPlaceholder: true };

    // 凡例名を取得
    const legend = colorLegends?.find((l: PouchColorLegend) => l.colorId === colorId);
    if (legend && legend.legend.trim() !== '') {
        return { text: legend.legend, isPlaceholder: false };
    }
    
    // Legendsと同じロジックでA,B,C...を生成
    // 実際にパウチが存在する色のみを対象にして、ソート済みの順序でインデックスを取得
    if (colorLegends && pouches) {
        const sortedColorLegends = [...colorLegends].sort((a, b) => {
            // 0番目を最後に、それ以外は番号順にする汎用的な順序
            const getColorOrder = (colorId: string) => {
                const colorIndex = POUCH_COLORS.findIndex(c => c.id === colorId);
                if (colorIndex === 0) return POUCH_COLORS.length; // 0番目を最後に
                return colorIndex >= 0 ? colorIndex : 0; // 1,2,3,4,5...の順
            };
            
            return getColorOrder(a.colorId) - getColorOrder(b.colorId);
        });
        
        const existingColorLegends = sortedColorLegends.filter(l => pouches.some(p => p.color === l.colorId));
        
        // タイトルなしが1つだけの場合、「misc.」を返す
        const titledLegends = existingColorLegends.filter(l => l.legend.trim() !== '');
        const untitledLegends = existingColorLegends.filter(l => l.legend.trim() === '');
        
        if (untitledLegends.length === 1 && untitledLegends[0].colorId === colorId) {
            return { text: miscLabel, isPlaceholder: true };
        }
        
        const index = existingColorLegends.findIndex(l => l.colorId === colorId);
        if (index >= 0) {
            return { text: String.fromCharCode(65 + index), isPlaceholder: true };
        }
    }
    
    // フォールバック: 従来の方法
    const colorIndex = POUCH_COLORS.findIndex(c => c.id === colorId);
    return { text: String.fromCharCode(65 + (colorIndex >= 0 ? colorIndex : 0)), isPlaceholder: true };
};

// TSV形式の数値をフォーマットする関数
const formatTsvValue = (value: number, isNegative: boolean = false): string => {
    const signedValue = isNegative ? -value : value;
    // 整数か小数点以下2桁に固定（必要な場合のみ）
    // 標準のロケールに依存せず、純粋な数値表現を返す（カンマなし）
    return signedValue % 1 === 0 ? 
        Math.round(signedValue).toString() : 
        signedValue.toFixed(2);
};

// GTPが参照グループ内に所属しているか（内包 × は |純マルチ|>=1、÷ は multiplier<0 で表示調整対象）
const isGtpIncludesSelf = (pouch: Pouch, currentGroupId: string, groups: Group[]): boolean => {
    if (!pouch.isGroupTotalPouch || !pouch.referencedGroupId) return false;
    if (pouch.referencedGroupId !== currentGroupId) return false;
    return isInclusiveGtpSelfDisplay(pouch, groups);
};

// 表示値ベースのマルチ値を計算する関数（テーブル用）
const getDisplayMultiplierForTable = (
    pouch: Pouch,
    currentGroupId: string,
    groups: Group[],
    sheet?: Sheet,
    layerId?: string,
): number => {
    const baseMult = sheet
        ? getPouchEffectiveMultiplierForSheet(pouch, sheet, layerId)
        : pouch.multiplier;
    if (!isGtpIncludesSelf(pouch, currentGroupId, groups)) {
        return baseMult;
    }
    return getInclusiveGtpDisplayMultiplier(pouch, baseMult, true);
};

/** テーブル行ソート用の純マルチ（÷ は逆数、内包GTPは表示マルチ基準） */
const getPureMultiplierForTableSort = (
    row: TableRow,
    groupId: string,
    pouches: Pouch[],
    groups: Group[],
    sheet?: Sheet,
    layerId?: string,
): number => {
    const pouch = row.pouchId ? pouches.find(p => p.id === row.pouchId) : undefined;
    const mult = pouch
        ? getDisplayMultiplierForTable(pouch, groupId, groups, sheet, layerId)
        : row.multiplier;
    const abs = Math.abs(mult);
    return mult < 0 ? 1 / abs : abs;
};

// GTPの参照グループのプレースホルダーテキストを計算する関数
const getGtpPlaceholderText = (
  pouch: Pouch,
  groups: Group[],
  pouches: Pouch[],
  currentSheet?: Sheet,
  groupN?: (n: number) => string,
): string => {
    if (!pouch.isGroupTotalPouch || !pouch.referencedGroupId) {
        return '';
    }
    
    const referencedGroup = groups.find(g => g.id === pouch.referencedGroupId);
    if (!referencedGroup) {
        return '';
    }
    
    // 参照グループのタイトルがある場合はそれを使用
    if (referencedGroup.title && referencedGroup.title.trim() !== '') {
        return referencedGroup.title;
    }
    
    // TOTAL表示の条件を満たす場合はシート名をプレースホルダーとして使用
    if (shouldShowTotalLabel(referencedGroup, groups, pouches) && currentSheet?.title) {
        return currentSheet.title;
    }
    
    // 通常の場合は「Group 1」「Group 2」などのプレースホルダー
    // シリアルナンバー計算を使用
    const serialNumber = getGroupSerialNumber(referencedGroup.id, groups);
    return groupN ? groupN(serialNumber) : getAppCopy().table.groupN(serialNumber);
};

// GTPの参照グループのz-indexを取得する関数（DOMから取得）
const getGtpReferencedGroupZIndex = (pouch: Pouch, groups: Group[]): number => {
    if (!pouch.isGroupTotalPouch || !pouch.referencedGroupId) {
        return 0;
    }
    
    // DOMからz-indexを取得
    const groupElement = document.querySelector(`[data-group-id="${pouch.referencedGroupId}"]`);
    if (groupElement) {
        const computedStyle = window.getComputedStyle(groupElement);
        const zIndex = parseInt(computedStyle.zIndex, 10);
        return isNaN(zIndex) ? 0 : zIndex;
    }
    
    return 0;
};

export function TableComponent({ 
  isOpen, 
  onToggle, 
  groups, 
  pouches, 
  colorLegends, 
  currentSheet,
  onHoverGroupChange = () => {},
  onSelectionChange = () => {},
  onUpdateGroupTitle = () => {},
  onUpdatePouchTitle = () => {},
  onUpdatePouchValue = () => {},
  onUpdatePouchMultiplier = () => {},
  onSetPouchError = () => {},
  onChangeColor = () => {},
  onChangeGroupColor = () => {},
  onBulkUpdateTitles = () => {},
  onBulkUpdate = () => {},
  isTouchDevice = false,
  isInitialLoading = false,
  isSheetSwitching = false,
  onSetActiveLayer = () => {},
  onAddSheetLayer = () => {},
  onRenameSheetLayer = () => {},
  onRemoveSheetLayer,
  tableScrollRef,
  onClearCanvasHover,
}: TableComponentProps) {
    const _isGuideEnabled = useContext(GuideContext);
    const { appCopy, tooltipCopy, locale } = useLocale();
    const sheetLayers = useMemo(
      () => (currentSheet ? getSortedLayers(currentSheet) : []),
      [currentSheet?.layers, currentSheet?.activeLayerId]
    );
    const cols = useMemo(() => getTableColumnLayout(sheetLayers.length || 1), [sheetLayers.length]);
    const multOpCell = sheetLayers.length > 1 ? cols.mult : 2;
    const multValCell = sheetLayers.length > 1 ? cols.mult + 1 : 3;
    const countCell = sheetLayers.length > 1 ? cols.count : 3;
    const totalCell = sheetLayers.length > 1 ? cols.total : 4;

    /** パウチ行の「全列選択」判定用（レイヤー列を含む） */
    const pouchRowDataColumnIndices = useMemo(() => {
        const indices = [0];
        for (let i = cols.layerStart; i < cols.layerStart + sheetLayers.length; i++) {
            indices.push(i);
        }
        indices.push(multOpCell, multValCell, countCell, totalCell);
        return indices;
    }, [cols.layerStart, sheetLayers.length, multOpCell, multValCell, countCell, totalCell]);
    const activeLayerId = currentSheet ? resolveActiveLayerId(currentSheet) : 'default';
    const { layerSwitchDirection, layerSwitchNonce } = useLayerSwitch();
    const useLayerSlide = sheetLayers.length > 1;
    const pendingNewLayerHeaderEditRef = useRef(false);
    /** ダブルクリック直後に発火する余分な click を無視（即確定防止） */
    const ignoreCellClickAfterDoubleRef = useRef(false);
    const consumeIgnoredClick = (): boolean => {
        if (ignoreCellClickAfterDoubleRef.current) {
            ignoreCellClickAfterDoubleRef.current = false;
            return true;
        }
        return false;
    };
    const layerIdForCellIndex = useCallback(
        (cellIndex: number): string | null => {
            if (sheetLayers.length <= 1) return null;
            const li = cellIndex - cols.layerStart;
            if (li < 0 || li >= sheetLayers.length) return null;
            return sheetLayers[li]?.id ?? null;
        },
        [sheetLayers, cols.layerStart]
    );

    const activateLayerForCellIndex = useCallback(
        (cellIndex: number) => {
            const layerId = layerIdForCellIndex(cellIndex);
            if (layerId && layerId !== activeLayerId) {
                void Promise.resolve(onSetActiveLayer(layerId));
            }
        },
        [layerIdForCellIndex, activeLayerId, onSetActiveLayer]
    );

    // マルチプライヤー表示用の値とシンボルを計算
    const getMultiplierDisplay = useCallback((multiplier: number, isTime?: boolean) => {
        const absMultiplier = Math.abs(multiplier);
        // 絶対値が1以上、または整数の場合は通常表示
        if (absMultiplier >= 1 || absMultiplier === Math.floor(absMultiplier)) {
            return {
                value: absMultiplier,
                symbol: '×',
                isDivision: multiplier < 0,
                showSymbolAfter: false  // ×は数字の前に表示
            };
        }
        // 時間パウチ：1未満の小数は % ではなく小数×表示
        if (isTime) {
            const roundedDecimal = Math.round(absMultiplier * 100) / 100;
            return {
                value: roundedDecimal,
                symbol: '×',
                isDivision: multiplier < 0,
                showSymbolAfter: false,
            };
        }
        // 絶対値が1未満で小数の場合はパーセント表示
        const percentage = absMultiplier * 100;
        // 浮動小数点の精度問題を修正：小数点以下2桁で丸める
        const roundedPercentage = Math.round(percentage * 100) / 100;
        return {
            value: roundedPercentage,
            symbol: '%',
            isDivision: multiplier < 0,
            showSymbolAfter: true  // %は数字の後に表示
        };
    }, []);
    const tableRef = useRef<HTMLTableElement>(null);
    const [expandedGroups, setExpandedGroups] = React.useState<Set<string>>(new Set(['ungrouped']));
    const [previousGroups, setPreviousGroups] = React.useState<Group[]>([]);
    const [copySuccess, setCopySuccess] = React.useState<boolean>(false);
    const [pasteSuccess, setPasteSuccess] = React.useState<boolean>(false);
    const [copiedCellColors, setCopiedCellColors] = React.useState<{[rowIndex: number]: {[cellIndex: number]: string}}>({});
    const [copiedTextData, setCopiedTextData] = React.useState<string>('');
    const [selectionStart, setSelectionStart] = React.useState<{row: number, cell: number} | null>(null);
    const [selectionEnd, setSelectionEnd] = React.useState<{row: number, cell: number} | null>(null);
    const [selectedCells, setSelectedCells] = React.useState<{[rowIndex: number]: {[cellIndex: number]: boolean}}>({});
    const selectedCellsRef = useRef(selectedCells);
    selectedCellsRef.current = selectedCells;
    const [isSelecting, setIsSelecting] = React.useState(false);
    const [isShiftKeyPressed, setIsShiftKeyPressed] = React.useState(false);
    const [tableCount, setTableCount] = React.useState(1);
    const GUIDE_HOVER_DELAY_MS = 2000;
    const [tableGuideHoverTier, setTableGuideHoverTier] = useState<0 | 1 | 2>(0);
    const tableGuideHoverTimerRef = useRef<number | null>(null);
    const tableGuideHoverAnchorRef = useRef<string | null>(null);
    const [hoveredGroup, setHoveredGroup] = useState<string | null>(null);
    const [hoveredPouch, setHoveredPouch] = useState<string | null>(null);
    const [selectedGroups, setSelectedGroups] = useState<{[groupId: string]: boolean}>({});
    const [selectedPouches, setSelectedPouches] = useState<{[pouchId: string]: boolean}>({});
    // タッチデバイス用の追加状態
    const [isResizing, setIsResizing] = useState<boolean>(false);
    const [lastTouchPosition, setLastTouchPosition] = useState<{row: number, cell: number} | null>(null);
    const resizeHandleRef = useRef<HTMLDivElement>(null);
    // リサイズハンドルのダブルタッチ検出用
    const [resizeHandleLastTapTime, setResizeHandleLastTapTime] = useState<number>(0);
    const [resizeHandleTapCount, setResizeHandleTapCount] = useState<number>(0);
    
    // テーブル編集用の状態
    const [editingCell, setEditingCell] = useState<{row: number, cell: number} | null>(null);
    const [editValue, setEditValue] = useState<string>('');
    const editInputRef = useRef<HTMLInputElement>(null);
    const [isComposing, setIsComposing] = useState<boolean>(false);
    const [isTableInteracting, setIsTableInteracting] = useState<boolean>(false);
    const [preservedGroupData, setPreservedGroupData] = useState<GroupedData[] | null>(null);
    const [preservedSortOrder, setPreservedSortOrder] = useState<{groupId: string, pouchIds: string[]}[] | null>(null);

    // グループをフィルタリング（ミュートグループを除外）
    const filteredGroups = groups.filter(group => !isMuteGroup(group));

    // パウチをフィルタリング（ミュートグループに所属するパウチを除外）
    const filteredPouches = useMemo(() => {
        return pouches.filter(pouch => !isInMuteGroup(pouch.id, groups));
    }, [pouches, groups]);

    /** アクティブレイヤーの値でミラーしたパウチ一覧（集計・GTP・BP 表示の基準） */
    const pouchesForActiveLayer = useMemo(() => {
        if (!currentSheet) return filteredPouches;
        return mirrorPouchesForLayer(
            filteredPouches,
            activeLayerId,
            currentSheet,
            filteredGroups
        );
    }, [filteredPouches, activeLayerId, currentSheet, filteredGroups]);

    const computePouchValueForLayer = useCallback(
        (pouch: Pouch, layerId: string): number => {
            let value = getLayerValue(pouch, layerId);
            if (pouch.isGroupTotalPouch && pouch.referencedGroupId) {
                const referencedGroup = filteredGroups.find(g => g.id === pouch.referencedGroupId);
                if (referencedGroup) {
                    const mirroredPouches = filteredPouches.map(p =>
                        pouchWithLayerValue(p, layerId, currentSheet!)
                    );
                    value = getGroupTotalPouchDisplayBase(pouch, filteredGroups, mirroredPouches);
                    if (pouch.oppositeToTotal) {
                        value = -value;
                    }
                }
            }
            return value;
        },
        [filteredGroups, filteredPouches, currentSheet]
    );

    // パウチからTableRowを作成する共通関数
    const createTableRow = (pouch: Pouch, groupTitle: string, contextGroupId?: string): TableRow => {
        const layerValues: Record<string, number> = {};
        const layerRoundUnits: Record<string, number | undefined> = {};
        for (const layer of sheetLayers) {
            layerValues[layer.id] = computePouchValueForLayer(pouch, layer.id);
            const ru = getPouchLayerRoundUnit(pouch, layer.id);
            if (ru !== undefined) {
                layerRoundUnits[layer.id] = ru;
            }
        }
        const value = layerValues[activeLayerId] ?? pouch.value;
        const roundUnit = getPouchLayerRoundUnit(pouch, activeLayerId);
        const mirroredPouch = pouchesForActiveLayer.find(p => p.id === pouch.id);
        const total = getPouchContributionForTotals(
            mirroredPouch ?? { ...pouch, value },
            filteredGroups,
            pouchesForActiveLayer,
            contextGroupId
        );
        
        return {
            groupTitle,
            pouchTitle: pouch.title || '',
            value: value,
            multiplier: getPouchLayerMultiplier(pouch, activeLayerId),
            roundUnit,
            total: total,
            color: pouch.color,
            isNegative: total < 0,
            pouchId: pouch.id,
            isGroupTotalPouch: pouch.isGroupTotalPouch || false,
            isTime: getPouchLayerIsTime(pouch, activeLayerId),
            layerValues,
            layerRoundUnits,
        };
    };

    // パウチをソートする共通関数
    const sortPouches = (pouches: TableRow[], groupId: string): TableRow[] => {
        const primaryLayerId = currentSheet
            ? getPrimaryLayer(currentSheet).id
            : (sheetLayers.find(l => l.primary)?.id ?? sheetLayers[0]?.id ?? 'default');
        const pureMultSortKey = (row: TableRow): number =>
            getPureMultiplierForTableSort(row, groupId, filteredPouches, groups, currentSheet, activeLayerId);
        const primaryLayerValue = (row: TableRow): number =>
            row.layerValues?.[primaryLayerId] ?? row.value;

        // 通常のパウチとGTPを分けて処理
        const normalPouches = pouches.filter(row => !row.isGroupTotalPouch);
        const gtps = pouches.filter(row => row.isGroupTotalPouch);
        
        // 通常のパウチを色順 → 純マルチの高い順 → メインレイヤー値の高い順にソート
        const sortedNormalPouches = normalPouches.sort((a, b) => {
            // まず色でソート（0番目を最後に、それ以外は番号順にする汎用的な順序）
            const getColorOrder = (colorId: string) => {
                const colorIndex = POUCH_COLORS.findIndex(c => c.id === colorId);
                if (colorIndex === 0) return POUCH_COLORS.length; // 0番目を最後に
                return colorIndex >= 0 ? colorIndex : 0; // 1,2,3,4,5...の順
            };
            
            const colorOrderA = getColorOrder(a.color || 'gray');
            const colorOrderB = getColorOrder(b.color || 'gray');
            if (colorOrderA !== colorOrderB) {
                return colorOrderA - colorOrderB;
            }
            const multDiff = pureMultSortKey(b) - pureMultSortKey(a);
            if (Math.abs(multDiff) > 1e-9) return multDiff;
            return primaryLayerValue(b) - primaryLayerValue(a);
        });
        
        // GTPを参照グループのz-indexの降順でソート（内包が先で外側が後）
        // ÷（multiplier<0）は合成上「最後」なので、同一グループ内の並びでは常に × 系の後にする
        const sortedGtps = gtps.sort((a, b) => {
            const divOrder = (row: TableRow): number => {
                if (!row.pouchId) return 0;
                const p = filteredPouches.find(x => x.id === row.pouchId);
                return p && p.multiplier < 0 ? 1 : 0;
            };
            const divDiff = divOrder(a) - divOrder(b);
            if (divDiff !== 0) return divDiff;

            if (!a.pouchId || !b.pouchId) {
                return b.value - a.value;
            }
            
            const pouchA = filteredPouches.find(p => p.id === a.pouchId);
            const pouchB = filteredPouches.find(p => p.id === b.pouchId);
            
            if (!pouchA || !pouchB) {
                return b.value - a.value;
            }
            
            const zIndexA = getGtpReferencedGroupZIndex(pouchA, groups);
            const zIndexB = getGtpReferencedGroupZIndex(pouchB, groups);
            
            // z-indexの降順でソート（高い値が先）
            if (zIndexA !== zIndexB) {
                return zIndexB - zIndexA;
            }
            
            return b.value - a.value;
        });
        
        // 通常のパウチの後にGTPを追加
        return [...sortedNormalPouches, ...sortedGtps];
    };

    // グループごとのTOTAL表示判定を遅延更新で確実に最新状態で判定
    const [groupTotalStates, setGroupTotalStates] = useState<Map<string, boolean>>(new Map());
    
    useEffect(() => {
        // 遅延させてから判定を更新（確実に最新の状態を反映）
        const timeoutId = setTimeout(() => {
            const states = new Map<string, boolean>();
            groups.forEach(group => {
                states.set(group.id, shouldShowTotalLabel(group, groups, pouches));
            });
            setGroupTotalStates(states);
        }, 50); // 50ms遅延でグループ操作完了後に判定更新
        
        return () => clearTimeout(timeoutId);
    }, [groups, pouches]);

    // グループごとにパウチをまとめる
    const groupData = useMemo(() => {
        const data: GroupedData[] = [];
        const emptyGroups: GroupedData[] = []; // 空のグループを別途管理
        
        // すべてのパウチのIDを記録するセット
        const processedPouchIds = new Set<string>();
        
        filteredGroups.forEach((group: Group) => {
        // 計算エリアと同じロジックでグループタイトルを取得
        const groupTitle = (() => {
            if (group.title && group.title.trim() !== '') {
                return group.title;
            }
            if (groupTotalStates.get(group.id) && currentSheet?.title) {
                return currentSheet.title;
            }
            return group.placeholderText || appCopy.table.groupN(getGroupSerialNumber(group.id, filteredGroups));
        })();
        const rows: TableRow[] = [];
        let groupTotal = 0;
        let groupCount = 0;

        group.pouches.forEach((pouchId: string) => {
            const pouch = filteredPouches.find((p: Pouch) => p.id === pouchId);
            // 削除中のパウチは除外
            if (pouch && !pouch._isDeleting) {
            const mirrored = currentSheet
                ? pouchWithLayerValue(pouch, activeLayerId, currentSheet)
                : pouch;
            const total = getPouchContributionForTotals(mirrored, groups, pouchesForActiveLayer, group.id);
            groupTotal += total;
            
            groupCount += getPouchContributionToSheetCount(mirrored, group, groups, pouchesForActiveLayer);
            
            rows.push(createTableRow(pouch, groupTitle, group.id));
            
            // 処理済みのパウチとしてマーク
            processedPouchIds.add(pouchId);
            }
        });
        
        // パウチがあるグループは通常のdataに、空のグループはemptyGroupsに追加
        const groupDataItem = {
            id: group.id,
            groupTitle,
            rows: isTableInteracting ? rows : sortPouches(rows, group.id), // テーブル操作中はソートを保留
            count: groupCount,
            total: groupTotal,
            color: group.color,
            placeholderText: group.placeholderText,
            pouches: group.pouches
        };
        
        if (rows.length > 0) {
            data.push(groupDataItem);
        } else {
            emptyGroups.push(groupDataItem);
        }
        });

        // どのグループにも属さないパウチを集計
        const ungroupedRows: TableRow[] = [];
        let ungroupedTotal = 0;
        let ungroupedCount = 0;
        
        filteredPouches.forEach((pouch: Pouch) => {
            // 処理済みでなく、削除中でもないパウチを対象にする
            if (!processedPouchIds.has(pouch.id) && !pouch._isDeleting) {
                const mirrored = currentSheet
                    ? pouchWithLayerValue(pouch, activeLayerId, currentSheet)
                    : pouch;
                const total = getPouchContributionForTotals(mirrored, groups, pouchesForActiveLayer);
                ungroupedTotal += total;
                
                ungroupedCount += getPouchContributionToSheetCount(mirrored, null, groups, pouchesForActiveLayer);
                
                ungroupedRows.push(createTableRow(pouch, appCopy.table.ungrouped));
            }
        });
        
        // 未グループのパウチがある場合、それらを追加
        if (ungroupedRows.length > 0) {
            // ungroupedパウチのIDを収集
            const ungroupedPouchIds = ungroupedRows.map(row => row.pouchId).filter(id => id !== undefined) as string[];
            
            data.push({
                id: 'ungrouped',
                groupTitle: appCopy.table.ungrouped,
                rows: isTableInteracting ? ungroupedRows : sortPouches(ungroupedRows, 'ungrouped'), // テーブル操作中はソートを保留
                count: ungroupedCount,
                total: ungroupedTotal,
                color: GROUP_COLORS[0].id, // デフォルトの色を使用
                pouches: ungroupedPouchIds // ungroupedパウチのID配列を設定
            });
        }
        
        // 空のグループをUngroupedの後に追加
        data.push(...emptyGroups);
        
        // グループをソート（パウチがあるグループ → Ungrouped → 空のグループの順）
        const sortedData = data.sort((a, b) => {
            // パウチがあるグループを最優先
            if (a.rows.length > 0 && b.rows.length === 0) return -1;
            if (a.rows.length === 0 && b.rows.length > 0) return 1;
            
            // パウチがあるグループ同士の場合は既存のロジック
            if (a.rows.length > 0 && b.rows.length > 0) {
                // "その他"は常に最後
                if (a.id === 'ungrouped') return 1;
                if (b.id === 'ungrouped') return -1;
            
            // TOTAL表示のグループを最優先
            const aGroup = groups.find(g => g.id === a.id);
            const bGroup = groups.find(g => g.id === b.id);
            const aIsTotal = groupTotalStates.get(a.id) || false;
            const bIsTotal = groupTotalStates.get(b.id) || false;
            
            if (aIsTotal && !bIsTotal) return -1;
            if (!aIsTotal && bIsTotal) return 1;
            
            // 両方ともTOTAL表示または両方とも通常の場合は色でソート
            const colorIndexA = GROUP_COLORS.findIndex(c => c.id === a.color);
            const colorIndexB = GROUP_COLORS.findIndex(c => c.id === b.color);
            if (colorIndexA !== colorIndexB) {
                return colorIndexA - colorIndexB;
            }
            // 色が同じ場合はタイトル昇順、タイトルがない場合はサブトータルの高い順にソート
            const aTitle = aGroup?.title?.trim() || '';
            const bTitle = bGroup?.title?.trim() || '';
            
            // 両方ともタイトルがある場合はタイトル昇順、同じ場合はサブトータル降順
            if (aTitle && bTitle) {
                const titleCompare = aTitle.localeCompare(bTitle, 'ja');
                if (titleCompare !== 0) {
                    return titleCompare;
                }
                // タイトルが同じ場合はサブトータルの高い順
                return b.total - a.total;
            }
            
            // 片方だけタイトルがある場合は、タイトルがある方を先に
            if (aTitle && !bTitle) return -1;
            if (!aTitle && bTitle) return 1;
            
            // 両方ともタイトルがない場合はサブトータルの高い順
            return b.total - a.total;
            }
            
            // 空のグループ同士の場合はUngroupedを先に
            if (a.rows.length === 0 && b.rows.length === 0) {
                if (a.id === 'ungrouped') return -1;
                if (b.id === 'ungrouped') return 1;
                // 両方とも空のグループでUngroupedでない場合は色順
                const colorIndexA = GROUP_COLORS.findIndex(c => c.id === a.color);
                const colorIndexB = GROUP_COLORS.findIndex(c => c.id === b.color);
                return colorIndexA - colorIndexB;
            }
            
            return 0;
        });
        
        // テーブル操作中は保存されたソート順序を使用、そうでなければ最新のソート済みデータを返す
        if (isTableInteracting && preservedSortOrder) {
            // 保存されたソート順序を適用してデータを再構築
            const reorderedData: GroupedData[] = [];
            
            preservedSortOrder.forEach(preservedGroup => {
                // Ungroupedの場合は特別処理
                if (preservedGroup.groupId === 'ungrouped') {
                    const ungroupedPouches = filteredPouches.filter(p => 
                        !p._isDeleting && 
                        !filteredGroups.some(g => g.pouches?.includes(p.id))
                    );
                    
                    if (ungroupedPouches.length > 0) {
                        const rows: TableRow[] = [];
                        let groupTotal = 0;
                        let groupCount = 0;
                        
                        // 保存された順序でパウチを処理
                        preservedGroup.pouchIds.forEach(pouchId => {
                            const pouch = ungroupedPouches.find(p => p.id === pouchId);
                            if (pouch) {
                                const mirrored = currentSheet
                                    ? pouchWithLayerValue(pouch, activeLayerId, currentSheet)
                                    : pouch;
                                const total = getPouchContributionForTotals(mirrored, groups, pouchesForActiveLayer);
                                groupTotal += total;
                                groupCount += getPouchContributionToSheetCount(mirrored, null, groups, pouchesForActiveLayer);
                                
                                rows.push(createTableRow(pouch, appCopy.table.ungrouped));
                            }
                        });
                        
                        // 空のUngroupedグループでもtheadを表示するため、rows.length > 0の条件を削除
                        reorderedData.push({
                            id: 'ungrouped',
                            groupTitle: appCopy.table.ungrouped,
                            rows: rows,
                            count: groupCount,
                            total: groupTotal,
                            color: GROUP_COLORS[0].id
                        });
                    }
                    return;
                }
                
                const group = filteredGroups.find(g => g.id === preservedGroup.groupId);
                if (!group) return;
                
                // 計算エリアと同じロジックでグループタイトルを取得
                const groupTitle = (() => {
                    if (group.title && group.title.trim() !== '') {
                        return group.title;
                    }
                    if (groupTotalStates.get(group.id) && currentSheet?.title) {
                        return currentSheet.title;
                    }
                    return group.placeholderText || appCopy.table.groupN(getGroupSerialNumber(group.id, filteredGroups));
                })();
                const rows: TableRow[] = [];
                let groupTotal = 0;
                let groupCount = 0;
                
                // 保存された順序でパウチを処理（値は最新を使用）
                preservedGroup.pouchIds.forEach(pouchId => {
                    const pouch = filteredPouches.find(p => p.id === pouchId);
                    if (pouch && !pouch._isDeleting) {
                        const mirrored = currentSheet
                            ? pouchWithLayerValue(pouch, activeLayerId, currentSheet)
                            : pouch;
                        const total = getPouchContributionForTotals(mirrored, groups, pouchesForActiveLayer, group.id);
                        groupTotal += total;
                        groupCount += getPouchContributionToSheetCount(mirrored, group, groups, pouchesForActiveLayer);
                        
                        rows.push(createTableRow(pouch, groupTitle, group.id));
                    }
                });
                
                // 空のグループでもtheadを表示するため、rows.length > 0の条件を削除
                reorderedData.push({
                    id: group.id,
                    groupTitle: groupTitle,
                    rows: rows,
                    count: groupCount,
                    total: groupTotal,
                    color: group.color // 最新のグループの色情報を使用
                });
            });
            
            return reorderedData;
        }
        
        return sortedData;
    }, [
        filteredGroups,
        filteredPouches,
        pouchesForActiveLayer,
        isTableInteracting,
        preservedSortOrder,
        groupTotalStates,
        pouches,
        groups,
        activeLayerId,
        currentSheet,
        sheetLayers,
        computePouchValueForLayer,
    ]);

    // 8桁以上の数値（カンマ除き）がある列を検出
    const largeValuesFlags = useMemo(() => {
        const hasLargeValue = (num: number): boolean => {
            // 絶対値で8桁以上（10,000,000以上）かどうか
            return Math.abs(num) >= 10000000;
        };

        let hasLargeLayerCol = false;
        let hasLargeMultiplierCol = false;
        let hasLargeTotalCol = false;

        for (const group of groupData) {
            if (hasLargeValue(group.count)) hasLargeMultiplierCol = true;
            if (hasLargeValue(group.total)) hasLargeTotalCol = true;

            for (const row of group.rows) {
                if (hasLargeValue(row.value)) hasLargeLayerCol = true;
                if (row.layerValues) {
                    for (const v of Object.values(row.layerValues)) {
                        if (hasLargeValue(v)) hasLargeLayerCol = true;
                    }
                }
                if (hasLargeValue(row.multiplier)) hasLargeMultiplierCol = true;
                if (hasLargeValue(row.total)) hasLargeTotalCol = true;
            }

            if (hasLargeLayerCol && hasLargeMultiplierCol && hasLargeTotalCol) break;
        }

        return {
            hasLargeLayerCol,
            hasLargeMultiplierCol,
            hasLargeTotalCol
        };
    }, [groupData]);

    // テーブル操作状態を監視
    React.useEffect(() => {
        const hasInteraction = editingCell !== null || Object.keys(selectedCells).length > 0;
        const wasInteracting = isTableInteracting;
        
        if (hasInteraction && !wasInteracting) {
            // テーブル操作開始：現在のソート順序を保存
            const sortOrder = groupData.map(group => ({
                groupId: group.id,
                pouchIds: group.rows.map(row => row.pouchId).filter((id): id is string => id !== undefined)
            }));
            setPreservedSortOrder(sortOrder);
        } else if (!hasInteraction && wasInteracting) {
            // テーブル操作終了：保存されたソート順序をクリア
            setPreservedSortOrder(null);
        }
        
        setIsTableInteracting(hasInteraction);
    }, [editingCell, selectedCells, isTableInteracting, groupData]);


    React.useEffect(() => {
        // 前回のグループと現在のグループを比較
        const newGroups = filteredGroups.filter(group => 
            !previousGroups.some(prevGroup => prevGroup.id === group.id)
        );

        // 新しいグループがある場合のみ更新
        if (newGroups.length > 0) {
            setExpandedGroups(prev => {
                const next = new Set(prev);
                newGroups.forEach(group => {
                    // すべてのグループを展開状態で追加
                    next.add(group.id);
                });
                return next;
            });
            // 現在のグループを保存（新しいグループがある場合のみ）
            setPreviousGroups(filteredGroups);
        }
    }, [filteredGroups, previousGroups]);

    // thead内のグループタイトル: タイトル列〜最後の値列までの幅で表示
    React.useEffect(() => {
        if (!tableRef.current) return;

        const titlePaddingLeft = 20;
        const lastValueColIndex = cols.mult - 1;

        const updateGroupTitleWidths = () => {
            const theadRows = tableRef.current?.querySelectorAll('thead tr.group-header');
            if (!theadRows) return;

            theadRows.forEach(row => {
                const cells = row.querySelectorAll('th, td');
                if (cells.length <= lastValueColIndex) return;

                const firstCell = cells[0] as HTMLElement;
                let totalWidth = 0;
                for (let i = 0; i <= lastValueColIndex; i++) {
                    totalWidth += (cells[i] as HTMLElement).offsetWidth;
                }
                const textWidth = Math.max(0, totalWidth - titlePaddingLeft);

                const shell = firstCell.querySelector('.table-cell-edit-shell') as HTMLElement | null;
                const titleText = firstCell.querySelector('.group-title-text') as HTMLElement | null;
                const inputElement = firstCell.querySelector('input') as HTMLElement | null;

                const applyWidth = (el: HTMLElement) => {
                    el.style.width = `${textWidth}px`;
                    el.style.maxWidth = `${textWidth}px`;
                };

                if (shell) applyWidth(shell);
                if (titleText) {
                    applyWidth(titleText);

                    setTimeout(() => {
                        const textHeight = titleText.offsetHeight * 1.2;
                        if (textHeight > 0) {
                            const minHeight = textHeight;
                            firstCell.style.minHeight = `${minHeight}px`;
                            (row as HTMLElement).style.height = `${minHeight}px`;
                        }
                    }, 0);
                }
                if (inputElement) {
                    applyWidth(inputElement);

                    setTimeout(() => {
                        const inputHeight = inputElement.offsetHeight * 1.3;
                        if (inputHeight > 0) {
                            const minHeight = inputHeight;
                            firstCell.style.minHeight = `${minHeight}px`;
                            (row as HTMLElement).style.height = `${minHeight}px`;
                        }
                    }, 0);
                }
            });
        };

        // 初回実行
        updateGroupTitleWidths();

        // リサイズ時にも再計算
        const resizeObserver = new ResizeObserver(() => {
            updateGroupTitleWidths();
        });

        if (tableRef.current) {
            resizeObserver.observe(tableRef.current);
        }

        return () => {
            resizeObserver.disconnect();
        };
    }, [groupData, editingCell, cols.mult, sheetLayers.length]);

    // グループの折りたたみ操作
    const toggleGroup = (groupId: string, e?: React.MouseEvent) => {
        // イベントがある場合は伝播を止める
        if (e) {
            e.stopPropagation();
        }
        
        setExpandedGroups(prev => {
            const next = new Set(prev);
            if (next.has(groupId)) {
                next.delete(groupId);
            } else {
                next.add(groupId);
            }
            return next;
        });
    };

    // トグルアイコンのクリック処理（折りたたみ専用）
    const handleToggleIconClick = (e: React.MouseEvent, groupId: string) => {
        e.stopPropagation(); // 親要素へのイベント伝播を防止
        toggleGroup(groupId);
    };

    // 選択範囲からHTMLテーブルを生成
    const getHTMLFromSelection = (): string => {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return '';
        
        // テーブル用のHTML（スタイル情報なしの最もシンプルな形式）
        let htmlContent = '<table border="1">\n';
        
        // 選択された行と列を追跡する変数
        const selectedRows = new Set<HTMLTableRowElement>();
        const selectedCells: Array<{row: number, col: number, element: HTMLElement}> = [];
        
        // 選択範囲内のセルを特定
        if (tableRef.current) {
            const tableRows = Array.from(tableRef.current.querySelectorAll('tr'));
            
            tableRows.forEach((row, rowIndex) => {
                const cells = Array.from(row.querySelectorAll('td, th'));
                let hasSelectedCell = false;
                
                cells.forEach((cell, colIndex) => {
                    if (selection.containsNode(cell, true)) {
                        selectedCells.push({row: rowIndex, col: colIndex, element: cell as HTMLElement});
                        hasSelectedCell = true;
                    }
                });
                
                if (hasSelectedCell) {
                    selectedRows.add(row);
                }
            });
            
            // 選択された行ごとにHTMLを生成
            Array.from(selectedRows).forEach(row => {
                htmlContent += '<tr>';
                
                const cells = Array.from(row.querySelectorAll('td, th'));
                const rowIndex = tableRows.indexOf(row);
                
                // 行が合計行かどうかを判定
                const firstCell = cells[0];
                const secondCell = cells[1];
                const isGroupTotal = secondCell && secondCell.textContent?.trim() === appCopy.table.excel.totalRow;
                const groupName = firstCell?.textContent?.trim() || '';
                
                cells.forEach((cell, colIndex) => {
                    // 選択されたセルのみ出力
                    if (selectedCells.some(sc => sc.row === rowIndex && sc.col === colIndex)) {
                        const isHeader = cell.tagName.toLowerCase() === 'th';
                        const tagName = isHeader ? 'th' : 'td';
                        const colSpan = cell.getAttribute('colspan');
                        const colSpanAttr = colSpan ? ` colspan="${colSpan}"` : '';
                        
                        // 数値セルかどうかを判定
                        let styleAttr = '';
                        if (cell.querySelector('.calcvalue') || (!isNaN(Number(cell.textContent?.trim().replace(/[,\s]/g, ''))))) {
                            styleAttr = ' style="mso-number-format:\'#,##0.00\'"';
                        }
                        
                        // 合計行の数値セルの場合、数式として出力
                        if (isGroupTotal && colIndex === 5) {
                            // 合計行の最後の列は常に数式として出力
                            const currentValue = cell.textContent?.trim().replace(/[,\s]/g, '') || '0';
                            htmlContent += `<${tagName}${colSpanAttr}${styleAttr}>=SUM(${currentValue})</${tagName}>`;
                        } 
                        // 合計行のカウントセルの場合も数式として出力
                        else if (isGroupTotal && colIndex === 4) {
                            const currentValue = cell.textContent?.trim().replace(/[,\s]/g, '') || '0';
                            htmlContent += `<${tagName}${colSpanAttr}${styleAttr}>=COUNT(${currentValue})</${tagName}>`;
                        }
                        // 通常のセルの場合は、計算セルであれば数式として出力
                        else if (colIndex === 5 && !isGroupTotal) {
                            // 計算結果列で演算子が定義されている場合は数式を生成
                            const operatorCell = cells[3];
                            const valueCell = cells[2];
                            const multiplierCell = cells[4];
                            
                            if (operatorCell && valueCell && multiplierCell) {
                                const operator = operatorCell.textContent?.trim();
                                if (operator === '×' || operator === '*' || operator === '/') {
                                    const value = valueCell.textContent?.trim().replace(/[,\s]/g, '') || '0';
                                    const multiplier = multiplierCell.textContent?.trim().replace(/[,\s]/g, '') || '0';
                                    const formula = operator === '/' ? 
                                        `=${value}/${multiplier}` : 
                                        `=${value}*${multiplier}`;
                                    
                                    htmlContent += `<${tagName}${colSpanAttr}${styleAttr}>${formula}</${tagName}>`;
                                } else {
                                    htmlContent += `<${tagName}${colSpanAttr}${styleAttr}>${cell.textContent?.trim() || ''}</${tagName}>`;
                                }
                            } else {
                                htmlContent += `<${tagName}${colSpanAttr}${styleAttr}>${cell.textContent?.trim() || ''}</${tagName}>`;
                            }
                        } else {
                            htmlContent += `<${tagName}${colSpanAttr}${styleAttr}>${cell.textContent?.trim() || ''}</${tagName}>`;
                        }
                    }
                });
                
                htmlContent += '</tr>\n';
            });
        }
        
        htmlContent += '</table>';
        
        return htmlContent;
    };

    // テーブル外クリックでセル選択をクリアするための処理
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (tableRef.current && !tableRef.current.contains(e.target as Node)) {
                setSelectedCells({});
                setSelectionStart(null);
                setSelectionEnd(null);
            }
        };
        
        // 計算エリアからのカスタムイベントで選択解除
        const handleTableSelectionClear = () => {
            setSelectedCells({});
            setSelectionStart(null);
            setSelectionEnd(null);
        };
        
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('tableSelectionClear', handleTableSelectionClear);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('tableSelectionClear', handleTableSelectionClear);
        };
    }, []);

    // カスタムコンテキストメニューの処理
    const handleContextMenu = (e: React.MouseEvent) => {
        // 選択されたセルがある場合、選択内容をコピーできるようにする
        if (Object.keys(selectedCells).length > 0) {
            // デフォルトのコンテキストメニューを表示（preventDefault は呼ばない）
            
            // 選択セルのテキストを取得
            const plainText = getSelectedCellsText();
            
            // テキストを選択状態にするためのダミー要素
            const textArea = document.createElement('textarea');
            textArea.value = plainText;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            textArea.style.top = '-999999px';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            
            // コンテキストメニューが閉じた後にクリーンアップ
            setTimeout(() => {
                document.body.removeChild(textArea);
            }, 100);
        }
    };

    // マウスダウンでセル選択開始
    const handleMouseDown = (e: React.MouseEvent) => {
        // 編集中の場合は値を確定してから処理を継続
        if (editingCell) {
            handleEditComplete();
        }
        
        // 右クリックの場合は選択範囲を維持して標準のコンテキストメニューを許可
        if (e.button === 2) {
            return;
        }

        const target = e.target as HTMLElement;
        if (
            target.closest('.layer-add-btn') ||
            target.closest('.layer-remove-btn') ||
            (target.tagName === 'BUTTON' && target.closest('.sheet-layer-header'))
        ) {
            return;
        }

        clearLayerRemoveConfirming();
        
        // テーブルにフォーカスを当てる
        if (tableRef.current) {
            tableRef.current.focus();
        }
        onClearCanvasHover?.();

        // テキスト選択を防止
        e.preventDefault();
        
        // テーブル全体のテキスト選択を無効化
        document.body.classList.add('no-select');
        
        const cell = target.closest('td, th');
        if (!cell) return;

        // グループ折りたたみヘッダーのクリックの場合は選択処理をスキップ
        // ただし、すでに選択中の場合は、トグルアイコンのクリックでも選択を優先
        if (cell.closest('.group-header .toggle-icon') && !isSelecting) return;

        // 折りたたまれたグループのtbody内のセルをクリックした場合は選択処理をスキップ
        const tbody = cell.closest('tbody');
        if (tbody && tbody.classList.contains('collapsed')) return;

        // 行と列のインデックスを取得
        const row = cell.parentElement as HTMLTableRowElement;
        if (!row) return;

        // セルのデータ属性から正確な行と列の位置を取得
        const currentRowIndex = parseInt(row.getAttribute('data-row-index') || '-1');
        const currentCellIndex = parseInt((cell as HTMLElement).getAttribute('data-cell-index') || '-1');
        
        if (currentCellIndex >= 0 && (currentRowIndex >= 0 || isLayerHeaderRow(currentRowIndex))) {
            // 新しい選択を開始
            setSelectionStart({row: currentRowIndex, cell: currentCellIndex});
            setSelectionEnd(null); // 選択終了位置をリセット
            
            // 選択中のセルをクリア（最初のセルのみ選択）
            setSelectedCells({
                [currentRowIndex]: {
                    [currentCellIndex]: true
                }
            });
            
            setIsSelecting(true);
        }
    };

    // タッチスタートでセル選択開始（モバイル対応）
    const handleTouchStart = (e: React.TouchEvent) => {
        // 編集中の場合は値を確定してから処理を継続
        if (editingCell) {
            handleEditComplete();
        }
        
        // リサイズハンドルでのタッチは処理しない
        if (e.target && (e.target as HTMLElement).closest('.table-selection-resize-handle')) {
            return;
        }
        
        // テーブルにフォーカスを当てる
        if (tableRef.current) {
            tableRef.current.focus();
        }

        // テキスト選択を防止
        // e.preventDefault();
        
        // テーブル全体のテキスト選択を無効化
        document.body.classList.add('no-select');
        
        // タッチ位置の要素を取得
        const touch = e.touches[0];
        const element = document.elementFromPoint(touch.clientX, touch.clientY);
        if (!element) return;

        // ボタン等は各要素の touch/pointer ハンドラに任せる
        if (
          element.closest(
            'button, a, input, select, textarea, .layer-add-btn, .layer-remove-btn'
          )
        ) {
            return;
        }
        
        // セル要素を取得
        const cell = (element as HTMLElement).closest('td, th');
        if (!cell) return;

        // グループ折りたたみヘッダーのクリックの場合は選択処理をスキップ
        if (cell.closest('.group-header .toggle-icon')) {
            // トグルアイコンがクリックされた場合は、グループの展開/折りたたみ処理を行う
            const groupHeader = cell.closest('.group-header');
            if (groupHeader) {
                const groupId = groupHeader.getAttribute('data-group-id');
                if (groupId) {
                    toggleGroup(groupId);
                    return;
                }
            }
        }

        // 折りたたまれたグループのtbody内のセルをタップした場合は選択処理をスキップ
        const tbody = cell.closest('tbody');
        if (tbody && tbody.classList.contains('collapsed')) return;

        // 行と列のインデックスを取得
        const row = cell.parentElement as HTMLTableRowElement;
        if (!row) return;

        // セルのデータ属性から正確な行と列の位置を取得
        const currentRowIndex = parseInt(row.getAttribute('data-row-index') || '-1');
        const currentCellIndex = parseInt((cell as HTMLElement).getAttribute('data-cell-index') || '-1');
        
        if (currentCellIndex >= 0 && (currentRowIndex >= 0 || isLayerHeaderRow(currentRowIndex))) {
            setSelectionStart({row: currentRowIndex, cell: currentCellIndex});
            setSelectionEnd({row: currentRowIndex, cell: currentCellIndex});
            setSelectedCells({
                [currentRowIndex]: {
                    [currentCellIndex]: true
                }
            });
            activateLayerForCellIndex(currentCellIndex);
            (cell as HTMLElement).focus?.();

            if (isTouchDevice) {
                setTimeout(() => {
                    updateResizeHandlePosition(currentRowIndex, currentCellIndex);
                }, 50);
            }
        }
    };

    // タッチ移動でセル選択範囲を更新
    const handleTouchMove = (e: React.TouchEvent) => {
        // リサイズハンドルのタッチムーブのみ処理し、それ以外はスクロールに使用するためスキップ
        if (!isResizing) return;
        
        if (!selectionStart) return;
        
        // 現在のタッチ位置を取得
        const touch = e.touches[0];
        const element = document.elementFromPoint(touch.clientX, touch.clientY);
        if (!element) return;
        
        // セル要素を取得
        const cell = (element as HTMLElement).closest('td, th');
        if (!cell) return;

        // 折りたたまれたグループのtbody内のセルの場合はスキップ
        const tbody = cell.closest('tbody');
        if (tbody && tbody.classList.contains('collapsed')) return;

        // 行と列のインデックスを取得
        const row = cell.parentElement as HTMLTableRowElement;
        if (!row) return;

        // セルのデータ属性から正確な行と列の位置を取得
        const currentRowIndex = parseInt(row.getAttribute('data-row-index') || '-1');
        const currentCellIndex = parseInt((cell as HTMLElement).getAttribute('data-cell-index') || '-1');
        
        if (currentCellIndex >= 0 && (currentRowIndex >= 0 || isLayerHeaderRow(currentRowIndex))) {
            // 現在位置が前回と同じなら処理をスキップ
            if (selectionEnd && selectionEnd.row === currentRowIndex && selectionEnd.cell === currentCellIndex) {
                return;
            }
            
            // 選択終了位置を更新
            setSelectionEnd({row: currentRowIndex, cell: currentCellIndex});
            
            // 行範囲を計算
            const minRow = Math.min(selectionStart.row, currentRowIndex);
            const maxRow = Math.max(selectionStart.row, currentRowIndex);
            
            // 列範囲を計算
            let minCol = Math.min(selectionStart.cell, currentCellIndex);
            let maxCol = Math.max(selectionStart.cell, currentCellIndex);
            
            
            
            
            // 選択セルマップを作成
            const newSelectedCells: {[rowIndex: number]: {[cellIndex: number]: boolean}} = {};
            
            // 各行を処理
            for (let r = minRow; r <= maxRow; r++) {
                newSelectedCells[r] = {};
                
                // 全ての行に対して同じ列範囲を適用
                for (let c = minCol; c <= maxCol; c++) {
                    newSelectedCells[r][c] = true;
                }
            }
            
            
            // 選択セルを更新
            setSelectedCells(newSelectedCells);
        }
    };

    // タッチ終了で選択完了
    const handleTouchEnd = (e: React.TouchEvent) => {
        // リサイズハンドルのタッチ終了は別処理
        if (isResizing) return;
        
        // テキスト選択を再有効化
        document.body.classList.remove('no-select');
        
        // リサイズハンドル位置は selectedCells 更新後の useEffect で更新する
        // （touchEnd 時点では state が未反映のため、ここで呼ぶと一つ前の選択位置になる）
    };

    // テーブル外タップでセル選択をクリアするためのハンドラー
    useEffect(() => {
        const handleDocumentTouchEnd = (e: TouchEvent) => {
            // デフォルトの動作を妨げないようにする
            // e.preventDefault()やe.stopPropagation()は呼び出さない
            
            // わずかに遅延させることで、通常のクリックイベントが先に処理されるようにする
            setTimeout(() => {
                // インタラクティブな要素や操作可能な要素の場合は処理をスキップ
                if (e.target instanceof Element) {
                    // ボタン、入力フィールド、リンクなど幅広いインタラクティブな要素をチェック
                    const isInteractive = e.target.closest('button, a, input, select, textarea, label, [role="button"], [role="link"], [role="checkbox"], [role="radio"], [role="tab"], [role="menuitem"], .excel-export-button, .clear-button, .copy-button, .paste-button, .clickable, .button, .btn') !== null;
                    
                    // 操作対象のツールバーやアプリ内のコントロール領域をチェック
                    const isAppControl = e.target.closest('.app-controls, .toolbar, .menu, .dropdown, .modal, .dialog, .drawer, nav, header, footer, .control-area, .sheet-tabs, .tab-bar, .sidebar') !== null;
                    
                    if (isInteractive || isAppControl) {
                        return;
                    }
                    
                    // クリックイベントがすでに処理された場合（例：data-clicked属性がある場合）は処理をスキップ
                    if (e.target.hasAttribute('data-clicked') || e.target.closest('[data-clicked]')) {
                        return;
                    }
                }
                
                // タッチ終了位置がテーブル外かつ、選択セルが存在する場合のみ処理
                if (tableRef.current && 
                    !tableRef.current.contains(e.target as Node) && 
                    Object.keys(selectedCells).length > 0) {
                    
                    
                    // テーブル外のタップでセル選択をクリア
                    setSelectedCells({});
                    setSelectionStart(null);
                    setSelectionEnd(null);
                }
            }, 10); // 短い遅延で、クリックイベントの後に処理が実行されるようにする
        };

        // ドキュメントレベルでタッチイベントを監視（この監視は他のリスナーより後に実行されるように）
        document.addEventListener('touchend', handleDocumentTouchEnd, { passive: true });

        return () => {
            document.removeEventListener('touchend', handleDocumentTouchEnd);
        };
    }, [selectedCells]); // selectedCellsを依存配列に追加して、選択状態に変化があるときだけ効果があるようにする

    // handleMouseMove関数を修正
    const handleMouseMove = (e: React.MouseEvent) => {
        if (!isSelecting || !selectionStart) return;

        // 現在のセル位置を取得
        const target = e.target as HTMLElement;
        const cell = target.closest('td, th');
        if (!cell) return;

        // 折りたたまれたグループのtbody内のセルをホバーした場合はスキップ
        const tbody = cell.closest('tbody');
        if (tbody && tbody.classList.contains('collapsed')) return;

        const row = cell.parentElement as HTMLTableRowElement;
        if (!row) return;

        // セルのデータ属性から正確な行と列の位置を取得
        const rowIndex = parseInt(row.getAttribute('data-row-index') || '-1');
        const cellIndex = parseInt((cell as HTMLElement).getAttribute('data-cell-index') || '-1');
        
        if (cellIndex >= 0 && (rowIndex >= 0 || isLayerHeaderRow(rowIndex))) {
            // 現在位置が前回と同じなら処理をスキップ
            if (selectionEnd && selectionEnd.row === rowIndex && selectionEnd.cell === cellIndex) {
                return;
            }
            
            // 選択終了位置を更新
            setSelectionEnd({row: rowIndex, cell: cellIndex});
            
            // 行範囲を計算
            const minRow = Math.min(selectionStart.row, rowIndex);
            const maxRow = Math.max(selectionStart.row, rowIndex);
            
            // 列範囲を計算
            let minCol = Math.min(selectionStart.cell, cellIndex);
            let maxCol = Math.max(selectionStart.cell, cellIndex);
            
            
            
            
            // 選択セルマップを作成
            const newSelectedCells: {[rowIndex: number]: {[cellIndex: number]: boolean}} = {};
            
            // 各行を処理
            for (let r = minRow; r <= maxRow; r++) {
                newSelectedCells[r] = {};
                
                // 全ての行に対して同じ列範囲を適用
                for (let c = minCol; c <= maxCol; c++) {
                    newSelectedCells[r][c] = true;
                }
            }
            
            
            // 選択セルを更新
            setSelectedCells(newSelectedCells);
        }
    };

    /** テーブルガイド: グループヘッダー(th)の通常グループが選択に含まれる */
    const tableGuideShowsGroupColorKeys = useMemo(() => {
        return Object.entries(selectedCells).some(([rowStr, cells]) => {
            const rowIndex = parseInt(rowStr, 10);
            if (rowIndex % 1000 !== 0 || !cells || Object.keys(cells).length === 0) {
                return false;
            }
            const g = groupData[Math.floor(rowIndex / 1000)];
            return Boolean(g?.id && g.id !== 'ungrouped');
        });
    }, [selectedCells, groupData]);

    /** テーブルガイド: パウチ行(th 以外)が選択に含まれる */
    const tableGuideShowsPouchColorKeys = useMemo(() => {
        return Object.keys(selectedCells).some(rowStr => {
            const rowIndex = parseInt(rowStr, 10);
            if (isLayerHeaderRow(rowIndex) || rowIndex % 1000 === 0) {
                return false;
            }
            const cells = selectedCells[rowIndex];
            return Boolean(cells && Object.keys(cells).length > 0);
        });
    }, [selectedCells]);

    const isCellSelected = (rowIdentifier: number, cellIndex: number) => {
        return selectionStart?.row === rowIdentifier && selectionStart?.cell === cellIndex;
    };

    // セルがボーダーを持つかどうかを判定（完全に改良）
    const getCellSelectionClasses = (rowIdentifier: number, cellIndex: number) => {
        // この関数はレンダリング時に各セルから呼ばれる
        // 選択されていない場合は空文字を返す
        if (!selectedCells[rowIdentifier] || !selectedCells[rowIdentifier][cellIndex]) {
            return '';
        }
        
        let classes = 'cell-selected';
        
        // セル要素とその属性を取得
        const rowElement = document.querySelector(`tr[data-row-index="${rowIdentifier}"]`) as HTMLTableRowElement;
        if (!rowElement) return classes;
        
        const cellElement = rowElement.querySelector(`[data-cell-index="${cellIndex}"]`) as HTMLElement;
        if (!cellElement) return classes;
        
        // colspanを取得
        const colSpan = parseInt(cellElement.getAttribute('colspan') || '1');
        
        // より精密な境界検出（グループ間の空白を考慮）
        // 選択されている行のリストを取得
        const selectedRowIndices = Object.keys(selectedCells).map(Number).sort((a, b) => a - b);
        const currentRowIndexInSelection = selectedRowIndices.indexOf(rowIdentifier);
        
        // 上の境界：選択範囲内で前の行が存在しない、または前の行の同じ列が選択されていない
        let isTopEdge = true;
        if (currentRowIndexInSelection > 0) {
            const prevRowIndex = selectedRowIndices[currentRowIndexInSelection - 1];
            isTopEdge = !selectedCells[prevRowIndex] || !selectedCells[prevRowIndex][cellIndex];
        }
        
        // 下の境界：選択範囲内で次の行が存在しない、または次の行の同じ列が選択されていない
        let isBottomEdge = true;
        if (currentRowIndexInSelection < selectedRowIndices.length - 1) {
            const nextRowIndex = selectedRowIndices[currentRowIndexInSelection + 1];
            isBottomEdge = !selectedCells[nextRowIndex] || !selectedCells[nextRowIndex][cellIndex];
        }
        
        // 左の境界：同じ行の左隣のセルが選択されていない
        const isLeftEdge = !selectedCells[rowIdentifier][cellIndex-1];
        
        // 右の境界：結合セルの場合は最後の列を考慮、通常の場合は右隣のセル
        let isRightEdge = false;
        if (colSpan > 1) {
            // 結合セルの場合、結合の最後の列+1が選択されていなければ右端
            isRightEdge = !selectedCells[rowIdentifier][cellIndex + colSpan];
        } else {
            // 通常のセルの場合は隣のセル
            isRightEdge = !selectedCells[rowIdentifier][cellIndex+1];
        }
        
        // 境界ごとにクラスを追加
        if (isTopEdge) classes += ' cell-selected-top';
        if (isBottomEdge) classes += ' cell-selected-bottom';
        if (isLeftEdge) classes += ' cell-selected-left';
        if (isRightEdge) classes += ' cell-selected-right';
        
        // 角のクラスを単純に追加（より一貫した方法）
        // 左上の角
        if (isTopEdge && isLeftEdge) {
            classes += ' cell-selected-top-left';
        }
        
        // 右上の角
        if (isTopEdge && isRightEdge) {
            classes += ' cell-selected-top-right';
        }
        
        // 左下の角：下と左が境界
        if (isBottomEdge && isLeftEdge) {
            classes += ' cell-selected-bottom-left';
        }
        
        // 右下の角：下と右が境界
        if (isBottomEdge && isRightEdge) {
            classes += ' cell-selected-bottom-right';
        }
        
        return classes;
    };

    // theadのth要素のcolspan値を考慮したセルインデックスを計算する
    const calculateCellIndices = () => {
        if (!tableRef.current) return;
        
        // 全ての行に対して処理
        const rows = tableRef.current.querySelectorAll('tr');
        rows.forEach(row => {
            let currentIndex = 0;
            
            // 行内の全てのセル（thとtd）に対して処理
            const cells = row.querySelectorAll('th, td');
            cells.forEach(cell => {
                // data-cell-indexを現在のインデックスに設定
                cell.setAttribute('data-cell-index', currentIndex.toString());
                
                // colspanがある場合はその分インデックスを進める
                const colSpan = parseInt(cell.getAttribute('colspan') || '1');
                currentIndex += colSpan;
            });
        });
    };

    // コンポーネントマウント時とテーブルデータ変更時にセルインデックスを計算
    useEffect(() => {
        calculateCellIndices();
    }, [groupData, expandedGroups]);

    // マウス操作イベントとは別にコピー機能とDelete機能を実装
    const handleSelectionCopy = async (e: React.KeyboardEvent<HTMLTableElement> | React.PointerEvent<HTMLButtonElement> | React.TouchEvent<HTMLButtonElement>) => {
        // Delete/Backspaceキーの処理
        if (e.type === 'keydown' && 'key' in e && (e.key === 'Delete' || e.key === 'Backspace')) {
            // input要素内の場合は通常の文字削除を許可（何もしない）
            if (e.target && (e.target as HTMLElement).tagName === 'INPUT') {
                return;
            }
            
            e.preventDefault();
            
            // 選択範囲がある場合は一括処理
            if (Object.keys(selectedCells).length > 0) {
                processSelectedCells(e.key === 'Delete');
                return;
            }
        }
        
        // コピー機能の処理
        // イベントタイプによって処理を分ける
        if (
            // キーボードイベントの場合
            (e.type === 'keydown' && 'ctrlKey' in e && 'key' in e && e.ctrlKey && e.key === 'c') || 
            // クリックイベントの場合
            e.type === 'click' || 
            // タッチイベントの場合
            e.type === 'touchend'
        ) {
            e.preventDefault();
            if (e.type === 'touchend') {
                e.stopPropagation();
            }
            
            // 選択されたセルがある場合
            if (Object.keys(selectedCells).length > 0) {
                const htmlContent = getSelectedCellsHTML();
                const plainText = getSelectedCellsText();
                
                try {
                    // HTMLを含むデータ構造を作成
                    const fullHtmlData = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>
table, th, td {
  border: 1px solid black;
  border-collapse: collapse;
}
</style>
</head>
<body>
${htmlContent}
</body>
</html>`;
                    
                    // セキュアな方法でコピー
                    await copyToClipboard(fullHtmlData, plainText);
                } catch (err) {
                    console.error('クリップボードエラー:', err);
                    // フォールバック
                    await copyTextToClipboard(plainText);
                }
            }
        }
    };
    
    // copyToClipboard関数を修正（モダンなClipboard APIを使用）
    const copyToClipboard = async (html: string, text: string) => {
        try {
            // モダンなClipboard APIが利用可能かチェック
            // iOSでのClipboardItem対応を確認
            if (navigator.clipboard && window.isSecureContext && typeof ClipboardItem !== 'undefined') {
                try {
                    // HTMLとテキストの両方をクリップボードに設定
                    const clipboardItem = new ClipboardItem({
                        'text/html': new Blob([html], { type: 'text/html' }),
                        'text/plain': new Blob([text], { type: 'text/plain' })
                    });
                    
                    await navigator.clipboard.write([clipboardItem]);
                    setCopySuccess(true);
                    setTimeout(() => setCopySuccess(false), 1500);
                    return;
                } catch (clipboardItemErr) {
                    console.warn('ClipboardItem でのコピーに失敗、テキストのみで再試行:', clipboardItemErr);
                    
                    // ClipboardItemが失敗した場合、テキストのみでClipboard APIを試行
                    try {
                        await navigator.clipboard.writeText(text);
                        setCopySuccess(true);
                        setTimeout(() => setCopySuccess(false), 1500);
                        return;
                    } catch (textOnlyErr) {
                        console.warn('Clipboard API (テキストのみ) でのコピーに失敗:', textOnlyErr);
                        throw textOnlyErr;
                    }
                }
            }
        } catch (err) {
            console.warn('Clipboard API でのコピーに失敗:', err);
        }
        
        // フォールバック: 古い方法を試す（iOS対応のため復活）
        try {
            // iOS Safariでも動作するようにテキストのみでフォールバック
            await copyTextToClipboard(text);
        } catch (err) {
            console.error('フォールバックコピー失敗:', err);
            // 最後の手段: ユーザーに手動コピーを促す
            console.warn('自動コピーに失敗しました。手動でコピーしてください：', text);
        }
    };

    // フォールバックのcopyTextToClipboard関数も修正
    const copyTextToClipboard = async (text: string) => {
        try {
            // まずモダンなAPIを試す
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(text);
                setCopySuccess(true);
                setTimeout(() => setCopySuccess(false), 1500);
                return;
            }
        } catch (err) {
            console.warn('Clipboard API でのテキストコピーに失敗:', err);
        }
        
        // フォールバック: 古い方法（iOS対応のため復活）
        try {
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.style.position = 'fixed';
            textArea.style.left = '-9999px';
            textArea.style.top = '-9999px';
            textArea.style.opacity = '0';
            // iOSでの選択を確実にするための設定
            textArea.contentEditable = 'true';
            textArea.readOnly = false;
            document.body.appendChild(textArea);
            
            // iOSでのフォーカスと選択を改善
            textArea.focus();
            textArea.setSelectionRange(0, text.length);
            
            const success = document.execCommand('copy');
            document.body.removeChild(textArea);
            
            if (success) {
                setCopySuccess(true);
                setTimeout(() => setCopySuccess(false), 1500);
            } else {
                console.error('テキストコピーに失敗しました');
                throw new Error('execCommand copy failed');
            }
        } catch (err) {
            console.error('フォールバックテキストコピー失敗:', err);
            throw err; // エラーを上位に伝播
        }
    };
    
    // 選択されたセルからHTMLを生成する関数
    const getSelectedCellsHTML = (): string => {
        // 選択範囲が存在しない場合は空文字を返す
        if (Object.keys(selectedCells).length === 0) return '';
        
        // HTML形式のテーブルを生成
        let htmlContent = '<table border="1" cellspacing="0" cellpadding="5">\n';
        
        // 選択された行を特定
        const selectedRowIndices = Object.keys(selectedCells).map(Number).sort((a, b) => a - b);
        
        // 選択された列を特定
        const allSelectedCellIndices = new Set<number>();
        selectedRowIndices.forEach(rowIndex => {
            if (selectedCells[rowIndex]) {
                Object.keys(selectedCells[rowIndex]).forEach(cellIndex => {
                    allSelectedCellIndices.add(parseInt(cellIndex));
                });
            }
        });
        const sortedSelectedCellIndices = Array.from(allSelectedCellIndices).sort((a, b) => a - b);
        
        // 選択された各行の処理
        selectedRowIndices.forEach((rowIndex) => {
            // DOM要素から対応する行を見つける
            const rowElement = document.querySelector(`tr[data-row-index="${rowIndex}"]`) as HTMLTableRowElement;
            if (!rowElement) return;
            
            // 折りたたまれたグループのtbody内の行はスキップ
            const tbody = rowElement.closest('tbody');
            if (tbody && tbody.classList.contains('collapsed')) return;
            
            // この行で選択されているセルインデックスを取得
            const rowSelectedCellIndices = Object.keys(selectedCells[rowIndex]).map(Number).sort((a, b) => a - b);
            
            // 選択されたセルが一つもない場合はスキップ
            if (rowSelectedCellIndices.length === 0) return;
            
            htmlContent += '  <tr>\n';
            
            // グループヘッダー行かどうかを確認
            const isGroupHeader = rowElement.classList.contains('group-header');
            
            // 行内の選択されたセルのみを処理
            rowSelectedCellIndices.forEach((cellIndex) => {
                // セル要素を取得
                const cellSelector = isGroupHeader && cellIndex === 0 ? 
                    'th' : // グループヘッダーの最初のセルはth
                    `[data-cell-index="${cellIndex}"]`;
                
                const cell = rowElement.querySelector(cellSelector);
                if (!cell) return;
                
                const isHeader = cell.tagName.toLowerCase() === 'th';
                const tagName = isHeader ? 'th' : 'td';
                const colSpan = cell.getAttribute('colspan');
                const colSpanAttr = colSpan ? ` colspan="${colSpan}"` : '';
                
                // 数値セルかどうかを判定
                let styleAttr = '';
                if (cell.querySelector('.calcvalue') || (!isNaN(Number(cell.textContent?.trim().replace(/[,\s]/g, ''))))) {
                    styleAttr = ' style="mso-number-format:\'#,##0.00\'"';
                }
                
                // セルのテキスト内容を取得してカンマをエスケープ
                const cellText = cell.textContent?.trim() || '';
                // HTMLではエンティティを使用してエスケープ
                const escapedCellText = cellText.replace(/"/g, '&quot;');
                htmlContent += `    <${tagName}${colSpanAttr}${styleAttr}>${escapedCellText}</${tagName}>\n`;
            });
            
            htmlContent += '  </tr>\n';
        });
        
        htmlContent += '</table>';
        
        return htmlContent;
    };

    // 選択されたセルからテキストを生成する関数
    const getSelectedCellsText = (): string => {
        // 選択範囲が存在しない場合は空文字を返す
        if (Object.keys(selectedCells).length === 0) return '';
        
        // 選択された行を特定
        const selectedRowIndices = Object.keys(selectedCells).map(Number).sort((a, b) => a - b);
        
        // タブ区切りのテキストを生成
        let textContent = '';
        
        // 選択された各行のテキストを生成
        let validRowCount = 0;
        selectedRowIndices.forEach((rowIndex) => {
            // DOM要素から対応する行を見つける
            const rowElement = document.querySelector(`tr[data-row-index="${rowIndex}"]`) as HTMLTableRowElement;
            if (!rowElement) return;
            
            // 折りたたまれたグループのtbody内の行はスキップ
            const tbody = rowElement.closest('tbody');
            if (tbody && tbody.classList.contains('collapsed')) return;
            
            // グループヘッダー行かどうかを確認
            const isGroupHeader = rowElement.classList.contains('group-header');
            
            // この行で選択されているセルインデックスを取得
            const selectedCellIndices = Object.keys(selectedCells[rowIndex]).map(Number).sort((a, b) => a - b);
            
            // 選択されたセルが一つもない場合はスキップ
            if (selectedCellIndices.length === 0) return;
            
            let rowText = '';
            let isFirstCell = true;
            
            // 行内の選択されたセルのみを処理
            selectedCellIndices.forEach((cellIndex) => {
                // セル要素を取得
                const cellSelector = isGroupHeader && cellIndex === 0 ? 
                    'th' : // グループヘッダーの最初のセルはth
                    `[data-cell-index="${cellIndex}"]`;
                
                const cellElement = rowElement.querySelector(cellSelector) as HTMLElement;
                if (!cellElement) return;
                
                // タブ区切りを追加（最初のセル以外）
                if (!isFirstCell) {
                    rowText += '\t';
                }
                isFirstCell = false;
                
                // セルのテキスト内容を追加（カンマとタブをエスケープ）
                const cellText = cellElement.textContent?.trim() || '';
                // カンマまたはタブが含まれる場合はダブルクォートで囲む
                const needsEscaping = cellText.includes(',') || cellText.includes('\t');
                const escapedCellText = needsEscaping ? `"${cellText.replace(/"/g, '""')}"` : cellText;
                rowText += escapedCellText;
            });
            
            // 行テキストが空でなければ追加
            if (rowText) {
                if (validRowCount > 0) {
                    textContent += '\n';
                }
                textContent += rowText;
                validRowCount++;
            }
        });
        
        return textContent;
    };

    // 共通のセル処理ロジック（actualRowIndex = DOM の data-row-index）
    const processCellData = useCallback((actualRowIndex: number, targetCellIndex: number, cellData: string, updates: any, pasteHistoryTypes?: Set<HistoryActionType>, pasteColor?: string): { rowIndex: number, cellIndex: number } | null => {
        const isValueCellIndex =
            targetCellIndex === 1 ||
            (sheetLayers.length > 1 &&
                targetCellIndex >= cols.layerStart &&
                targetCellIndex < cols.layerStart + sheetLayers.length);
        const resolvePasteLayerId = (cellIndex: number): string => {
            if (sheetLayers.length > 1 && cellIndex >= cols.layerStart) {
                return sheetLayers[cellIndex - cols.layerStart]?.id ?? activeLayerId;
            }
            return activeLayerId;
        };

        if (isLayerHeaderRow(actualRowIndex)) {
            const li = targetCellIndex - cols.layerStart;
            if (li >= 0 && li < sheetLayers.length) {
                const layer = sheetLayers[li];
                const nextName = cellData.trim();
                if (layer && nextName !== (layer.name || '')) {
                    if (!updates.layerRenames) updates.layerRenames = [];
                    updates.layerRenames.push({ layerId: layer.id, name: nextName });
                    pasteHistoryTypes?.add(HistoryActionType.RENAME_SHEET_LAYER);
                    return { rowIndex: actualRowIndex, cellIndex: targetCellIndex };
                }
            }
            return null;
        }

        const groupIndex = Math.floor(actualRowIndex / 1000);
        if (groupIndex < 0 || groupIndex >= groupData.length) {
            return null;
        }
        const targetGroup = groupData[groupIndex];
        const isGroupHeader = actualRowIndex % 1000 === 0;
        const rowIndexInGroup = (actualRowIndex % 1000) - 1;
        const targetRow =
            !isGroupHeader && targetGroup.rows?.[rowIndexInGroup]
                ? targetGroup.rows[rowIndexInGroup]
                : null;

        if (isGroupHeader) {
                    // グループヘッダー行（Ungroupedグループは編集不可）
            if (targetCellIndex === 0 && targetGroup && targetGroup.id !== 'ungrouped') {
                // 現在のタイトルと比較（実際のタイトルのみを使用）
                const correspondingGroup = groups.find(g => g.id === targetGroup!.id);
                const currentTitle = correspondingGroup?.title || '';
                if (currentTitle !== cellData) {
                    if (!updates.groupTitles) updates.groupTitles = [];
                    updates.groupTitles.push({ groupId: targetGroup!.id, title: cellData });
                    
                    // 履歴タイプを収集（実際に変更がある場合のみ）
                    if (pasteHistoryTypes) {
                        pasteHistoryTypes.add(HistoryActionType.EDIT_GROUP_TITLE);
                    }
                    return { rowIndex: actualRowIndex, cellIndex: targetCellIndex };
                }
                    }
            return null;
        }

        if (!targetRow?.pouchId) {
            return null;
        }
        const safeTargetRow = targetRow;

        if (targetCellIndex === 0) {
                        // パウチタイトル
                        const currentPouch = pouches.find(p => p.id === safeTargetRow.pouchId);
                        const currentTitle = currentPouch?.title || '';
                        const currentColor = currentPouch?.color || '';
                        
                        // タイトルまたは色が変更された場合のみ更新
                        const titleChanged = currentTitle !== cellData;
                        const colorChanged = pasteColor && currentColor !== pasteColor;
                        
                        if (titleChanged || colorChanged) {
                            if (titleChanged) {
                                if (!updates.pouchTitles) updates.pouchTitles = [];
                                updates.pouchTitles.push({ pouchId: safeTargetRow.pouchId!, title: cellData });
                                
                                // 履歴タイプを収集（実際に変更がある場合のみ）
                                if (pasteHistoryTypes) {
                                    pasteHistoryTypes.add(HistoryActionType.EDIT_POUCH_TITLE);
                                }
                            }
                            if (colorChanged) {
                                if (!updates.pouchColors) updates.pouchColors = [];
                                updates.pouchColors.push({ pouchId: safeTargetRow.pouchId!, color: pasteColor! });
                            }
                            return { rowIndex: actualRowIndex, cellIndex: targetCellIndex };
                        }
        } else if (isValueCellIndex) {
            const currentPouch = pouches.find(p => p.id === safeTargetRow.pouchId);
            if (!currentPouch) return null;
            if (currentPouch.isGroupTotalPouch) return null;
            if (currentSheet?.balancingPouchId && currentPouch.id === currentSheet.balancingPouchId) {
                return null;
            }

            const editLayerId = resolvePasteLayerId(targetCellIndex);
            const storedLayerValue =
                sheetLayers.length > 1
                    ? (safeTargetRow.layerValues?.[editLayerId] ?? getLayerValue(currentPouch, editLayerId))
                    : currentPouch.value;
            const pushValueUpdate = (
                value: number,
                multiplier: number,
                lastExpression?: string,
                valueWhenLastExpression?: number,
                multiplyWhenLastExpression?: number
            ) => {
                if (!updates.pouchValues) updates.pouchValues = [];
                updates.pouchValues.push({
                    pouchId: safeTargetRow.pouchId!,
                    layerId: editLayerId,
                    value,
                    multiplier,
                    lastExpression,
                    valueWhenLastExpression,
                    multiplyWhenLastExpression,
                });
                pasteHistoryTypes?.add(HistoryActionType.EDIT_POUCH_VALUE);
                return { rowIndex: actualRowIndex, cellIndex: targetCellIndex };
            };

            const hasOperator = /[+\-*/×÷xX^%:~＋－＊／＝％：～?=]/.test(cellData);
            if (hasOperator) {
                if (!isValidFormulaForPaste(cellData)) {
                    if (!updates.pouchErrors) updates.pouchErrors = [];
                    updates.pouchErrors.push({ pouchId: safeTargetRow.pouchId!, hasError: true });
                    return null;
                }
                const result = handleUnifiedExpression(
                    cellData,
                    storedLayerValue,
                    currentPouch.multiplier,
                    !!currentPouch.isTime,
                    !!(currentPouch.isGroupTotalPouch && !currentPouch.isTime),
                );
                if (result?.hasError) {
                    if (!updates.pouchErrors) updates.pouchErrors = [];
                    updates.pouchErrors.push({ pouchId: safeTargetRow.pouchId!, hasError: true });
                } else if (result?.result) {
                    const hasValueChanged = storedLayerValue !== result.result.value;
                    const hasMultiplierChanged = currentPouch.multiplier !== result.result.multiplier;
                    if (hasValueChanged || hasMultiplierChanged) {
                        return pushValueUpdate(
                            result.result.value,
                            result.result.multiplier,
                            result.lastExpression,
                            storedLayerValue,
                            currentPouch.multiplier
                        );
                    }
                    if (!updates.pouchErrors) updates.pouchErrors = [];
                    updates.pouchErrors.push({ pouchId: safeTargetRow.pouchId!, hasError: false });
                }
            } else {
                const cleanedData = cellData.replace(/,/g, '');
                const numValue = parseFloat(cleanedData);
                if (!isNaN(numValue)) {
                    if (storedLayerValue !== numValue) {
                        return pushValueUpdate(numValue, currentPouch.multiplier || 1, '', undefined, undefined);
                    }
                    if (!updates.pouchErrors) updates.pouchErrors = [];
                    updates.pouchErrors.push({ pouchId: safeTargetRow.pouchId!, hasError: false });
                } else {
                    if (!updates.pouchErrors) updates.pouchErrors = [];
                    updates.pouchErrors.push({ pouchId: safeTargetRow.pouchId!, hasError: true });
                }
            }
        } else if (targetCellIndex === multOpCell) {
                        // マルチプライヤー符号（×/÷）
                        const currentPouch = pouches.find(p => p.id === safeTargetRow.pouchId);
                        if (currentPouch) {
                            // GTPの場合は構造化ペーストをブロック
                            if (currentPouch.isGroupTotalPouch) {
                                return null;
                            }
                            // BPの場合は符号ペーストをブロック
                            if (currentSheet?.balancingPouchId && currentPouch.id === currentSheet.balancingPouchId) {
                                return null;
                            }
                    const isMultiplySymbol = /[×xX✕✖✗☓✘❌✖*]/.test(cellData);
                    const isDivideSymbol = /[\/／÷➗]/.test(cellData);
                            
                            if (isMultiplySymbol) {
                                // 乗算記号：マルチ値を正値に
                                const newMultiplier = Math.abs(currentPouch.multiplier);
                                if (newMultiplier !== currentPouch.multiplier) {
                                    if (!updates.pouchMultipliers) updates.pouchMultipliers = [];
                                    updates.pouchMultipliers.push({ 
                                        pouchId: safeTargetRow.pouchId!, 
                                        multiplier: newMultiplier 
                                    });
                                    
                                    // 履歴タイプを収集
                                    if (pasteHistoryTypes) {
                                        pasteHistoryTypes.add(HistoryActionType.EDIT_POUCH_VALUE);
                                    }
                                }
                            } else if (isDivideSymbol) {
                                // 除算記号：マルチ値を負値に
                                const newMultiplier = -Math.abs(currentPouch.multiplier);
                                if (newMultiplier !== currentPouch.multiplier) {
                                    if (!updates.pouchMultipliers) updates.pouchMultipliers = [];
                                    updates.pouchMultipliers.push({ 
                                        pouchId: safeTargetRow.pouchId!, 
                                        multiplier: newMultiplier 
                                    });
                                    
                                    // 履歴タイプを収集
                                    if (pasteHistoryTypes) {
                                        pasteHistoryTypes.add(HistoryActionType.EDIT_POUCH_VALUE);
                                    }
                                }
                            }
                            // 無効な符号の場合は何もしない（エラーにもしない）
                        }
            return { rowIndex: actualRowIndex, cellIndex: targetCellIndex };
        } else if (targetCellIndex === multValCell) {
                // マルチプライヤー値（倍率セル）（カンマを除去してから解析）
                const cleanedData = cellData.replace(/,/g, '');
                const multiplierValue = parseFloat(cleanedData);
                        if (!isNaN(multiplierValue) && multiplierValue !== 0) {
                            // 負値の場合は絶対値に変換してマルチに設定、パウチ値を正負反転
                            const absValue = Math.abs(multiplierValue);
                            const currentPouch = pouches.find(p => p.id === safeTargetRow.pouchId);
                            
                            // GTPの場合は構造化ペーストをブロック
                            if (currentPouch && currentPouch.isGroupTotalPouch) {
                                return null;
                            }
                            
                            // BPの場合はマルチペーストをブロック
                            if (currentPouch && currentSheet?.balancingPouchId && currentPouch.id === currentSheet.balancingPouchId) {
                                return null;
                            }
                            
                            let hasChanged = false;
                            
                            if (multiplierValue < 0 && currentPouch) {
                                const currentSign = currentPouch.multiplier < 0 ? -1 : 1;
                                const finalMultiplier = currentSign * absValue;
                                const flipLayerId = activeLayerId;
                                const currentLayerValue =
                                    sheetLayers.length > 1
                                        ? (safeTargetRow.layerValues?.[flipLayerId] ??
                                          getLayerValue(currentPouch, flipLayerId))
                                        : currentPouch.value;
                                const newPouchValue = -currentLayerValue;

                                if (currentLayerValue !== newPouchValue || currentPouch.multiplier !== finalMultiplier) {
                                    if (!updates.pouchValues) updates.pouchValues = [];
                                    updates.pouchValues.push({
                                        pouchId: safeTargetRow.pouchId!,
                                        layerId: sheetLayers.length > 1 ? flipLayerId : undefined,
                                        value: newPouchValue,
                                        multiplier: finalMultiplier,
                                    });
                                    hasChanged = true;
                                }
                            } else {
                                // 正値が貼り付けられた場合、通常の処理
                                const currentSign = currentPouch ? (currentPouch.multiplier < 0 ? -1 : 1) : 1;
                                const finalMultiplier = currentSign * absValue;
                                
                                // 実際にマルチプライヤーが変更されるかチェック
                                if (!currentPouch || currentPouch.multiplier !== finalMultiplier) {
                                    if (!updates.pouchMultipliers) updates.pouchMultipliers = [];
                                    updates.pouchMultipliers.push({ 
                                        pouchId: safeTargetRow.pouchId!, 
                                        multiplier: finalMultiplier 
                                    });
                                    hasChanged = true;
                                }
                            }
                            
                            // 履歴タイプを収集（実際に変更がある場合のみ）
                            if (hasChanged && pasteHistoryTypes) {
                                pasteHistoryTypes.add(HistoryActionType.EDIT_POUCH_VALUE);
                                return { rowIndex: actualRowIndex, cellIndex: targetCellIndex };
                            }
                            
                            if (!updates.pouchErrors) updates.pouchErrors = [];
                            updates.pouchErrors.push({ pouchId: safeTargetRow.pouchId!, hasError: false });
                        } else {
                            if (!updates.pouchErrors) updates.pouchErrors = [];
                            updates.pouchErrors.push({ pouchId: safeTargetRow.pouchId!, hasError: true });
                        }
        }

        return null;
    }, [
        groupData,
        groups,
        pouches,
        handleUnifiedExpression,
        isValidFormulaForPaste,
        currentSheet,
        sheetLayers,
        cols.layerStart,
        activeLayerId,
        multOpCell,
        multValCell,
    ]);

    // 共通の更新処理ロジック
    const applyUpdates = useCallback((updates: {
        groupTitles?: { groupId: string; title: string }[];
        pouchTitles?: { pouchId: string; title: string }[];
        pouchValues?: {
            pouchId: string;
            layerId?: string;
            value: number;
            multiplier: number;
            lastExpression?: string;
            valueWhenLastExpression?: number;
            multiplyWhenLastExpression?: number;
        }[];
        pouchMultipliers?: { pouchId: string; multiplier: number }[];
        pouchErrors?: { pouchId: string; hasError: boolean }[];
        pouchColors?: { pouchId: string; color: string }[];
        pasteHistoryType?: HistoryActionType;
    }) => {
        if (Object.keys(updates).length === 0) return;
        
            // 現在のシートデータから更新後のデータを作成
            let updatedGroups = groups;
            let updatedPouches = pouches;
            
            // グループタイトルを更新
            if (updates.groupTitles) {
                updatedGroups = groups.map(group => {
                    const groupUpdate = updates.groupTitles!.find(gt => gt.groupId === group.id);
                    return groupUpdate ? { ...group, title: groupUpdate.title } : group;
                });
            }
            
            // パウチ関連の更新を統合
            if (updates.pouchTitles || updates.pouchValues || updates.pouchMultipliers || updates.pouchErrors || updates.pouchColors) {
                updatedPouches = pouches.map(pouch => {
                    let updatedPouch = { ...pouch };
                    
                    // タイトル更新
                    const titleUpdate = updates.pouchTitles?.find(pt => pt.pouchId === pouch.id);
                    if (titleUpdate) {
                        updatedPouch.title = titleUpdate.title;
                    }
                    
                    // 値更新（同一パウチの複数レイヤー列はすべて適用）
                    const valueUpdatesForPouch =
                        updates.pouchValues?.filter(pv => pv.pouchId === pouch.id) ?? [];
                    for (const valueUpdate of valueUpdatesForPouch) {
                        const layerExtras = {
                            ...(valueUpdate.lastExpression !== undefined
                                ? { lastExpression: valueUpdate.lastExpression }
                                : {}),
                            ...(valueUpdate.valueWhenLastExpression !== undefined
                                ? { valueWhenLastExpression: valueUpdate.valueWhenLastExpression }
                                : {}),
                        };
                        if (valueUpdate.layerId && currentSheet) {
                            updatedPouch = setPouchLayerValue(
                                updatedPouch,
                                valueUpdate.layerId,
                                valueUpdate.value,
                                layerExtras,
                                currentSheet
                            );
                        } else {
                            updatedPouch = {
                                ...updatedPouch,
                                value: valueUpdate.value,
                                lastExpression: valueUpdate.lastExpression ?? updatedPouch.lastExpression,
                                valueWhenLastExpression:
                                    valueUpdate.valueWhenLastExpression ??
                                    updatedPouch.valueWhenLastExpression,
                            };
                        }
                        updatedPouch.multiplier = valueUpdate.multiplier;
                        updatedPouch.isNegative = valueUpdate.value < 0;
                        if (valueUpdate.multiplyWhenLastExpression !== undefined) {
                            updatedPouch.multiplyWhenLastExpression =
                                valueUpdate.multiplyWhenLastExpression;
                        }
                    }
                    
                    // マルチプライヤー更新
                    const multiplierUpdate = updates.pouchMultipliers?.find(pm => pm.pouchId === pouch.id);
                    if (multiplierUpdate) {
                        updatedPouch.multiplier = multiplierUpdate.multiplier;
                    }
                    
                    // 色更新
                    const colorUpdate = updates.pouchColors?.find(pc => pc.pouchId === pouch.id);
                    if (colorUpdate) {
                        updatedPouch.color = colorUpdate.color;
                    }
                
                // エラー状態更新
                const errorUpdate = updates.pouchErrors?.find(pe => pe.pouchId === pouch.id);
                if (errorUpdate !== undefined) {
                    }
                    
                    return updatedPouch;
                });
            }
            
            // 完全なシートデータとしてonBulkUpdateに渡す
            onBulkUpdate({
                completeGroups: updatedGroups,
                completePouches: updatedPouches,
                pasteHistoryType: updates.pasteHistoryType // ペースト履歴タイプを渡す
            } as any);

        // エラーフラグが設定されたパウチに対して1.5秒後に自動クリアするタイマーを設定
        if (updates.pouchErrors) {
            updates.pouchErrors.forEach(errorUpdate => {
                if (errorUpdate.hasError) {
                    setTimeout(() => {
                        // 1.5秒後にエラーフラグをクリア
                        onBulkUpdate({
                            completeGroups: groups,
                            completePouches: pouches.map(pouch => 
                                pouch.id === errorUpdate.pouchId 
                                    ? { ...pouch }
                                    : pouch
                            )
                        } as any);
                    }, 1500);
                }
            });
        }
    }, [groups, pouches, onBulkUpdate, currentSheet]);


    // CSV/TSV形式の行を解析する関数（ダブルクォートエスケープ対応）
    const parseDelimitedRow = (row: string, delimiter: string): string[] => {
        const result: string[] = [];
        let current = '';
        let inQuotes = false;
        let i = 0;
        
        while (i < row.length) {
            const char = row[i];
            
            if (char === '"') {
                if (inQuotes && i + 1 < row.length && row[i + 1] === '"') {
                    // エスケープされたダブルクォート（""）
                    current += '"';
                    i += 2;
                } else {
                    // クォートの開始/終了
                    inQuotes = !inQuotes;
                    i++;
                }
            } else if (char === delimiter && !inQuotes) {
                // 区切り文字（クォート外でのみ区切り文字として扱う）
                result.push(current.trim());
                current = '';
                i++;
            } else {
                current += char;
                i++;
            }
        }
        
        // 最後のフィールドを追加
        result.push(current.trim());
        
        return result;
    };

    // 貼り付けデータを処理する関数
    // 統合されたペースト処理関数
    const handlePasteData = useCallback((clipboardText: string) => {
        // 完全に空のクリップボードテキストをチェック
        if (!clipboardText || clipboardText.trim().length === 0) return;
        
        
        // データを解析（空行も保持する）
        const rows = clipboardText.split('\n').map(row => row.trim());
        
        let dataGrid: string[][];
        let isStructured = false;
        
        if (clipboardText.includes('\t')) {
            // タブ区切りの場合（TSV形式の標準的な解析を使用）
            dataGrid = rows.map(row => parseDelimitedRow(row, '\t'));
            isStructured = true;
        } else if (clipboardText.includes(',')) {
            // カンマ区切りの場合（CSV形式の標準的な解析を使用）
            dataGrid = rows.map(row => parseDelimitedRow(row, ','));
            isStructured = true;
        } else if (rows.length > 1) {
            // 改行のみで複数行の場合
            dataGrid = rows.map(row => [row]);
            isStructured = true;
        } else {
            // 単一値の場合
            dataGrid = [[clipboardText.trim()]];
            isStructured = false;
        }
        
        
        if (dataGrid.length === 0) return;
        
        // 選択範囲の情報を取得
        const selectedRowIndices = Object.keys(selectedCells).map(Number).sort((a, b) => a - b);
        if (selectedRowIndices.length === 0) return;
        
        const startRowIndex = selectedRowIndices[0];
        const selectedCellIndices = Object.keys(selectedCells[startRowIndex]).map(Number).sort((a, b) => a - b);
        if (selectedCellIndices.length === 0) return;
        
        const startCellIndex = selectedCellIndices[0];
        
        // 単独選択かどうかを判定
        const isSingleCellSelection = selectedRowIndices.length === 1 && 
            Object.keys(selectedCells[startRowIndex]).length === 1;

        // 単一セル貼り付け: クリップボードの余分なタブ・列を無視（隣列の × 等が混入して「体重+」になるのを防ぐ）
        if (isSingleCellSelection) {
            const primary = (dataGrid[0]?.[0] ?? '').trim();
            if (primary) {
                dataGrid = [[primary]];
                isStructured = false;
            }
        }
        
        const updates: {
            groupTitles?: { groupId: string; title: string }[];
            pouchTitles?: { pouchId: string; title: string }[];
            pouchValues?: {
                pouchId: string;
                layerId?: string;
                value: number;
                multiplier: number;
                lastExpression?: string;
                valueWhenLastExpression?: number;
                multiplyWhenLastExpression?: number;
            }[];
            pouchMultipliers?: { pouchId: string; multiplier: number }[];
            pouchErrors?: { pouchId: string; hasError: boolean }[];
            pouchColors?: { pouchId: string; color: string }[];
            layerRenames?: { layerId: string; name: string }[];
        } = {};
        
        // ペースト中に検出された履歴タイプを収集
        const pasteHistoryTypes = new Set<HistoryActionType>();
        
        // 実際に処理されたセルを追跡
        const processedCells: {[rowIndex: number]: {[cellIndex: number]: boolean}} = {};
        
        // 色情報の配列を準備（1列目のコピーの場合）
        const copiedColors: string[] = [];
        if (Object.keys(copiedCellColors).length > 0) {
            const copiedRowIndices = Object.keys(copiedCellColors).map(Number).sort((a, b) => a - b);
            copiedRowIndices.forEach((rowIndex) => {
                if (copiedCellColors[rowIndex] && copiedCellColors[rowIndex][0]) {
                    copiedColors.push(copiedCellColors[rowIndex][0]);
                }
            });
        }
        
        // 連続した行インデックスのマッピング（レイヤー見出し行を先頭に含む）
        const sequentialRowIndices: number[] = [];
        const actualToSequentialMap: {[actualRowIndex: number]: number} = {};
        let sequentialIndex = 0;

        actualToSequentialMap[LAYER_HEADER_ROW_INDEX] = sequentialIndex;
        sequentialRowIndices.push(sequentialIndex);
        sequentialIndex++;

        const resolveActualRowFromSequential = (seq: number): number | undefined => {
            const entry = Object.entries(actualToSequentialMap).find(([, s]) => s === seq);
            return entry ? Number(entry[0]) : undefined;
        };
        
        for (let groupIndex = 0; groupIndex < groupData.length; groupIndex++) {
            const group = groupData[groupIndex];
            // グループヘッダー行
            const actualHeaderIndex = groupIndex * 1000;
            sequentialRowIndices.push(sequentialIndex);
            actualToSequentialMap[actualHeaderIndex] = sequentialIndex;
            sequentialIndex++;
            
            // パウチ行
            for (let rowInGroup = 0; rowInGroup < group.rows.length; rowInGroup++) {
                const actualRowIndex = groupIndex * 1000 + rowInGroup + 1;
                sequentialRowIndices.push(sequentialIndex);
                actualToSequentialMap[actualRowIndex] = sequentialIndex;
                sequentialIndex++;
            }
        }
        
        
        if (!isStructured || isSingleCellSelection) {
            // 単体ペースト処理：全選択セルに同じ値を適用、または単一セルから展開
            if (isSingleCellSelection && isStructured) {
                // 単一セル選択で構造化データ：起点から展開
                for (let dataRowIndex = 0; dataRowIndex < dataGrid.length; dataRowIndex++) {
                    // 実際のテーブル行インデックスを連続インデックスに変換
                    const sequentialStartIndex = actualToSequentialMap[startRowIndex];
                    if (sequentialStartIndex === undefined) {
                        break;
                    }
                    
                    const targetSequentialIndex = sequentialStartIndex + dataRowIndex;
                    if (targetSequentialIndex >= sequentialRowIndices.length) {
                        break;
                    }
                    const targetActualRowIndex = resolveActualRowFromSequential(targetSequentialIndex);
                    if (targetActualRowIndex === undefined) {
                        break;
                    }
                    
                    const dataRow = dataGrid[dataRowIndex];
                    
                    for (let dataCellIndex = 0; dataCellIndex < dataRow.length; dataCellIndex++) {
                        const targetCellIndex = startCellIndex + dataCellIndex;
                        const cellData = dataRow[dataCellIndex];
                        
                        if (cellData !== undefined && cellData !== '') {
                            // 1列目で色情報がある場合は色も渡す
                            const pasteColor = (targetCellIndex === 0 && copiedColors.length > 0) 
                                ? copiedColors[dataRowIndex % copiedColors.length]
                                : undefined;
                            const result = processCellData(targetActualRowIndex, targetCellIndex, cellData, updates, pasteHistoryTypes, pasteColor);
                            if (result) {
                                if (!processedCells[result.rowIndex]) processedCells[result.rowIndex] = {};
                                processedCells[result.rowIndex][result.cellIndex] = true;
                            }
                        }
                    }
                }
            } else {
                // 複数セル選択で単一値：全選択セルに同じ値を適用
                const singleValue = dataGrid[0][0];
                const singleColor = copiedColors.length > 0 ? copiedColors[0] : undefined;
                selectedRowIndices.forEach(actualRowIndex => {
                    const cellIndices = Object.keys(selectedCells[actualRowIndex]).map(Number);
                    cellIndices.forEach(cellIndex => {
                            const pasteColor = (cellIndex === 0 && singleColor) ? singleColor : undefined;
                            const result = processCellData(actualRowIndex, cellIndex, singleValue, updates, pasteHistoryTypes, pasteColor);
                            if (result) {
                                if (!processedCells[result.rowIndex]) processedCells[result.rowIndex] = {};
                                processedCells[result.rowIndex][result.cellIndex] = true;
                            }
                        });
                });
            }
        } else {
            // 構造化ペースト処理：選択範囲の形状に応じた展開（2パス処理）
            const maxSelectedRowIndex = Math.max(...selectedRowIndices);
            const maxSelectedCellIndex = Math.max(...selectedRowIndices.flatMap(rowIndex => 
                Object.keys(selectedCells[rowIndex]).map(Number)
            ));
            
            const startSequentialIndex = actualToSequentialMap[startRowIndex];
            const maxSequentialIndex = actualToSequentialMap[maxSelectedRowIndex];
            if (startSequentialIndex === undefined || maxSequentialIndex === undefined) {
                return;
            }
            
            const selectedRowCount = maxSequentialIndex - startSequentialIndex + 1;
            const selectedCellCount = maxSelectedCellIndex - startCellIndex + 1;
            
            // データを貼り付け（2パス処理：先にマルチ値以外、後で符号）
            const processPaste = (isFirstPass: boolean) => {
                // 範囲選択：選択範囲内で処理、余った部分にはリピート
                for (let dataRowIndex = 0; dataRowIndex < Math.max(dataGrid.length, selectedRowCount); dataRowIndex++) {
                    const targetSequentialIndex = startSequentialIndex + dataRowIndex;
                    const dataRow = dataGrid[dataRowIndex % dataGrid.length]; // リピート
                    
                    // 連続インデックスから実際の行インデックスを逆算
                    let targetActualRowIndex: number | undefined;
                    for (const [actualIdx, seqIdx] of Object.entries(actualToSequentialMap)) {
                        if (seqIdx === targetSequentialIndex) {
                            targetActualRowIndex = parseInt(actualIdx);
                            break;
                        }
                    }
                    
                    if (targetActualRowIndex === undefined) continue;
                    
                    for (let dataCellIndex = 0; dataCellIndex < Math.max(dataRow.length, selectedCellCount); dataCellIndex++) {
                        const targetCellIndex = startCellIndex + dataCellIndex;
                        const cellData = dataRow[dataCellIndex % dataRow.length]; // リピート
                        
                        // 対象セルが選択範囲内にある場合のみ処理
                        if (selectedCells[targetActualRowIndex] && selectedCells[targetActualRowIndex][targetCellIndex]) {
                            // 1パス目：マルチ値以外、2パス目：符号のみ
                            if ((isFirstPass && targetCellIndex !== multOpCell) || (!isFirstPass && targetCellIndex === multOpCell)) {
                                // 1列目で色情報がある場合は色も渡す
                                const pasteColor = (targetCellIndex === 0 && copiedColors.length > 0) 
                                    ? copiedColors[dataRowIndex % copiedColors.length]
                                    : undefined;
                                const result = processCellData(targetActualRowIndex, targetCellIndex, cellData, updates, pasteHistoryTypes, pasteColor);
                                if (result) {
                                    if (!processedCells[result.rowIndex]) processedCells[result.rowIndex] = {};
                                    processedCells[result.rowIndex][result.cellIndex] = true;
                                }
                            }
                        }
                    }
                }
            };        
            // 1パス目：マルチ値以外を処理
            processPaste(true);
            // 2パス目：符号を処理
            processPaste(false);
        }
        
        // 実際に変更があった場合のみ更新を適用
        const hasAnyUpdates = Object.keys(updates).some(key => {
            const updateArray = updates[key as keyof typeof updates];
            return updateArray && updateArray.length > 0;
        });
        
        if (hasAnyUpdates) {
            // ペースト範囲から履歴タイプを判定
            let pasteHistoryType: HistoryActionType;
            if (pasteHistoryTypes.size > 1) {
                pasteHistoryType = HistoryActionType.PASTE_BULK_UPDATE;
            } else if (pasteHistoryTypes.size === 1) {
                pasteHistoryType = pasteHistoryTypes.values().next().value!;
            } else {
                pasteHistoryType = HistoryActionType.EDIT_POUCH_VALUE;
            }
            
            // 色情報を反映（1列目から1列目へのペーストの場合のみ）
            // ただし、コピー時のテキストデータと一致する場合のみ（Excelなど外部からのペーストを除外）
            if (Object.keys(copiedCellColors).length > 0 && Object.keys(processedCells).length > 0) {
                if (copiedTextData && clipboardText === copiedTextData) {
                    // テキストデータが一致する場合のみ色情報を適用
                    const pouchColorUpdates: { pouchId: string; color: string }[] = [];
                    
                    // コピー元の色情報を配列にして順序を保持
                    const copiedRowIndices = Object.keys(copiedCellColors).map(Number).sort((a, b) => a - b);
                    const copiedColors: string[] = [];
                    copiedRowIndices.forEach((rowIndex) => {
                        if (copiedCellColors[rowIndex] && copiedCellColors[rowIndex][0]) {
                            copiedColors.push(copiedCellColors[rowIndex][0]);
                        }
                    });
                    
                    // ペースト先の1列目のセルのみをフィルタリング
                    const allPastedRowIndices = Object.keys(processedCells).map(Number).sort((a, b) => a - b);
                    const pastedFirstColumnRows: number[] = [];
                    allPastedRowIndices.forEach(rowIndex => {
                        // 1列目にペーストされた場合のみ
                        if (processedCells[rowIndex] && processedCells[rowIndex][0]) {
                            pastedFirstColumnRows.push(rowIndex);
                        }
                    });
                    
                    // 1列目にペーストされた行に対して色を適用
                    pastedFirstColumnRows.forEach((rowIndex, idx) => {
                        // コピー元の相対インデックスから色を取得（繰り返しの場合は%で循環）
                        const relativeIndex = idx % copiedColors.length;
                        const color = copiedColors[relativeIndex];
                        
                        if (color) {
                            // rowIndexから対応するPouchを見つける
                            const groupIndex = Math.floor(rowIndex / 1000);
                            const pouchIndexInGroup = (rowIndex % 1000) - 1;
                            
                            if (groupData[groupIndex] && groupData[groupIndex].rows[pouchIndexInGroup]) {
                                const row = groupData[groupIndex].rows[pouchIndexInGroup];
                                if (row.pouchId) {
                                    pouchColorUpdates.push({ pouchId: row.pouchId, color });
                                }
                            }
                        }
                    });
                    
                    if (pouchColorUpdates.length > 0) {
                        updates.pouchColors = pouchColorUpdates;
                    }
                } else {
                    // テキストデータが一致しない場合は色情報をクリア
                    setCopiedCellColors({});
                    setCopiedTextData('');
                }
            }
            
            // 更新を適用（履歴タイプを含む）
            applyUpdates({
                ...updates,
                pasteHistoryType
            });
        }

        if (updates.layerRenames?.length) {
            for (const { layerId, name } of updates.layerRenames) {
                void Promise.resolve(onRenameSheetLayer(layerId, name));
            }
        }
        
        // 構造化貼り付けで単一セル選択の場合、選択範囲を貼り付けデータのサイズに拡張
        if (isStructured && isSingleCellSelection) {
            // 貼り付けデータの範囲を計算（元の開始位置から最終位置まで）
            const dataRows = dataGrid.length;
            const dataCols = dataGrid.length > 0 ? dataGrid[0].length : 0;
            
            
            if (dataRows > 0 && dataCols > 0) {
                // 連続インデックスに基づいて選択範囲を計算
                const sequentialStartIndex = actualToSequentialMap[startRowIndex];
                if (sequentialStartIndex !== undefined && sequentialStartIndex + dataRows - 1 < sequentialRowIndices.length) {
                    // 連続インデックスから実際の行インデックスを逆算
                    let newEndRowIndex: number | undefined;
                    const targetSequentialEndIndex = sequentialStartIndex + dataRows - 1;
                    for (const [actualIdx, seqIdx] of Object.entries(actualToSequentialMap)) {
                        if (seqIdx === targetSequentialEndIndex) {
                            newEndRowIndex = parseInt(actualIdx);
                            break;
                        }
                    }
                    
                    if (newEndRowIndex !== undefined) {
                        const newEndCellIndex = startCellIndex + dataCols - 1;
                    
                    
                        // 選択範囲を更新（実際のテーブル行インデックスを使用）
                        const newSelectedCells: {[rowIndex: number]: {[cellIndex: number]: boolean}} = {};
                        for (let i = 0; i < dataRows; i++) {
                            const targetSequentialIndex = sequentialStartIndex + i;
                            // 連続インデックスから実際の行インデックスを逆算
                            let targetActualRowIndex: number | undefined;
                            for (const [actualIdx, seqIdx] of Object.entries(actualToSequentialMap)) {
                                if (seqIdx === targetSequentialIndex) {
                                    targetActualRowIndex = parseInt(actualIdx);
                                    break;
                                }
                            }
                            
                            if (targetActualRowIndex !== undefined) {
                                newSelectedCells[targetActualRowIndex] = {};
                                for (let c = startCellIndex; c <= newEndCellIndex; c++) {
                                    newSelectedCells[targetActualRowIndex][c] = true;
                                }
                            }
                        }
                
                    // ペースト時は全範囲を選択するため、空行削除は不要
                    
                        if (Object.keys(newSelectedCells).length > 0) {
                            setSelectedCells(newSelectedCells);
                            setSelectionStart({ row: startRowIndex, cell: startCellIndex });
                            setSelectionEnd({ row: newEndRowIndex, cell: newEndCellIndex });
                        }
                    }
                }
            }
        }

        // ペースト成功したセルに.cell-pastedクラスを付与
        setTimeout(() => {
            // processedCellsから成功したセルの情報を取得
            
            // デバッグ: すべての行要素を確認
            const allRows = document.querySelectorAll('tr[data-row-index]');
            
            Object.keys(processedCells).forEach(rowIndexStr => {
                const rowIndex = parseInt(rowIndexStr);
                const cellIndices = Object.keys(processedCells[rowIndex]).map(Number);
                
                cellIndices.forEach(cellIndex => {
                    // まず行要素を確認
                    const rowElement = document.querySelector(`tr[data-row-index="${rowIndex}"]`);
                    
                    if (rowElement) {
                        // 行内のセルを確認
                        const cells = rowElement.querySelectorAll('td, th');
                        const cellElement = rowElement.querySelector(`td:nth-child(${cellIndex + 1}), th:nth-child(${cellIndex + 1})`) as HTMLElement;
                        if (cellElement) {
                            cellElement.classList.add('cell-pasted');
                        }
                    }
                });
            });
            
            // 1.5秒後にクラスを削除
            setTimeout(() => {
                Object.keys(processedCells).forEach(rowIndexStr => {
                    const rowIndex = parseInt(rowIndexStr);
                    const cellIndices = Object.keys(processedCells[rowIndex]).map(Number);
                    
                    cellIndices.forEach(cellIndex => {
                        const rowElement = document.querySelector(`tr[data-row-index="${rowIndex}"]`);
                        if (rowElement) {
                            const cellElement = rowElement.querySelector(`td:nth-child(${cellIndex + 1}), th:nth-child(${cellIndex + 1})`) as HTMLElement;
                            if (cellElement) {
                                cellElement.classList.remove('cell-pasted');
                            }
                        }
                    });
                });
            }, 1500);
        }, 100);
    }, [selectedCells, groupData, processCellData, applyUpdates, onRenameSheetLayer, multOpCell, setSelectedCells, setSelectionStart, setSelectionEnd, copiedCellColors, copiedTextData, setCopiedCellColors, setCopiedTextData]);

    // 旧関数は削除済み - 統合されたhandlePasteDataを使用

    // Pasteボタン用のハンドラー
    const handlePasteButtonClick = useCallback(async (e: React.PointerEvent<HTMLButtonElement> | React.TouchEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        
        // セル選択がない場合は何もしない
        const hasSelectedCells = Object.keys(selectedCells).length > 0;
        if (!hasSelectedCells) {
            console.warn('ペーストするには先にセルを選択してください');
            return;
        }
        
        let clipboardText = '';
        let success = false;
        
        try {
            // モダンなClipboard APIを試す
            if (navigator.clipboard && window.isSecureContext) {
                clipboardText = await navigator.clipboard.readText();
                success = true;
            }
        } catch (err) {
            console.warn('Clipboard API でのペーストに失敗:', err);
        }
        
        // フォールバック: 一時的なtextareaを使用してペーストイベントを発生させる
        if (!success) {
            try {
                // 一時的なtextareaを作成
                const textarea = document.createElement('textarea');
                textarea.style.position = 'fixed';
                textarea.style.left = '-9999px';
                textarea.style.top = '-9999px';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                
                // フォーカスして選択
                textarea.focus();
                textarea.select();
                
                // ペーストイベントを待つPromise
                const pastePromise = new Promise<string>((resolve, reject) => {
                    const timeout = setTimeout(() => {
                        reject(new Error('ペーストタイムアウト'));
                    }, 3000);
                    
                    const pasteHandler = (e: ClipboardEvent) => {
                        clearTimeout(timeout);
                        textarea.removeEventListener('paste', pasteHandler);
                        
                        const clipboardData = e.clipboardData?.getData('text/plain') || '';
                        if (clipboardData) {
                            resolve(clipboardData);
                        } else {
                            reject(new Error('クリップボードデータなし'));
                        }
                    };
                    
                    textarea.addEventListener('paste', pasteHandler);
                });
                
                // execCommandでペーストを実行
                document.execCommand('paste');
                
                clipboardText = await pastePromise;
                success = true;
                
                // クリーンアップ
                document.body.removeChild(textarea);
                
            } catch (err) {
                console.error('フォールバックペースト失敗:', err);
                // ユーザーに手動でCtrl+Vを促す（より親切なメッセージ）
                console.warn('ペーストボタンが動作しません。キーボードショートカットをお試しください：\n- Windows/Linux: Ctrl+V\n- Mac: Cmd+V');
                
                // 短時間だけエラー状態を表示
                setPasteSuccess(false);
                // 代わりにコンソールでガイダンスを提供
                return;
            }
        }
        
        // ペーストデータの処理
        if (clipboardText) {
            handlePasteData(clipboardText);
            setPasteSuccess(true);
            setTimeout(() => setPasteSuccess(false), 1500);
        } else {
            console.warn('クリップボードにデータがありません');
        }
    }, [selectedCells, handlePasteData]);

    // Excel形式としてエクスポートする関数
    const exportToExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Sheet1');
        
        let currentRow = 1;
        
        // 各グループごとに別々のテーブルを作成
        for(const group of groupData) {
            // 展開されているグループの詳細行を追加
            if (expandedGroups.has(group.id) && group.rows.length > 0) {
            // グループ名を取得
            const groupName = group.id === 'ungrouped'
                ? appCopy.table.ungrouped
                : (group.groupTitle || group.placeholderText || appCopy.table.groupN(groupData.indexOf(group) + 1));
            
                // テーブル開始行を記録
                const tableStartRow = currentRow;
                
                // ヘッダー行を追加
                const excelColumns = appCopy.table.excel.columns.split(', ');
                const headerRow = worksheet.addRow(excelColumns);
                headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } }; // 白色の文字
                headerRow.eachCell((cell) => {
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'FF4472C4' } // 青系の背景色
                    };
                    cell.border = {
                        top: { style: 'thin' },
                        left: { style: 'thin' },
                        bottom: { style: 'thin' },
                        right: { style: 'thin' }
                    };
                });
                currentRow++;
                
                // データ行を準備して追加（テーブルの実際の行データ）
                const tableRows = [];
                const firstDataRow = currentRow; // 最初のデータ行番号を記録
                
                // グループ内の各パウチをデータ行として追加
                for (const row of group.rows) {
                    const actualValue = row.value;
                    const display = getMultiplierDisplay(row.multiplier, row.isTime);
                    const colorLabel = getColorLabel(row.color, colorLegends, pouches, appCopy.table.misc);
                const title = row.pouchTitle || colorLabel.text;
                    
                    // テーブル用のデータ配列を作成
                    tableRows.push([
                        groupName,
                        title,
                        actualValue,
                        display.isDivision ? '/' : '×',
                        `${display.value}${display.showSymbolAfter ? display.symbol : ''}`,
                        { formula: `IF(D${currentRow}="×",C${currentRow}*E${currentRow},C${currentRow}/E${currentRow})` }
                    ]);
                    
                    // 物理的な行を追加（Excelでは計算用に元の数値を使用）
                    const dataRow = worksheet.addRow([
                        groupName,
                        title,
                        actualValue,
                        row.multiplier < 0 ? '/' : '×', // Excel計算用に固定
                        Math.abs(row.multiplier), // Excel計算用に数値のみ
                        { formula: `IF(D${currentRow}="×",C${currentRow}*E${currentRow},C${currentRow}/E${currentRow})` }
                    ]);
                    
                    // フォントカラーを黒に
                    dataRow.font = { color: { argb: 'FF000000' } }; // 黒色の文字
                    
                    // 数値セルのフォーマットを設定
                    worksheet.getCell(`C${currentRow}`).numFmt = '0';
                    worksheet.getCell(`E${currentRow}`).numFmt = '0';
                    worksheet.getCell(`F${currentRow}`).numFmt = '#,##0.00';
                    
                    currentRow++;
                }
                
                // テーブル範囲に合計行を含める(次の行)
                const lastDataRow = currentRow - 1;
                const totalRow = worksheet.addRow([
                    groupName,
                    appCopy.table.excel.totalRow,
                    '',
                    '',
                    { formula: `SUMIF(D${firstDataRow}:D${lastDataRow},"×",E${firstDataRow}:E${lastDataRow})+COUNTIF(D${firstDataRow}:D${lastDataRow},"/")` }, // SUMIF+COUNTIF関数を使用
                    { formula: `SUM(F${firstDataRow}:F${lastDataRow})` }    // SUM関数を使用
                ]);
                
                // 合計行のスタイル設定
                totalRow.font = { bold: true, color: { argb: 'FF000000' } }; // 黒色の太字
                totalRow.eachCell((cell) => {
                    cell.border = {
                        top: { style: 'thin' },
                        left: { style: 'thin' },
                        bottom: { style: 'thin' },
                        right: { style: 'thin' }
                    };
                });
                
                // 集計行のフォーマット設定
                worksheet.getCell(`E${currentRow}`).numFmt = '0';       // 整数形式
                worksheet.getCell(`F${currentRow}`).numFmt = '#,##0.00'; // 小数点2桁
                
                currentRow++;
                
                // テーブル範囲（ヘッダー行から合計行まで含む）
                const tableEndRow = currentRow - 1;
                const tableRef = `A${tableStartRow}:F${tableEndRow}`;
                
                // テーブルを追加（シンプルなスタイルを使用）
                try {
                    const tableName = `PouchesTable${tableCount}`;
                    
                    // カスタムテーブルスタイルを設定する (自前でスタイリング)
                    // テーブルの定義では最低限のスタイル設定のみ行い、セルスタイルは直接設定
                    worksheet.addTable({
                        name: tableName,
                        ref: tableRef,
                        headerRow: true,
                        totalsRow: true,  // 集計行を有効化
                        // 余計な縞模様や不要なスタイルを避ける
                        style: {
                            theme: 'TableStyleMedium2', // 標準テーブルスタイル
                            showRowStripes: true,      // 行の縞模様あり
                            showColumnStripes: false   // 列の縞模様なし
                        },
                        columns: [
                            { name: excelColumns[0], totalsRowFunction: 'none', totalsRowLabel: '' },
                            { name: excelColumns[1], totalsRowFunction: 'none', totalsRowLabel: appCopy.table.excel.totalRow },
                            { name: excelColumns[2], totalsRowFunction: 'none' },
                            { name: excelColumns[3], totalsRowFunction: 'none' },
                            { name: excelColumns[4], totalsRowFunction: 'custom', totalsRowFormula: `SUMIF(D${firstDataRow}:D${lastDataRow},"×",E${firstDataRow}:E${lastDataRow})+COUNTIF(D${firstDataRow}:D${lastDataRow},"/")` },
                            { name: excelColumns[5], totalsRowFunction: 'sum' }   // 合計関数
                        ],
                        rows: tableRows
                    });
                    
                    // テーブル内の各行に手動でスタイルを適用
                    for (let i = tableStartRow + 1; i < tableEndRow; i++) {
                        // ヘッダーと合計行以外の通常行
                        if (i !== tableStartRow && i !== tableEndRow) {
                            // 各行の全セルを取得して処理
                            for (let j = 1; j <= 6; j++) {
                                const cell = worksheet.getCell(i, j);
                                // 白背景を確実に設定
                                cell.fill = {
                                    type: 'pattern',
                                    pattern: 'solid',
                                    fgColor: { argb: 'FFFFFFFF' } // 白
                                };
                            }
                        }
                    }
                    
                    setTableCount(prevCount => prevCount + 1);
                } catch (e) {
                    console.warn('テーブル作成エラー:', e);
                }
                
                // グループ間の空行
                worksheet.addRow([]);
                currentRow++;
            } else {
                // 折りたたまれたグループは合計行のみ表示
                const groupName = group.id === 'ungrouped'
                    ? appCopy.table.ungrouped
                    : (group.groupTitle || appCopy.table.groupN(groupData.indexOf(group) + 1));
                const totalRowSimple = worksheet.addRow([
                    groupName,
                    appCopy.table.excel.totalRow,
                    '',
                    '',
                    group.count,
                    group.total
                ]);
                
                // スタイル設定
                totalRowSimple.font = { bold: true, color: { argb: 'FF000000' } }; // 黒色の太字
                worksheet.getCell(`F${currentRow}`).numFmt = '#,##0.00';
                
                // 枠線を設定
                totalRowSimple.eachCell((cell: any) => {
                    cell.border = {
                        top: { style: 'thin' },
                        left: { style: 'thin' },
                        bottom: { style: 'thin' },
                        right: { style: 'thin' }
                    };
                });
                
                currentRow++;
                
                // 空行を追加
                worksheet.addRow([]);
                currentRow++;
            }
        }
        
        // ファイル名を現在のシート名に設定（デフォルト名も準備）
        const fileName = currentSheet?.title ? 
            `${currentSheet.title.replace(/[\\/:*?"<>|]/g, '_')}.xlsx` : // ファイル名に使えない文字を置換
            appCopy.table.excel.filename; // デフォルト名
        
        // エクスポート
        workbook.xlsx.writeBuffer().then(async (buffer) => {
            const blob = new Blob([buffer], { type: 'application/octet-stream' });
            await shareOrDownloadBlob(blob, fileName);
        });
    };

    // 貼り付けイベントをハンドリング
    useEffect(() => {
        const handlePaste = (e: ClipboardEvent) => {
            // テーブル要素内で貼り付けが発生した場合のみ処理
            if (tableRef.current && 
                e.target && 
                (tableRef.current === e.target || tableRef.current.contains(e.target as Node))) {
                
                const hasSelectedCells = Object.keys(selectedCells).length > 0;
                
                if (hasSelectedCells) {
                    e.preventDefault(); // デフォルトの貼り付け動作を防止
                    
                    // クリップボードからテキストを取得
                    const clipboardText = e.clipboardData?.getData('text/plain') || '';
                    if (clipboardText) {
                        handlePasteData(clipboardText);
                    }
                }
            }
        };
        
        // イベントリスナーを追加
        document.addEventListener('paste', handlePaste);
        
        // クリーンアップ
        return () => {
            document.removeEventListener('paste', handlePaste);
        };
    }, [selectedCells]);

    // コピーイベントをハンドリング
    useEffect(() => {
        const handleCopy = (e: ClipboardEvent) => {
            // テーブル要素内でコピーが発生した場合のみ処理
            if (tableRef.current && 
                e.target && 
                (tableRef.current === e.target || tableRef.current.contains(e.target as Node))) {
                
                const hasSelectedCells = Object.keys(selectedCells).length > 0;
                
                if (hasSelectedCells) {
                    // 選択範囲がある場合は、選択セルをHTML形式でコピー
                    e.preventDefault(); // デフォルトのコピー動作を防止
                    
                    // 1列目のセルの色情報を保存
                    const colorMap: {[rowIndex: number]: {[cellIndex: number]: string}} = {};
                    Object.keys(selectedCells).forEach(rowIndexStr => {
                        const rowIndex = parseInt(rowIndexStr);
                        const cellIndices = Object.keys(selectedCells[rowIndex]);
                        
                        cellIndices.forEach(cellIndexStr => {
                            const cellIndex = parseInt(cellIndexStr);
                            
                            // 1列目のみ色情報を保存
                            if (cellIndex === 0) {
                                // tbodyの行のみ（theadは除外）
                                if (rowIndex % 1000 !== 0) {
                                    // rowIndexから対応するPouchを見つける
                                    const groupIndex = Math.floor(rowIndex / 1000);
                                    const pouchIndexInGroup = (rowIndex % 1000) - 1;
                                    
                                    if (groupData[groupIndex] && groupData[groupIndex].rows[pouchIndexInGroup]) {
                                        const row = groupData[groupIndex].rows[pouchIndexInGroup];
                                        if (row.color) {
                                            if (!colorMap[rowIndex]) colorMap[rowIndex] = {};
                                            colorMap[rowIndex][cellIndex] = row.color;
                                        }
                                    }
                                }
                            }
                        });
                    });
                    setCopiedCellColors(colorMap);
                    
                    // 選択セルからHTMLを生成
                    const htmlContent = getSelectedCellsHTML();
                    const plainText = getSelectedCellsText();
                    
                    // コピーしたテキストデータを保存（色情報との照合用）
                    setCopiedTextData(plainText);
                    
                    // フォント指定を完全に排除したHTML
                    const fullHtmlData = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>
table, th, td {
  border: 1px solid black;
  border-collapse: collapse;
}
</style>
</head>
<body>
${htmlContent}
</body>
</html>`;
                    
                    try {
                        e.clipboardData?.setData('text/html', fullHtmlData);
                        e.clipboardData?.setData('text/plain', plainText);
                    } catch (err) {
                        console.error('クリップボードへのHTML設定に失敗しました', err);
                        // フォールバック: プレーンテキストのみ設定
                        e.clipboardData?.setData('text/plain', plainText);
                    }
                    return;
                } else if (window.getSelection && window.getSelection()?.toString().trim()) {
                    // 通常の選択範囲がある場合は、選択範囲をHTML形式でコピー
                    const selection = window.getSelection();
                    e.preventDefault(); // デフォルトのコピー動作を防止
                    
                    // HTMLフォーマットでクリップボードに設定
                    const htmlContent = getHTMLFromSelection();
                    const plainText = selection?.toString() || '';
                    
                    // HTMLデータを設定
                    const fullHtmlData = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>
table, th, td {
  border: 1px solid black;
  border-collapse: collapse;
}
</style>
</head>
<body>
${htmlContent}
</body>
</html>`;
                     try {
                         e.clipboardData?.setData('text/html', fullHtmlData);
                         e.clipboardData?.setData('text/plain', plainText);
                     } catch (err) {
                         console.error('クリップボードへのHTML設定に失敗しました', err);
                         // フォールバック: プレーンテキストのみ設定
                         e.clipboardData?.setData('text/plain', plainText);
                     }
                 }
             }
         };
         
         // イベントリスナーを追加
         document.addEventListener('copy', handleCopy);
         
         // クリーンアップ
         return () => {
             document.removeEventListener('copy', handleCopy);
         };
     }, [groupData, expandedGroups, selectedCells, setCopiedCellColors, setCopiedTextData]);

    // Shiftキーの状態を追跡
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Shift') {
                setIsShiftKeyPressed(true);
            }
        };
        
        const handleKeyUp = (e: KeyboardEvent) => {
            if (e.key === 'Shift') {
                setIsShiftKeyPressed(false);
            }
        };
        
        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('keyup', handleKeyUp);
        
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('keyup', handleKeyUp);
        };
    }, []);

    // セルフォーカス時の処理を関数化
    const handleCellFocus = useCallback((rowIndex: number, cellIndex: number) => {
        onClearCanvasHover?.();
        const layerId = layerIdForCellIndex(cellIndex);
        if (layerId && layerId !== activeLayerId) {
            onSetActiveLayer(layerId);
        }
        setTimeout(() => {
            if (!isShiftKeyPressed) {
                setSelectionStart({row: rowIndex, cell: cellIndex});
                setSelectionEnd({row: rowIndex, cell: cellIndex});
                setSelectedCells({
                    [rowIndex]: {
                        [cellIndex]: true
                    }
                });
            }
        }, 0);
    }, [isShiftKeyPressed, layerIdForCellIndex, activeLayerId, onSetActiveLayer, onClearCanvasHover]);

    // 数式記録時と現在の値・マルチプライヤーが異なるかどうかを判定する関数
    // パウチ値編集開始時の表示値を決定する関数（layerId は編集対象列のレイヤー）
    const getPouchValueEditInitialValue = useCallback((pouch: Pouch, layerId?: string) => {
        if (pouch.isGroupTotalPouch) {
            const isDivision = pouch.multiplier < 0;
            const absMultiplier = Math.abs(pouch.multiplier);
            return isDivision ? `/${absMultiplier}` : `*${absMultiplier}`;
        }
        return getPouchEditInitialString(pouch, currentSheet ?? undefined, layerId);
    }, [currentSheet]);

    const resolvePouchForTableRow = useCallback(
        (rowIndex: number): Pouch | undefined => {
            if (isLayerHeaderRow(rowIndex)) return undefined;
            const groupIdx = Math.floor(rowIndex / 1000);
            const rowIdxInGroup = (rowIndex % 1000) - 1;
            const group = groupData[groupIdx];
            const row = group?.rows?.[rowIdxInGroup];
            if (!row?.pouchId) return undefined;
            return pouches.find(p => p.id === row.pouchId);
        },
        [groupData, pouches]
    );

    // 編集開始ハンドラー
    const handleEditStart = useCallback(async (rowIndex: number, cellIndex: number, initialValue: string, pouch?: Pouch, shouldSelectText: boolean = true) => {
        const layerId = layerIdForCellIndex(cellIndex);
        const isValueCell =
            cellIndex === 1 ||
            (sheetLayers.length > 1 &&
                layerId != null &&
                cellIndex >= cols.layerStart &&
                cellIndex < cols.layerStart + sheetLayers.length);
        const isMultOpCell = cellIndex === (sheetLayers.length > 1 ? cols.mult : 2);
        const isMultValCell = cellIndex === multValCell;

        // BP パウチの値・符号・マルチ値セルは編集禁止
        if (currentSheet?.balancingPouchId && (isValueCell || isMultOpCell || isMultValCell)) {
            const targetPouch = pouch ?? resolvePouchForTableRow(rowIndex);
            if (targetPouch?.id === currentSheet.balancingPouchId) {
                return;
            }
        }
        if (layerId && layerId !== activeLayerId) {
            await Promise.resolve(onSetActiveLayer(layerId));
        }
        setEditingCell({row: rowIndex, cell: cellIndex});
        
        let finalInitialValue = initialValue;
        if (isLayerHeaderRow(rowIndex)) {
            const li = cellIndex - cols.layerStart;
            if (li >= 0 && li < sheetLayers.length) {
                finalInitialValue = sheetLayers[li].name || 'Value';
            }
        } else if (isValueCell) {
            const targetPouch = pouch ?? resolvePouchForTableRow(rowIndex);
            if (targetPouch) {
                finalInitialValue = getPouchValueEditInitialValue(
                    targetPouch,
                    layerId ?? activeLayerId
                );
            }
        }
        
        setEditValue(finalInitialValue);
        
        // テキスト選択が必要な場合のみ実行（Enterキーで編集開始した場合など）
        if (shouldSelectText) {
            setTimeout(() => {
                if (editInputRef.current) {
                    editInputRef.current.select();
                }
            }, 0);
        }
    }, [
        getPouchValueEditInitialValue,
        sheetLayers,
        cols.layerStart,
        cols.mult,
        activeLayerId,
        currentSheet?.balancingPouchId,
        layerIdForCellIndex,
        multValCell,
        onSetActiveLayer,
        resolvePouchForTableRow,
    ]);

    const startLayerHeaderEdit = useCallback(
        async (layerIndex: number) => {
            const layer = sheetLayers[layerIndex];
            if (!layer) return;
            const cellIndex = cols.layerStart + layerIndex;
            await handleEditStart(
                LAYER_HEADER_ROW_INDEX,
                cellIndex,
                layer.name ?? '',
                undefined,
                true
            );
            const tryFocusInput = (attempt = 0) => {
                if (editInputRef.current) {
                    editInputRef.current.focus();
                    editInputRef.current.select();
                    return;
                }
                if (attempt < 12) {
                    requestAnimationFrame(() => tryFocusInput(attempt + 1));
                }
            };
            tryFocusInput();
        },
        [sheetLayers, cols.layerStart, handleEditStart]
    );

    const handleAddLayerClick = useCallback(() => {
        pendingNewLayerHeaderEditRef.current = true;
        void onAddSheetLayer();
    }, [onAddSheetLayer]);

    useEffect(() => {
        if (!pendingNewLayerHeaderEditRef.current || sheetLayers.length === 0) return;
        const layerIndex = sheetLayers.length - 1;
        const newLayer = sheetLayers[layerIndex];
        if (!newLayer) return;
        const activeId = currentSheet ? resolveActiveLayerId(currentSheet) : null;
        if (newLayer.id !== activeId) return;
        pendingNewLayerHeaderEditRef.current = false;
        void startLayerHeaderEdit(layerIndex);
    }, [sheetLayers, currentSheet?.activeLayerId, currentSheet, startLayerHeaderEdit]);

    // 下のセルに移動する共通関数（ArrowDownキーと同じロジック）
    const moveToNextRowCell = useCallback((currentRowIndex: number, currentCellIndex: number) => {
        // handleCellKeyDownのArrowDownキーの通常移動と同じロジック
        const getCurrentCell = (row: number, cell: number): HTMLElement | null => {
            return document.querySelector(`tr[data-row-index="${row}"] [data-cell-index="${cell}"]`) as HTMLElement;
        };

        const moveToCell = (newRowIndex: number, newCellIndex: number) => {
            const newCell = getCurrentCell(newRowIndex, newCellIndex);
            if (newCell) {
                newCell.focus();
                newCell.scrollIntoView({ block: 'nearest', inline: 'nearest' });
                setSelectionStart({row: newRowIndex, cell: newCellIndex});
                setSelectionEnd({row: newRowIndex, cell: newCellIndex});
                setSelectedCells({
                    [newRowIndex]: {
                        [newCellIndex]: true
                    }
                });
            }
        };

        const nextRowIndex = currentRowIndex + 1;
        const nextCell = getCurrentCell(nextRowIndex, currentCellIndex);
        const isTbodyRow = currentRowIndex % 1000 !== 0;
        const isTheadCell = currentRowIndex % 1000 === 0;
        
        if (nextCell) {
            // 通常の移動
            moveToCell(nextRowIndex, currentCellIndex);
        } else {
            // tbodyの最後の行から次のtheadに移動
            if (isTbodyRow) {
                // 次のグループのtheadを探す
                const currentGroupIndex = Math.floor(currentRowIndex / 1000);
                const nextGroupIndex = currentGroupIndex + 1;
                const nextGroupRowIndex = nextGroupIndex * 1000;
                
                // 次のグループが存在するかチェック
                const nextGroupCell = getCurrentCell(nextGroupRowIndex, currentCellIndex);
                if (nextGroupCell) {
                    moveToCell(nextGroupRowIndex, currentCellIndex);
                }
                // 最後の行の場合は何もしない（既に最初のif文でチェック済み）
            } else if (isTheadCell) {
                // theadから次のtbodyの最初の行に移動
                const currentGroupIndex = Math.floor(currentRowIndex / 1000);
                const nextGroupIndex = currentGroupIndex + 1;
                const nextGroupFirstRowIndex = nextGroupIndex * 1000 + 1;
                
                // 次のグループのtbodyの最初の行が存在するかチェック
                const nextGroupFirstCell = getCurrentCell(nextGroupFirstRowIndex, currentCellIndex);
                if (nextGroupFirstCell) {
                    moveToCell(nextGroupFirstRowIndex, currentCellIndex);
                } else {
                    // 次のグループのtheadに移動
                    const nextGroupTheadRowIndex = nextGroupIndex * 1000;
                    const nextGroupTheadCell = getCurrentCell(nextGroupTheadRowIndex, currentCellIndex);
                    if (nextGroupTheadCell) {
                        moveToCell(nextGroupTheadRowIndex, currentCellIndex);
                    }
                    // 最後のグループの場合は何もしない（既に最初のif文でチェック済み）
                }
            }
        }
    }, []);

    // 編集完了ハンドラー
    const handleEditComplete = useCallback(() => {
        if (!editingCell) return;
        
        const {row: rowIndex, cell: cellIndex} = editingCell;
        
        try {
            if (isLayerHeaderRow(rowIndex)) {
                const li = cellIndex - cols.layerStart;
                if (li >= 0 && li < sheetLayers.length && onRenameSheetLayer) {
                    onRenameSheetLayer(sheetLayers[li].id, editValue.trim());
                }
            } else if (cellIndex === 0) {
            // グループタイトルまたはパウチタイトルの編集
            if (rowIndex % 1000 === 0) {
                // グループタイトルの編集
                const groupIndex = Math.floor(rowIndex / 1000);
                const group = groupData[groupIndex];
                if (group) {
                    onUpdateGroupTitle(group.id, editValue);
                }
            } else {
                // パウチタイトルの編集
                const groupIndex = Math.floor(rowIndex / 1000);
                const rowIndexInGroup = (rowIndex % 1000) - 1;
                const group = groupData[groupIndex];
                if (group && group.rows && group.rows[rowIndexInGroup]) {
                    const row = group.rows[rowIndexInGroup];
                    if (row.pouchId) {
                        onUpdatePouchTitle(row.pouchId, editValue);
                    }
                }
            }
        } else if (
            !isLayerHeaderRow(rowIndex) &&
            (cellIndex === 1 ||
            (sheetLayers.length > 1 &&
                cellIndex >= cols.layerStart &&
                cellIndex < cols.layerStart + sheetLayers.length))
        ) {
            // パウチ値の編集
            const groupIndex = Math.floor(rowIndex / 1000);
            const rowIndexInGroup = (rowIndex % 1000) - 1;
            const group = groupData[groupIndex];
            const editLayerId =
                sheetLayers.length > 1 && cellIndex >= cols.layerStart
                    ? sheetLayers[cellIndex - cols.layerStart]?.id
                    : activeLayerId;
            if (group && group.rows && group.rows[rowIndexInGroup]) {
                const row = group.rows[rowIndexInGroup];
                if (row.pouchId) {
                    const pouch = pouches.find(p => p.id === row.pouchId);
                    if (pouch) {
                        const baseValue =
                            sheetLayers.length > 1 && editLayerId
                                ? (row.layerValues?.[editLayerId] ?? 0)
                                : pouch.value;
                        // 空値チェック：空文字列や空白のみの場合はエラー
                        if (!editValue || editValue.trim() === '') {
                            console.warn('空値で確定しようとしました。エラー状態を設定します。');
                            return;
                        }
                        
                        // PouchComponentの数式処理を使用
                        const allowZeroMultiplier = !!(pouch.isGroupTotalPouch && !pouch.isTime);
                        const result = handleUnifiedExpression(
                            editValue,
                            baseValue,
                            pouch.multiplier,
                            !!pouch.isTime,
                            allowZeroMultiplier,
                        );
                        
                        // エラー処理：計算に失敗した場合はエラー状態を設定（値は変更しない）
                        if (result === null || (result && result.hasError)) {
                            console.warn('計算に失敗しました。エラー状態を設定します。値は元のまま保持されます。');
                            
                            // エラー表示
                            if (onSetPouchError && row.pouchId) {
                                onSetPouchError(row.pouchId, true);
                            }
                        } else {
                        // 結果がある場合は1回だけ更新
                        if (result.result && result.result.value !== undefined && result.result.multiplier !== undefined) {
                            // GTPの場合は通常のパウチに変換
                            if (pouch.isGroupTotalPouch) {
                                // GTPを通常のパウチに変換（値とマルチプライヤーのみ更新）
                                onUpdatePouchValue(row.pouchId, result.result.value, result.result.multiplier, HistoryActionType.EDIT_POUCH_VALUE, true);
                            } else {
                                // lastExpressionの保存判定（PouchComponentと同じロジック）
                                let lastExpressionToSave: string | undefined;
                                let valueWhenLastExpression: number | undefined;
                                let multiplyWhenLastExpression: number | undefined;
                                let roundUnitWhenLastExpression: number | undefined;
                                
                                if (result.lastExpression && result.lastExpression !== undefined) {
                                    // 単純な数値の場合は空文字列を記録、それ以外は数式を記録
                                    const isSimpleNumber = /^-?\d+(?:\.\d+)?$/.test(result.lastExpression);
                                    if (isSimpleNumber) {
                                        // 単純な数値の場合は空文字列を記録
                                        lastExpressionToSave = "";
                                        valueWhenLastExpression = undefined;
                                        multiplyWhenLastExpression = undefined;
                                        roundUnitWhenLastExpression = undefined;
                                    } else {
                                        // 演算子が含まれている場合は数式を記録
                                        lastExpressionToSave = result.lastExpression;
                                        valueWhenLastExpression = result.result.value;
                                        multiplyWhenLastExpression = result.result.multiplier;
                                        roundUnitWhenLastExpression = result.result.roundUnit;
                                    }
                                } else {
                                    lastExpressionToSave = undefined;
                                    valueWhenLastExpression = undefined;
                                    multiplyWhenLastExpression = undefined;
                                    roundUnitWhenLastExpression = undefined;
                                }
                                
                                const finalValue = result.result.roundUnitOnly ? baseValue : result.result.value;
                                const finalMultiplier = result.result.roundUnitOnly ? pouch.multiplier : result.result.multiplier;
                                
                                // 値とマルチプライヤーを更新（isNegativeも自動切り替え）
                                onUpdatePouchValue(
                                    row.pouchId,
                                    finalValue,
                                    finalMultiplier,
                                    HistoryActionType.EDIT_POUCH_VALUE,
                                    !result.result.roundUnitOnly,
                                    lastExpressionToSave,
                                    valueWhenLastExpression,
                                    multiplyWhenLastExpression,
                                    result.result?.isTime,
                                    result.result.roundUnit,
                                    roundUnitWhenLastExpression,
                                    editLayerId
                                );
                            }
                        }
                        }
                    }
                }
            }
        } else if (cellIndex === multValCell) {
            // マルチ値の編集
            const groupIndex = Math.floor(rowIndex / 1000);
            const rowIndexInGroup = (rowIndex % 1000) - 1;
            const group = groupData[groupIndex];
            if (group && group.rows && group.rows[rowIndexInGroup]) {
                const row = group.rows[rowIndexInGroup];
                if (row.pouchId) {
                    const pouch = pouches.find(p => p.id === row.pouchId);
                    if (!pouch) {
                        return;
                    }
                    const baseValue =
                        sheetLayers.length > 0
                            ? getLayerValue(pouch, activeLayerId)
                            : pouch.value;

                    // ~ や *2~10 等は Pouch と同じ数式経路
                    if (/~/.test(editValue) || /^[\*\/]/.test(editValue.trim())) {
                        let expr = editValue.trim();
                        if (!/^[\*\/~]/.test(expr) && /^\d/.test(expr) && expr.includes('~')) {
                            const m = expr.match(/^(\d+(?:\.\d+)?)(~.+)$/);
                            if (m) {
                                expr = `*${m[1]}${m[2]}`;
                            }
                        }
                        const allowZeroMultiplier = !!(pouch.isGroupTotalPouch && !pouch.isTime);
                        const result = handleUnifiedExpression(
                            expr,
                            baseValue,
                            pouch.multiplier,
                            !!pouch.isTime,
                            allowZeroMultiplier,
                        );
                        if (result === null || result.hasError || !result.result) {
                            if (onSetPouchError && row.pouchId) {
                                onSetPouchError(row.pouchId, true);
                            }
                            return;
                        }
                        const finalValue = result.result.roundUnitOnly ? baseValue : result.result.value;
                        const finalMultiplier = result.result.roundUnitOnly
                            ? pouch.multiplier
                            : result.result.multiplier;
                        onUpdatePouchValue(
                            row.pouchId,
                            finalValue,
                            finalMultiplier,
                            HistoryActionType.EDIT_POUCH_VALUE,
                            !result.result.roundUnitOnly,
                            undefined,
                            undefined,
                            undefined,
                            result.result.isTime,
                            result.result.clearRoundUnit
                                ? null
                                : result.result.roundUnit,
                            result.result.roundUnit,
                            activeLayerId
                        );
                        return;
                    }

                    let newMultiplier: number;
                    
                    // パーセンテージ処理
                    if (editValue.includes('%')) {
                        if (editValue.startsWith('+')) {
                            // +n% → 1 + (n/100)
                            const percentage = parseFloat(editValue.slice(1, -1)) || 0;
                            newMultiplier = 1 + (percentage / 100);
                        } else if (editValue.startsWith('-')) {
                            // -n% → 1 - (n/100)
                            const percentage = parseFloat(editValue.slice(1, -1)) || 0;
                            newMultiplier = 1 - (percentage / 100);
                        } else {
                            // n% → n/100
                            const percentage = parseFloat(editValue.slice(0, -1)) || 0;
                            newMultiplier = percentage / 100;
                        }
                    } else {
                        // 通常の数値
                        newMultiplier = parseFloat(editValue) || 1;
                    }
                    
                    // 元の符号を保持（負の場合は割り算、正の場合は掛け算）
                    const originalMultiplier = pouches.find(p => p.id === row.pouchId)?.multiplier || 1;
                    let finalMultiplier: number;
                    let shouldFlipPouchValue = false;
                    // 負値が入力された場合は、マルチ値は絶対値で保存し、パウチ値を正負転換
                    if (newMultiplier < 0) {
                        finalMultiplier = Math.abs(newMultiplier);
                        shouldFlipPouchValue = true;
                    } else {
                        finalMultiplier = originalMultiplier < 0 ? -Math.abs(newMultiplier) : Math.abs(newMultiplier);
                    }
                    
                    onUpdatePouchMultiplier(row.pouchId, finalMultiplier);
                    
                    // 負値入力時はパウチ値を正負転換
                    if (shouldFlipPouchValue) {
                        const pouch = pouches.find(p => p.id === row.pouchId);
                        if (pouch) {
                            const current =
                              sheetLayers.length > 0
                                ? getLayerValue(pouch, activeLayerId)
                                : pouch.value;
                            const newPouchValue = -current;
                            onUpdatePouchValue(
                              row.pouchId,
                              newPouchValue,
                              finalMultiplier,
                              HistoryActionType.EDIT_POUCH_VALUE,
                              true,
                              undefined,
                              newPouchValue,
                              finalMultiplier,
                              undefined,
                              undefined,
                              undefined,
                              activeLayerId
                            );
                        }
                    }
                }
            }
        }
        
        // ソート順序は保持されるため、値の更新は自動的に反映される
        } catch (error) {
            console.error('編集完了処理でエラーが発生しました:', error);
        } finally {
            // 必ず編集中フラグを解除
            setEditingCell(null);
            setEditValue('');
        }
    }, [editingCell, editValue, filteredGroups, groupData, onUpdateGroupTitle, onUpdatePouchTitle, onUpdatePouchValue, onRenameSheetLayer, sheetLayers, cols, activeLayerId, multValCell]);

    // 編集キャンセルハンドラー
    const handleEditCancel = useCallback(() => {
        const currentEditingCell = editingCell;
        
        try {
            // キャンセル後に元のセルにフォーカスを戻す
            if (currentEditingCell) {
                setTimeout(() => {
                    const targetCell = document.querySelector(`tr[data-row-index="${currentEditingCell.row}"] [data-cell-index="${currentEditingCell.cell}"]`) as HTMLElement;
                    if (targetCell) {
                        targetCell.focus();
                        setSelectionStart({row: currentEditingCell.row, cell: currentEditingCell.cell});
                        setSelectionEnd({row: currentEditingCell.row, cell: currentEditingCell.cell});
                    }
                }, 0);
            }
        } catch (error) {
            console.error('編集キャンセル処理でエラーが発生しました:', error);
        } finally {
            // 必ず編集中フラグを解除
            setEditingCell(null);
            setEditValue('');
        }
    }, [editingCell]);


    // ダブルクリック・ダブルタッチハンドラー
    const handleDoubleClick = useCallback((e: React.MouseEvent, rowIndex: number, cellIndex: number, currentValue: string, pouch?: Pouch) => {
        ignoreCellClickAfterDoubleRef.current = true;
        e.preventDefault();
        e.stopPropagation();
        handleEditStart(rowIndex, cellIndex, currentValue, pouch);
    }, [handleEditStart]);

    const handleDoubleTouch = useCallback((e: React.TouchEvent, rowIndex: number, cellIndex: number, currentValue: string, pouch?: Pouch) => {
        ignoreCellClickAfterDoubleRef.current = true;
        e.preventDefault();
        e.stopPropagation();
        handleEditStart(rowIndex, cellIndex, currentValue, pouch);
    }, [handleEditStart]);

    // セル移動のヘルパー関数（編集状態で移動）
    const moveToCell = useCallback((rowIndex: number, cellIndex: number, initialValue: string = '') => {
        // 現在のセルの編集を完了
        if (editingCell) {
            handleEditComplete();
        }
        
        // 新しいセルで編集開始
        setTimeout(() => {
            handleEditStart(rowIndex, cellIndex, initialValue);
        }, 0);
    }, [editingCell, handleEditComplete, handleEditStart]);

    // セル移動のヘルパー関数（編集状態にしない）
    const moveToCellWithoutEdit = useCallback((rowIndex: number, cellIndex: number) => {
        // 現在のセルの編集を完了
        if (editingCell) {
            handleEditComplete();
        }
        
        // 既存のhandleCellKeyDownと同じロジックを使用
        setTimeout(() => {
            const getCurrentCell = (row: number, cell: number): HTMLElement | null => {
                return document.querySelector(`tr[data-row-index="${row}"] [data-cell-index="${cell}"]`) as HTMLElement;
            };

            const newCell = getCurrentCell(rowIndex, cellIndex);
            if (newCell) {
                newCell.focus();
                setSelectionStart({row: rowIndex, cell: cellIndex});
                setSelectionEnd({row: rowIndex, cell: cellIndex});
                setSelectedCells({
                    [rowIndex]: {
                        [cellIndex]: true
                    }
                });
                
                // フォーカスが確実に設定されるように、少し遅延して再フォーカス
                setTimeout(() => {
                    if (document.activeElement !== newCell) {
                        newCell.focus();
                    }
                }, 10);
            }
        }, 0);
    }, [editingCell, handleEditComplete]);

    type CellSelectionMap = { [rowIndex: number]: { [cellIndex: number]: boolean } };

    const getEffectiveSelection = useCallback((): CellSelectionMap => {
        const fromState = selectedCellsRef.current;
        if (Object.keys(fromState).length > 0) {
            return fromState;
        }
        const active = document.activeElement;
        if (!(active instanceof HTMLElement)) {
            return fromState;
        }
        const cell = active.closest(
            '.pouch-table td[data-cell-index], .pouch-table th[data-cell-index]'
        );
        if (!cell) {
            return fromState;
        }
        const tr = cell.closest('tr[data-row-index]');
        if (!tr) {
            return fromState;
        }
        const row = parseInt(tr.getAttribute('data-row-index') || '-1', 10);
        const col = parseInt(cell.getAttribute('data-cell-index') || '-1', 10);
        if (!isLayerHeaderRow(row) && row < 0) {
            return fromState;
        }
        if (col < 0) {
            return fromState;
        }
        return { [row]: { [col]: true } };
    }, []);

    // 選択範囲内の特定のセルタイプを取得する関数
    const getSelectedCellsOfType = useCallback((
        cellType: 'groupTitle' | 'pouchTitle' | 'pouchValue' | 'multiplier' | 'multiplierSign' | 'multiplierValue',
        selection: CellSelectionMap = selectedCellsRef.current
    ) => {
        const selectedCellsOfType: Array<{
            rowIndex: number;
            cellIndex: number;
            groupId?: string;
            pouchId?: string;
            layerId?: string;
        }> = [];
        
        Object.entries(selection).forEach(([rowIndexStr, cells]) => {
            const rowIndex = parseInt(rowIndexStr);
            Object.keys(cells).forEach(cellIndexStr => {
                const cellIndex = parseInt(cellIndexStr);
                
                // セルタイプの判定
                const isGroupTitle = rowIndex % 1000 === 0 && cellIndex === 0;
                const isPouchTitle = rowIndex % 1000 !== 0 && cellIndex === 0;
                const isPouchValue =
                    rowIndex % 1000 !== 0 &&
                    (cellIndex === 1 ||
                        (sheetLayers.length > 1 &&
                            cellIndex >= cols.layerStart &&
                            cellIndex < cols.layerStart + sheetLayers.length));
                const isMultiplierSign = rowIndex % 1000 !== 0 && cellIndex === multOpCell;
                const isMultiplierValue = rowIndex % 1000 !== 0 && cellIndex === multValCell;
                const isMultiplier = isMultiplierValue;
                
                let matchesType = false;
                switch (cellType) {
                    case 'groupTitle':
                        matchesType = isGroupTitle;
                        break;
                    case 'pouchTitle':
                        matchesType = isPouchTitle;
                        break;
                    case 'pouchValue':
                        matchesType = isPouchValue;
                        break;
                    case 'multiplierSign':
                        matchesType = isMultiplierSign;
                        break;
                    case 'multiplierValue':
                        matchesType = isMultiplierValue;
                        break;
                    case 'multiplier':
                        matchesType = isMultiplier;
                        break;
                }
                
                if (matchesType) {
                    const groupIndex = Math.floor(rowIndex / 1000);
                    const group = groupData[groupIndex];
                    
                    if (group) {
                        const cellInfo: {
                            rowIndex: number;
                            cellIndex: number;
                            groupId: string;
                            pouchId?: string;
                            layerId?: string;
                        } = { rowIndex, cellIndex, groupId: group.id };

                        if (isPouchTitle || isPouchValue || isMultiplierSign || isMultiplierValue || isMultiplier) {
                            const rowIndexInGroup = (rowIndex % 1000) - 1;
                            const row = group.rows[rowIndexInGroup];
                            if (row?.pouchId) {
                                cellInfo.pouchId = row.pouchId;
                            }
                            if (isPouchValue && currentSheet) {
                                if (
                                    sheetLayers.length > 1 &&
                                    cellIndex >= cols.layerStart &&
                                    cellIndex < cols.layerStart + sheetLayers.length
                                ) {
                                    cellInfo.layerId = sheetLayers[cellIndex - cols.layerStart]?.id;
                                } else {
                                    cellInfo.layerId = activeLayerId;
                                }
                            }
                        }
                        
                        selectedCellsOfType.push(cellInfo);
                    }
                }
            });
        });
        
        return selectedCellsOfType;
    }, [groupData, sheetLayers, cols.layerStart, multOpCell, multValCell, currentSheet, activeLayerId]);

    /** 選択範囲に含まれるパウチ行の pouchId（重複なし）。グループヘッダー行のみの選択は空 */
    const getUniquePouchIdsFromSelection = useCallback((): string[] => {
        const ids = new Set<string>();
        Object.entries(selectedCells).forEach(([rowIndexStr, cells]) => {
            const rowIndex = parseInt(rowIndexStr, 10);
            if (rowIndex % 1000 === 0) {
                return;
            }
            if (!cells || Object.keys(cells).length === 0) {
                return;
            }
            const groupIndex = Math.floor(rowIndex / 1000);
            const rowInGroup = (rowIndex % 1000) - 1;
            const group = groupData[groupIndex];
            if (!group || rowInGroup < 0 || rowInGroup >= group.rows.length) {
                return;
            }
            const row = group.rows[rowInGroup];
            if (row?.pouchId) {
                ids.add(row.pouchId);
            }
        });
        return Array.from(ids);
    }, [selectedCells, groupData]);

    /** 選択範囲に含まれるグループヘッダー行（th）に対応するグループ id（重複なし、ungrouped は含めない） */
    const getUniqueGroupIdsFromHeaderSelection = useCallback((): string[] => {
        const ids = new Set<string>();
        Object.entries(selectedCells).forEach(([rowIndexStr, cells]) => {
            const rowIndex = parseInt(rowIndexStr, 10);
            if (rowIndex % 1000 !== 0) {
                return;
            }
            if (!cells || Object.keys(cells).length === 0) {
                return;
            }
            const groupIndex = Math.floor(rowIndex / 1000);
            const group = groupData[groupIndex];
            if (group?.id && group.id !== 'ungrouped') {
                ids.add(group.id);
            }
        });
        return Array.from(ids);
    }, [selectedCells, groupData]);

    /** テーブル色ガイドの「次／前」プレビュー用（計算エリアのパウチ／グループ表記と揃える代表色） */
    const tableGuideRefPouchColorId = useMemo(() => {
        const ids = getUniquePouchIdsFromSelection();
        if (ids.length === 0) {
            return POUCH_COLORS[0].id;
        }
        const p = pouches.find(po => po.id === ids[0]);
        if (!p?.color) {
            return POUCH_COLORS[0].id;
        }
        return POUCH_COLORS.some(c => c.id === p.color) ? p.color : POUCH_COLORS[0].id;
    }, [getUniquePouchIdsFromSelection, pouches]);

    const tableGuideRefGroupColorId = useMemo(() => {
        const gids = getUniqueGroupIdsFromHeaderSelection();
        if (gids.length === 0) {
            return GROUP_COLORS[0].id;
        }
        const g = groups.find(gr => gr.id === gids[0]);
        return g?.color || GROUP_COLORS[0].id;
    }, [getUniqueGroupIdsFromHeaderSelection, groups]);

    const getCellTooltip = (rowIdentifier: number, cellIndex: number) => {
        const selected = isCellSelected(rowIdentifier, cellIndex);
        const isGroupHeader = rowIdentifier % 1000 === 0;
        const columnLabels: Record<number, string> = isGroupHeader
            ? { 0: appCopy.table.header.groupTitle }
            : {
                0: appCopy.table.header.pouchTitle,
                1: appCopy.table.header.value,
                2: appCopy.table.header.opSign,
                3: appCopy.table.header.multiplier,
            };
        const label = columnLabels[cellIndex] || appCopy.table.header.cell;
        let html: string;
        if (cellIndex === 2) {
            html = selected
                ? formatTooltip(`Double Click / Space: ${doubleClickToggleColumn(label, locale)}`)
                : formatTooltip(`Double Click: ${doubleClickToggleColumn(label, locale)}`);
        } else {
            html = selected
                ? formatTooltip(`Double Click / Space: ${doubleClickEditColumn(label, locale)}\nDelete: ${deleteClearColumn(label, locale)}`)
                : formatTooltip(`Double Click: ${doubleClickEditColumn(label, locale)}`);
        }
        const gi = Math.floor(rowIdentifier / 1000);
        let pouchColorForGuide = tableGuideRefPouchColorId;
        if (rowIdentifier % 1000 !== 0) {
            const rig = (rowIdentifier % 1000) - 1;
            const gRow = groupData[gi];
            if (gRow && rig >= 0 && rig < gRow.rows.length) {
                const pid = gRow.rows[rig]?.pouchId;
                if (pid) {
                    const p = pouches.find(po => po.id === pid);
                    if (p?.color && POUCH_COLORS.some(c => c.id === p.color)) {
                        pouchColorForGuide = p.color;
                    }
                }
            }
        }
        let groupColorForGuide = tableGuideRefGroupColorId;
        const gMeta = groupData[gi];
        if (gMeta?.id) {
            const g = groups.find(gr => gr.id === gMeta.id);
            if (g?.color) {
                groupColorForGuide = g.color;
            }
        }
        // th: グループ色のみ（ヘッダー行の COUNT/合計は td）。td: パウチ色のみ（tbody および合計列）
        const isHoveredThCell = rowIdentifier % 1000 === 0 && cellIndex <= 2;
        const showGroupColorKeysForHover =
            tableGuideShowsGroupColorKeys && isHoveredThCell;
        const showPouchColorKeysForHover = tableGuideShowsPouchColorKeys && !isHoveredThCell;
        if (!isTouchDevice && selectedCells[rowIdentifier]?.[cellIndex] && _isGuideEnabled) {
            if (tableGuideHoverTier >= 2) {
                const dragLine = formatTooltip(`Drag: ${tooltipCopy.dragSelectCells}`);
                const tier2Parts: string[] = [];
                if (showGroupColorKeysForHover) {
                    tier2Parts.push(
                        formatTableGuideGroupColorKeysPcHtml(
                            getModifierKeys().shift,
                            groupColorForGuide,
                            { tableNoMute: true },
                        ),
                    );
                }
                if (showPouchColorKeysForHover) {
                    tier2Parts.push(
                        formatTableGuidePouchColorKeysPcHtml(
                            getModifierKeys().shift,
                            pouchColorForGuide,
                        ),
                    );
                }
                const tier2 = tier2Parts.join('<br/>');
                html = tier2 ? `${html}<br/>${dragLine}<br/>${tier2}` : `${html}<br/>${dragLine}`;
            } else if (tableGuideHoverTier >= 1) {
                html = `${html}<br/>${formatTooltip(`Drag: ${tooltipCopy.dragSelectCells}`)}`;
            }
        }
        const withoutBr = html.replace(/<br\s*\/?>/gi, '').trim();
        if (!withoutBr) {
            return undefined;
        }
        return html;
    };

    const tooltipPropsIfContent = (html: string | undefined) => {
        if (html == null || html === '') {
            return {};
        }
        if (!html.replace(/<br\s*\/?>/gi, '').trim()) {
            return {};
        }
        return { 'data-tooltip-id': 'tt' as const, 'data-tooltip-html': html };
    };

    const cellTooltipDataProps = (rowIdentifier: number, cellIndex: number) =>
        tooltipPropsIfContent(getCellTooltip(rowIdentifier, cellIndex));

    // PC: セル選択中にパウチ色ホットキー（複数行は一括）
    useEffect(() => {
        if (!isOpen || isTouchDevice) {
            return;
        }
        const onKeyDown = (e: KeyboardEvent) => {
            if (editingCell !== null) {
                return;
            }
            if (Object.keys(selectedCells).length === 0) {
                return;
            }
            if ((window as any).isIMEComposing) {
                return;
            }
            const active = document.activeElement;
            if (isEditableElement(active)) {
                return;
            }
            if (!active?.closest('.table-component')) {
                return;
            }
            const groupIdsFromHeader = getUniqueGroupIdsFromHeaderSelection();
            const pouchIds = getUniquePouchIdsFromSelection();
            const rawGroupAction = resolveGroupColorHotkey(e);
            if (rawGroupAction?.type === 'muteToggle' && groupIdsFromHeader.length > 0) {
                e.preventDefault();
                e.stopPropagation();
                return;
            }
            const gAction =
                rawGroupAction?.type === 'muteToggle' ? null : rawGroupAction;
            const pAction = resolvePouchColorHotkey(e);

            // th と td の両方にまたがる: グループとパウチをそれぞれ解決できるキーなら両方更新
            if (groupIdsFromHeader.length > 0 && pouchIds.length > 0) {
                if (!gAction && !pAction) {
                    return;
                }
                e.preventDefault();
                e.stopPropagation();
                let nextGroups = groups;
                let nextPouches = pouches;
                if (gAction) {
                    nextGroups = applyGroupColorHotkeyToGroups(groups, groupIdsFromHeader, gAction);
                }
                if (pAction) {
                    nextPouches = applyPouchColorHotkeyToPouches(pouches, pouchIds, pAction);
                }
                const historyType =
                    gAction && pAction
                        ? HistoryActionType.PASTE_BULK_UPDATE
                        : gAction
                          ? HistoryActionType.CHANGE_GROUP_COLOR
                          : HistoryActionType.CHANGE_POUCH_COLOR;
                onBulkUpdate({
                    completeGroups: nextGroups,
                    completePouches: nextPouches,
                    pasteHistoryType: historyType,
                } as any);
                return;
            }

            if (groupIdsFromHeader.length > 0) {
                if (!gAction) {
                    return;
                }
                e.preventDefault();
                e.stopPropagation();
                const updatedGroups = applyGroupColorHotkeyToGroups(groups, groupIdsFromHeader, gAction);
                onBulkUpdate({
                    completeGroups: updatedGroups,
                    completePouches: pouches,
                    pasteHistoryType: HistoryActionType.CHANGE_GROUP_COLOR,
                } as any);
                return;
            }

            if (!pAction) {
                return;
            }
            if (pouchIds.length === 0) {
                return;
            }
            e.preventDefault();
            e.stopPropagation();
            const nextPouches = applyPouchColorHotkeyToPouches(pouches, pouchIds, pAction);
            onBulkUpdate({
                completeGroups: groups,
                completePouches: nextPouches,
                pasteHistoryType: HistoryActionType.CHANGE_POUCH_COLOR,
            } as any);
        };
        window.addEventListener('keydown', onKeyDown, true);
        return () => window.removeEventListener('keydown', onKeyDown, true);
    }, [
        isOpen,
        isTouchDevice,
        editingCell,
        selectedCells,
        pouches,
        groups,
        getUniquePouchIdsFromSelection,
        getUniqueGroupIdsFromHeaderSelection,
        onBulkUpdate,
    ]);

    // 選択範囲内のセルを一括処理する関数
    const processSelectedCells = useCallback((isDeleteKey: boolean = true, selectionOverride?: CellSelectionMap) => {
        const selection = selectionOverride ?? getEffectiveSelection();
        if (Object.keys(selection).length === 0) {
            return;
        }

        // 全列選択の判定：行ごとに選択されているセルを確認
        const rowSelections: { [rowIndex: number]: Set<number> } = {};
        
        // 選択されたセルを行ごとにグループ化
        Object.keys(selection).forEach(rowKey => {
            const rowIndex = parseInt(rowKey);
            if (!rowSelections[rowIndex]) {
                rowSelections[rowIndex] = new Set();
            }
            Object.keys(selection[parseInt(rowKey)]).forEach(cellKey => {
                const cellIndex = parseInt(cellKey);
                rowSelections[rowIndex].add(cellIndex);
            });
        });
        
        // 削除対象のグループとパウチを特定
        const groupsToDelete = new Set<string>();
        const pouchesToDelete = new Set<string>();
        
        Object.keys(rowSelections).forEach(rowKey => {
            const rowIndex = parseInt(rowKey);
            const selectedCols = rowSelections[rowIndex];
            
            // グループヘッダー行（colspan 1 セルのみ）
            if (rowIndex % 1000 === 0) {
                if (selectedCols.has(0)) {
                    // 全列選択の場合
                    const groupIndex = Math.floor(rowIndex / 1000);
                    const group = groupData[groupIndex];
                    if (group && group.id !== 'ungrouped') {
                        if (isDeleteKey) {
                            // Deleteキー：グループを削除対象に追加
                            groupsToDelete.add(group.id);
                        } else {
                            // Backspaceキー：値クリアのみ（削除対象には追加しない）
                        }
                    }
                }
            } else {
                const hasAllColumns = pouchRowDataColumnIndices.every(col => selectedCols.has(col));
                
                if (hasAllColumns) {
                    // 全列選択の場合
                    const groupIndex = Math.floor(rowIndex / 1000);
                    const group = groupData[groupIndex];
                    if (group) {
                        const rowIndexInGroup = (rowIndex % 1000) - 1;
                        const row = group.rows[rowIndexInGroup];
                        if (row && row.pouchId) {
                            if (isDeleteKey) {
                                // Deleteキー：パウチを削除対象に追加
                                pouchesToDelete.add(row.pouchId);
                            } else {
                                // Backspaceキー：値クリアのみ（削除対象には追加しない）
                            }
                        }
                    }
                }
            }
        });
        
        // 削除処理がある場合（Deleteキーの場合のみ）は、削除対象のIDをonBulkUpdateに渡す
        if (isDeleteKey && (groupsToDelete.size > 0 || pouchesToDelete.size > 0)) {
            const updates: {
                deletedGroups?: string[];
                deletedPouches?: string[];
            } = {};
            
            if (groupsToDelete.size > 0) {
                updates.deletedGroups = Array.from(groupsToDelete);
            }
            
            if (pouchesToDelete.size > 0) {
                updates.deletedPouches = Array.from(pouchesToDelete);
            }
            
            onBulkUpdate(updates);
            
            // 削除処理後に選択範囲をクリア
            setSelectedCells({});
            setSelectionStart(null);
            setSelectionEnd(null);
            
            return;
        }
        
        // 削除対象がない場合は、従来の値リセット処理を実行
        const groupTitleCells = getSelectedCellsOfType('groupTitle', selection);
        const pouchTitleCells = getSelectedCellsOfType('pouchTitle', selection);
        const pouchValueCells = getSelectedCellsOfType('pouchValue', selection);
        const multiplierSignCells = getSelectedCellsOfType('multiplierSign', selection);
        const multiplierValueCells = getSelectedCellsOfType('multiplierValue', selection);
        
        // 変更内容を蓄積
        const updates: {
            groupTitles?: { groupId: string; title: string }[];
            pouchTitles?: { pouchId: string; title: string }[];
            pouchValues?: { pouchId: string; value: number; multiplier: number; lastExpression?: string; valueWhenLastExpression?: number; multiplyWhenLastExpression?: number }[];
            pouchMultipliers?: { pouchId: string; multiplier: number }[];
            pouchErrors?: { pouchId: string; hasError: boolean }[];
        } = {};
        
        // グループタイトルをクリア
        if (groupTitleCells.length > 0) {
            updates.groupTitles = groupTitleCells
                .filter(cell => cell.groupId)
                .map(cell => ({ groupId: cell.groupId!, title: '' }));
        }
        
        // パウチタイトルをクリア
        if (pouchTitleCells.length > 0) {
            updates.pouchTitles = pouchTitleCells
                .filter(cell => cell.pouchId)
                .map(cell => ({ pouchId: cell.pouchId!, title: '' }));
        }
        
        // パウチ値を0にリセット（レイヤー列は該当 layer のみ、マルチは維持）
        if (pouchValueCells.length > 0) {
            updates.pouchValues = pouchValueCells
                .filter(cell => cell.pouchId)
                .map(cell => {
                    const pouch = pouches.find(p => p.id === cell.pouchId);
                    return {
                        pouchId: cell.pouchId!,
                        layerId: cell.layerId ?? activeLayerId,
                        value: 0,
                        multiplier: pouch?.multiplier ?? 1,
                        lastExpression: '',
                        valueWhenLastExpression: undefined,
                        multiplyWhenLastExpression: undefined,
                    };
                });
        }
        
        // マルチプライヤー符号を×（正値）にリセット
        if (multiplierSignCells.length > 0) {
            const multiplierUpdates = multiplierSignCells
                .filter(cell => cell.pouchId)
                .map(cell => {
                    const pouch = pouches.find(p => p.id === cell.pouchId);
                    return pouch && pouch.multiplier < 0 
                        ? { pouchId: cell.pouchId!, multiplier: Math.abs(pouch.multiplier) }
                        : null;
                })
                .filter((update): update is { pouchId: string; multiplier: number } => update !== null);
            
            if (multiplierUpdates.length > 0) {
                updates.pouchMultipliers = multiplierUpdates;
            }
        }
        
        // マルチプライヤー値を1にリセット
        if (multiplierValueCells.length > 0) {
            const multiplierValueUpdates = multiplierValueCells
                .filter(cell => cell.pouchId)
                .map(cell => ({ pouchId: cell.pouchId!, multiplier: 1 }));
            
            // 既にマルチプライヤー更新がある場合はマージ
            if (updates.pouchMultipliers) {
                updates.pouchMultipliers = [...updates.pouchMultipliers, ...multiplierValueUpdates];
            } else {
                updates.pouchMultipliers = multiplierValueUpdates;
            }
        }
        
        // 変更がある場合は applyUpdates（レイヤー値・completePouches 経由で反映）
        if (updates.groupTitles || updates.pouchTitles || updates.pouchValues || updates.pouchMultipliers) {
            applyUpdates({
                ...updates,
                pasteHistoryType: HistoryActionType.EDIT_POUCH_VALUE,
            });
        }
    }, [
        getEffectiveSelection,
        getSelectedCellsOfType,
        applyUpdates,
        pouches,
        groupData,
        groups,
        pouchRowDataColumnIndices,
        activeLayerId,
        onBulkUpdate,
    ]);

    // テーブル操作中: 計算エリアの Delete を止めつつ、値セル Delete は 0 リセット等を実行
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key !== 'Delete' && e.key !== 'Backspace') {
                return;
            }
            if (!isOpen || editingCell) {
                return;
            }
            if (isEditableElement(document.activeElement)) {
                return;
            }
            if (!isTableKeyboardContextActive()) {
                return;
            }
            const selection = getEffectiveSelection();
            if (Object.keys(selection).length === 0) {
                return;
            }
            e.preventDefault();
            e.stopPropagation();
            processSelectedCells(e.key === 'Delete', selection);
        };
        window.addEventListener('keydown', handler, true);
        return () => window.removeEventListener('keydown', handler, true);
    }, [isOpen, editingCell, getEffectiveSelection, processSelectedCells]);

    const handleClearButtonActivate = useCallback(
        (e: React.MouseEvent | React.TouchEvent) => {
            e.preventDefault();
            e.stopPropagation();
            if (Object.keys(selectedCells).length > 0) {
                processSelectedCells(false);
                return;
            }
            if (!selectionStart) return;
            const { row: rowIndex, cell: cellIndex } = selectionStart;
            const groupIndex = Math.floor(rowIndex / 1000);
            const group = groupData[groupIndex];
            if (!group) return;
            if (rowIndex % 1000 === 0 && cellIndex === 0) {
                onUpdateGroupTitle(group.id, '');
            } else if (rowIndex % 1000 !== 0) {
                const rowIndexInGroup = (rowIndex % 1000) - 1;
                const row = group.rows[rowIndexInGroup];
                if (!row || !row.pouchId) return;
                if (cellIndex === 0) {
                    onUpdatePouchTitle(row.pouchId, '');
                } else if (cellIndex === 1) {
                    onUpdatePouchValue(row.pouchId, 0, 1, HistoryActionType.EDIT_POUCH_VALUE);
                } else if (cellIndex === 2) {
                    const pouch = pouches.find(p => p.id === row.pouchId);
                    if (pouch && pouch.multiplier < 0) {
                        onUpdatePouchMultiplier(row.pouchId, Math.abs(pouch.multiplier));
                    }
                } else if (cellIndex === 3) {
                    onUpdatePouchMultiplier(row.pouchId, 1);
                }
            }
        },
        [
            selectedCells,
            selectionStart,
            groupData,
            pouches,
            processSelectedCells,
            onUpdateGroupTitle,
            onUpdatePouchTitle,
            onUpdatePouchValue,
            onUpdatePouchMultiplier,
        ]
    );

    // セル選択時のキー入力で編集開始（useCallback にしない — handleCellKeyDown の最新クロージャを参照する）
    const handleCellKeyDownForEdit = (e: React.KeyboardEvent, rowIndex: number, cellIndex: number, currentValue: string) => {
        // 編集中の場合は何もしない（入力フィールドで処理）
        if (editingCell) return;
        
        // カーソルキー、TabキーはhandleCellKeyDownに委譲
        if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown', 'Tab'].includes(e.key)) {
            e.preventDefault();
            handleCellKeyDown(e as React.KeyboardEvent<HTMLTableCellElement>);
            return;
        }
        
        // Shift+EnterもhandleCellKeyDownに委譲（編集開始しない）
        if (e.key === 'Enter' && e.shiftKey) {
            handleCellKeyDown(e as React.KeyboardEvent<HTMLTableCellElement>);
            return;
        }
        
        // Enterキーで編集開始
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleEditStart(rowIndex, cellIndex, currentValue, undefined, true);
            return;
        }
        
        // スペースキーで編集開始
        if (e.key === ' ') {
            e.preventDefault();
            handleEditStart(rowIndex, cellIndex, currentValue, undefined, true);
            return;
        }
        
        // F2キーで編集開始（WindowsのExcelと同じ）
        if (e.key === 'F2' && !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey) {
            e.preventDefault();
            handleEditStart(rowIndex, cellIndex, currentValue, undefined, true);
            return;
        }
        
        // Delete/Backspaceキーで値をリセット
        if (e.key === 'Delete' || e.key === 'Backspace') {
            e.preventDefault();
            
            // 選択範囲がある場合は一括処理
            if (Object.keys(selectedCells).length > 0) {
                processSelectedCells(e.key === 'Delete');
                return;
            }
            
            // 単一セルの場合の処理
            const groupIndex = Math.floor(rowIndex / 1000);
            const group = groupData[groupIndex];
            
            if (!group) return;
            
            if (rowIndex % 1000 === 0 && cellIndex === 0) {
                // グループタイトルの場合
                onUpdateGroupTitle(group.id, '');
            } else if (rowIndex % 1000 !== 0) {
                const rowIndexInGroup = (rowIndex % 1000) - 1;
                const row = group.rows[rowIndexInGroup];
                if (!row || !row.pouchId) return;
                
                if (cellIndex === 0) {
                    onUpdatePouchTitle(row.pouchId, '');
                } else if (
                    cellIndex === 1 ||
                    (sheetLayers.length > 1 &&
                        cellIndex >= cols.layerStart &&
                        cellIndex < cols.layerStart + sheetLayers.length)
                ) {
                    const layerId =
                        sheetLayers.length > 1 && cellIndex >= cols.layerStart
                            ? sheetLayers[cellIndex - cols.layerStart]?.id
                            : undefined;
                    onUpdatePouchValue(
                        row.pouchId,
                        0,
                        1,
                        HistoryActionType.EDIT_POUCH_VALUE,
                        true,
                        '',
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        layerId
                    );
                } else if (cellIndex === multOpCell) {
                    const pouch = pouches.find(p => p.id === row.pouchId);
                    if (pouch && pouch.multiplier < 0) {
                        onUpdatePouchMultiplier(row.pouchId, Math.abs(pouch.multiplier));
                    }
                } else if (cellIndex === multValCell) {
                    onUpdatePouchMultiplier(row.pouchId, 1);
                }
            }
            return;
        }
        
        // Tabキーで右のセルに移動（編集状態にしない）
        if (e.key === 'Tab' && !e.shiftKey) {
            e.preventDefault();
            const nextCellIndex = cellIndex + 1;
            // 最大列数を動的に計算
            const maxCells = Math.max(5, 10); // 十分な余裕を持たせる
            if (nextCellIndex < maxCells) {
                moveToCellWithoutEdit(rowIndex, nextCellIndex);
            }
            return;
        }
        
        // Shift+Enterで上のセルに移動（編集状態にしない）
        if (e.key === 'Enter' && e.shiftKey) {
            e.preventDefault();
            const prevRowIndex = rowIndex - 1;
            if (prevRowIndex >= 0) {
                moveToCellWithoutEdit(prevRowIndex, cellIndex);
            }
            return;
        }
        
        // Shift+Tabで左のセルに移動（編集状態にしない）
        if (e.key === 'Tab' && e.shiftKey) {
            e.preventDefault();
            if (cellIndex === 0) {
                // 先頭列（cellIndex=0）でShift+Tab：前の行の5列目へ
                const prevRowIndex = rowIndex - 1;
                if (prevRowIndex >= 0) {
                    moveToCellWithoutEdit(prevRowIndex, totalCell);
                }
            } else {
                const prevCellIndex = cellIndex - 1;
                if (prevCellIndex >= 0) {
                    moveToCellWithoutEdit(rowIndex, prevCellIndex);
                }
            }
            return;
        }
        
        // 数字とパウチ値用文字のみ編集開始を許可（計算エリアと同じ文字セット）
        const allowedChars = /^[0-9+\-*/×÷xX^%:~＋－＊／％：～.?='()]$/;
        
        if (allowedChars.test(e.key)) {
            // Shift以外の修飾キーと組み合わさっている場合は無効
            if (e.ctrlKey || e.metaKey || e.altKey) {
                return;
            }
            
            // 全てのセルで数字・演算子による編集開始を許可
            // cellIndex 0: グループタイトル・パウチタイトル
            // cellIndex 1: パウチ値
            // cellIndex 3: マルチプライヤー
            e.preventDefault();
            handleEditStart(rowIndex, cellIndex, e.key, undefined, false);
            return;
        }
        
        // その他のキーは編集開始しない（通常のセル移動などに使用）
    };

    // 選択範囲の計算を関数化（マウスドラッグと同じロジック）
    // 戻り値: { updated: boolean, adjustedEnd?: {row: number, cell: number} }
    const calculateSelectionRange = useCallback((startRow: number, startCell: number, endRow: number, endCell: number): { updated: boolean, adjustedEnd?: {row: number, cell: number} } => {
        const minRow = Math.min(startRow, endRow);
        const maxRow = Math.max(startRow, endRow);
        let minCol = Math.min(startCell, endCell);
        let maxCol = Math.max(startCell, endCell);
        
        
        // 新しい選択範囲を生成（DOM に存在する行・セルのみ）
        const newSelectedCells: {[rowIndex: number]: {[cellIndex: number]: boolean}} = {};
        if (!tableRef.current) {
            return { updated: false };
        }
        tableRef.current.querySelectorAll('tr[data-row-index]').forEach(tr => {
            const r = parseInt(tr.getAttribute('data-row-index') ?? '', 10);
            if (!Number.isFinite(r) || r < minRow || r > maxRow) return;
            const rowCells: {[cellIndex: number]: boolean} = {};
            for (let c = minCol; c <= maxCol; c++) {
                if (tr.querySelector(`[data-cell-index="${c}"]`)) {
                    rowCells[c] = true;
                }
            }
            if (Object.keys(rowCells).length > 0) {
                newSelectedCells[r] = rowCells;
            }
        });
        
        // 現在の選択範囲と新しい選択範囲が同じかどうかをチェック
        const currentSelectedKeys = Object.keys(selectedCells).sort();
        const newSelectedKeys = Object.keys(newSelectedCells).sort();
        
        let isSameSelection = currentSelectedKeys.length === newSelectedKeys.length;
        if (isSameSelection) {
            for (let i = 0; i < currentSelectedKeys.length; i++) {
                const rowKey = currentSelectedKeys[i];
                const currentCols = Object.keys(selectedCells[parseInt(rowKey)] || {}).sort();
                const newCols = Object.keys(newSelectedCells[parseInt(rowKey)] || {}).sort();
                
                if (currentCols.length !== newCols.length) {
                    isSameSelection = false;
                    break;
                }
                
                for (let j = 0; j < currentCols.length; j++) {
                    if (currentCols[j] !== newCols[j]) {
                        isSameSelection = false;
                        break;
                    }
                }
                
                if (!isSameSelection) break;
            }
        }
        
        // 選択範囲が変わらない場合は状態を更新しない
        if (isSameSelection) {
            
            return { updated: false };
        }
        
        
        
        
        // 移動ロジック側で適切な位置に移動済みなので、終了位置はそのまま使用
        let adjustedEndRow = endRow;
        let adjustedEndCell = endCell;
        
        // 選択範囲が変わった場合のみ状態を更新
        setSelectionStart({row: startRow, cell: startCell});
        setSelectionEnd({row: adjustedEndRow, cell: adjustedEndCell});
        setSelectedCells(newSelectedCells);
        
        return { 
            updated: true, 
            adjustedEnd: { row: adjustedEndRow, cell: adjustedEndCell }
        };
    }, [selectedCells]);

     // 選択セルが変更されたときに実行
     useEffect(() => {
         // 選択セルの情報からグループIDとパウチIDを抽出
         if (Object.keys(selectedCells).length > 0) {
             // IDベースでの選択状態追跡用
             const newSelectedGroupIds: string[] = [];
             const newSelectedPouchIds: string[] = [];

             // テーブル内の選択された行を取得
             const selectedRows = document.querySelectorAll('tr[data-row-index]');
             
             selectedRows.forEach(row => {
                 const rowIndex = parseInt(row.getAttribute('data-row-index') || '-1', 10);
                 
                 // 選択されている行かチェック
                 if (rowIndex >= 0 && selectedCells[rowIndex]) {
                     // グループIDを取得
                     const groupId = row.getAttribute('data-group-id');
                     if (groupId && !newSelectedGroupIds.includes(groupId)) {
                         newSelectedGroupIds.push(groupId);
                     }
                     
                     // パウチIDを取得
                     const pouchId = row.getAttribute('data-pouch-id');
                     if (pouchId && !newSelectedPouchIds.includes(pouchId)) {
                         newSelectedPouchIds.push(pouchId);
                     }
                     
                     // セル内の選択された要素も確認
                     const cells = row.querySelectorAll('td[data-cell-index], th[data-cell-index]');
                     cells.forEach(cell => {
                         const cellIndex = parseInt(cell.getAttribute('data-cell-index') || '-1', 10);
                         
                         // セルが選択されているかチェック
                         if (cellIndex >= 0 && selectedCells[rowIndex][cellIndex]) {
                             // セルのグループIDを取得
                             const cellGroupId = cell.getAttribute('data-group-id');
                             if (cellGroupId && !newSelectedGroupIds.includes(cellGroupId)) {
                                 newSelectedGroupIds.push(cellGroupId);
                             }
                             
                             // セルのパウチIDを取得
                             const cellPouchId = cell.getAttribute('data-pouch-id');
                             if (cellPouchId && !newSelectedPouchIds.includes(cellPouchId)) {
                                 newSelectedPouchIds.push(cellPouchId);
                             }
                         }
                     });
                 }
             });
             
             // 選択結果を通知
             if (onSelectionChange) {
                 onSelectionChange(newSelectedGroupIds, newSelectedPouchIds);
             }
         } else {
             // 選択がない場合は空の配列を通知
             if (onSelectionChange) {
                 onSelectionChange([], []);
             }
         }
     }, [selectedCells, onSelectionChange]);

     // ホバー状態が変更されたときに実行
     useEffect(() => {
         if (onHoverGroupChange) {
             onHoverGroupChange(hoveredGroup, hoveredPouch);
         }
     }, [hoveredGroup, hoveredPouch, onHoverGroupChange]);

     // グループヘッダーのマウスイベントを処理
     const handleGroupHeaderMouseEnter = (groupId: string) => {
         setHoveredGroup(groupId);
         setHoveredPouch(null);
     };

     const handleGroupHeaderMouseLeave = () => {
         setHoveredGroup(null);
     };

     // パウチ行のマウスイベントを処理
     const findPouchIdFromRow = (groupId: string, row: TableRow): string | null => {
         // 行に対応するパウチを見つける
         const targetGroup = groupData.find(g => g.id === groupId);
         if (!targetGroup) return null;
         
         // より正確なパウチの特定
         const matchingPouch = filteredPouches.find(p => 
             // タイトルが一致（どちらも空または同じ値）
             (p.title === row.pouchTitle || (!p.title && !row.pouchTitle)) && 
             // 色が一致
             p.color === row.color && 
             // 値が一致
             p.value === row.value &&
             // 乗数が一致
             p.multiplier === row.multiplier &&
             // 所属グループの条件
             (groupId === 'ungrouped' 
                ? !groups.some(g => g.pouches && g.pouches.includes(p.id))
                : (targetGroup.pouches && targetGroup.pouches.includes(p.id)))
         );
         
         // マッチするパウチが見つかった場合はそのIDを返す
         if (matchingPouch) {
             return matchingPouch.id;
         }
         
         // 厳密な条件で見つからない場合、グループ所属のみで判定
         const groupPouches = filteredPouches.filter(p => 
             (groupId === 'ungrouped' 
                ? !groups.some(g => g.pouches && g.pouches.includes(p.id))
                : (targetGroup.pouches && targetGroup.pouches.includes(p.id)))
         );
         
         if (groupPouches.length > 0) {
             return groupPouches[0].id; // グループに属する最初のパウチのIDを返す
         }
         
         return null;
     };

    // 指のX座標から対応する列を特定する関数
    const getColumnFromXCoordinate = (clientX: number, rowIndex: number): number => {
        if (!tableRef.current) return -1;
        
        // 指定された行の要素を取得
        const rowElement = tableRef.current.querySelector(`tr[data-row-index="${rowIndex}"]`);
        if (!rowElement) return -1;
        
        // その行のすべてのセルを取得
        const cells = rowElement.querySelectorAll('[data-cell-index]') as NodeListOf<HTMLElement>;
        if (cells.length === 0) return -1;
        
        // 各セルの位置をチェックして、X座標が含まれる列を見つける
        for (let i = 0; i < cells.length; i++) {
            const cell = cells[i];
            const cellRect = cell.getBoundingClientRect();
            
            // X座標がセルの範囲内にある場合
            if (clientX >= cellRect.left && clientX <= cellRect.right) {
                const cellIndex = parseInt(cell.getAttribute('data-cell-index') || '-1');
                return cellIndex;
            }
        }
        
        // どのセルにも該当しない場合、一番右のセルを返す
        const lastCell = cells[cells.length - 1];
        const lastCellIndex = parseInt(lastCell.getAttribute('data-cell-index') || '-1');
        return lastCellIndex;
    };

    // リサイズハンドル位置更新用の関数を抽出
    const updateResizeHandlePosition = (rowIndex: number, cellIndex: number) => {
        if (!isTouchDevice || !resizeHandleRef.current || !tableRef.current || !selectionStart) return;
        
        // 選択開始位置より上や左に行かないように制限
        const constrainedRowIndex = Math.max(rowIndex, selectionStart.row);
        const constrainedCellIndex = Math.max(cellIndex, selectionStart.cell);
        
        // 対応するDOM要素を見つける
        const targetCell = tableRef.current.querySelector(
            `tr[data-row-index="${constrainedRowIndex}"] [data-cell-index="${constrainedCellIndex}"]`
        ) as HTMLElement | null;
        
        if (targetCell) {
            
            // セルの位置情報を取得
            const cellRect = targetCell.getBoundingClientRect();
            
            // テーブルの位置情報を取得
            const tableRect = tableRef.current.getBoundingClientRect();
            
            // テーブル左端からの距離を計算
            const distanceFromTableLeft = cellRect.right - tableRect.left;
            
            // 新しいleft位置を設定（テーブル左端からの距離で計算）
            resizeHandleRef.current.style.left = `${distanceFromTableLeft}px`;
            resizeHandleRef.current.style.top = `${cellRect.bottom}px`;
        }
    };

    // リサイズハンドルを明示的に表示させる関数
    const forceShowResizeHandle = () => {
        if (!isTouchDevice || !resizeHandleRef.current || Object.keys(selectedCells).length === 0 || !tableRef.current || !selectionStart) {
            return false;
        }
        // 選択範囲の右下のセルを特定
        const selectedRowIndices = Object.keys(selectedCells).map(Number);
        const maxRow = Math.max(...selectedRowIndices);
        
        let maxCell = -1;
        // 最大行の中で最も右のセルを特定
        if (selectedCells[maxRow]) {
            maxCell = Math.max(...Object.keys(selectedCells[maxRow]).map(Number));
        }
        
        const bottomRow = selectionEnd
            ? Math.max(selectionStart.row, selectionEnd.row)
            : maxRow;
        const bottomCell = selectionEnd
            ? Math.max(selectionStart.cell, selectionEnd.cell)
            : maxCell;

        if ((bottomRow >= 0 || isLayerHeaderRow(bottomRow)) && bottomCell >= 0) {
            // 選択開始位置より上や左に行かないように制限
            const constrainedRowIndex = Math.max(bottomRow, selectionStart.row);
            const constrainedCellIndex = Math.max(bottomCell, selectionStart.cell);
            
            // 対応するDOM要素を見つける
            const targetCell = tableRef.current.querySelector(
                `tr[data-row-index="${constrainedRowIndex}"] [data-cell-index="${constrainedCellIndex}"]`
            ) as HTMLElement | null;
            
            if (targetCell) {
                
                // セルの位置情報を取得
                const cellRect = targetCell.getBoundingClientRect();
                
                // テーブルの位置情報を取得
                const tableRect = tableRef.current.getBoundingClientRect();
                
                // テーブル左端からの距離を計算
                const distanceFromTableLeft = cellRect.right - tableRect.left;
                
                // 新しいleft位置を設定（テーブル左端からの距離で計算）
                resizeHandleRef.current.style.left = `${distanceFromTableLeft}px`;
                
                // topはそのまま使用
                resizeHandleRef.current.style.top = `${cellRect.bottom}px`;
                return true;
            }
        }
        
        
        return false;
    };

    // リサイズハンドルのダブルタッチ処理
    const handleResizeHandleDoubleTap = useCallback(() => {
        // 現在選択中のセルを特定（選択範囲の左上のセル）
        if (Object.keys(selectedCells).length > 0) {
            const selectedRowIndices = Object.keys(selectedCells).map(Number).sort((a, b) => a - b);
            const minRow = selectedRowIndices[0];
            
            if (selectedCells[minRow]) {
                const selectedCellIndices = Object.keys(selectedCells[minRow]).map(Number).sort((a, b) => a - b);
                const minCell = selectedCellIndices[0];
                
                if (minRow >= 0 && minCell >= 0) {
                    // 対応するセルにフォーカスしてダブルクリックをシミュレート
                    const targetCell = document.querySelector(`[tabIndex="${minRow * 10 + minCell}"]`) as HTMLElement;
                    if (targetCell) {
                        targetCell.focus();
                        // ダブルクリックイベントを発火
                        const doubleClickEvent = new MouseEvent('dblclick', {
                            bubbles: true,
                            cancelable: true,
                            view: window
                        });
                        targetCell.dispatchEvent(doubleClickEvent);
                    }
                }
            }
        }
    }, [selectedCells]);

    // タッチデバイス用のリサイズハンドル開始ハンドラー
    const handleResizeStart = (e: React.TouchEvent) => {
        // タッチデバイスでなければ処理しない
        if (!isTouchDevice) return;
        
        e.stopPropagation();
        // e.preventDefault();

        // リサイズフラグをセット
        setIsResizing(true);
        
        // リサイズ処理（単一タップの場合のみ）
        // リサイズ中は視覚的フィードバックを提供
        if (resizeHandleRef.current) {
            resizeHandleRef.current.style.backgroundColor = '#005fa3';
            resizeHandleRef.current.style.opacity = '1'; // 不透明度を上げて目立たせる
        }
        
        // 選択セルがある場合
        if (Object.keys(selectedCells).length > 0) {
            // 選択範囲の右下のセルを特定
            const selectedRowIndices = Object.keys(selectedCells).map(Number);
            const maxRow = Math.max(...selectedRowIndices);
            
            let maxCell = -1;
            // 最大行の中で最も右のセルを特定
            if (selectedCells[maxRow]) {
                maxCell = Math.max(...Object.keys(selectedCells[maxRow]).map(Number));
            }
            
            const bottomRow = selectionEnd
                ? Math.max(selectionStart?.row ?? maxRow, selectionEnd.row)
                : maxRow;
            const bottomCell = selectionEnd
                ? Math.max(selectionStart?.cell ?? maxCell, selectionEnd.cell)
                : maxCell;

            if ((bottomRow >= 0 || isLayerHeaderRow(bottomRow)) && bottomCell >= 0) {
                // このセルを最後のタッチ位置として設定
                if (!selectionStart) {
                    setSelectionStart({row: bottomRow, cell: bottomCell});
                }
                if (!selectionEnd) {
                    setSelectionEnd({row: bottomRow, cell: bottomCell});
                }
                setLastTouchPosition({row: bottomRow, cell: bottomCell});
            }
        }
    };

    // タッチ移動でリサイズ処理
    const handleResizeMove = (e: React.TouchEvent) => {
        // タッチデバイスでなければ処理しない
        if (!isTouchDevice) return;
        
        // リサイズ中でない場合は何もしない
        if (!isResizing || !selectionStart) return;
        
        // e.preventDefault();
        e.stopPropagation();
        
        // 新しいタッチ位置を取得
        const touch = e.touches[0];
        const element = document.elementFromPoint(touch.clientX, touch.clientY);
        
        let currentRowIndex = -1;
        let currentCellIndex = -1;
        
        if (element) {
            // タッチ位置のセル要素を取得
            const cell = (element as HTMLElement).closest('td, th');
            
            if (cell) {
                // 折りたたまれたグループのtbody内のセルの場合はスキップ
                const tbody = cell.closest('tbody');
                if (tbody && tbody.classList.contains('collapsed')) return;
                
                // 行と列のインデックスを取得
                const row = cell.parentElement as HTMLTableRowElement;
                if (row) {
                    // セルのデータ属性から正確な行と列の位置を取得
                    currentRowIndex = parseInt(row.getAttribute('data-row-index') || '-1');
                    currentCellIndex = parseInt((cell as HTMLElement).getAttribute('data-cell-index') || '-1');
                }
            }
        }
        
        // 表外に指が出た場合の処理
        if (currentRowIndex < 0 || currentCellIndex < 0) {
            // 選択範囲の最下行を取得
            const selectedRows = Object.keys(selectedCells).map(r => parseInt(r)).sort((a, b) => b - a);
            if (selectedRows.length === 0) return;
            
            const bottomRow = selectedRows[0]; // 最下行
            
            // 指のY座標を確認して、表の下に出ているかチェック
            if (tableRef.current) {
                const tableRect = tableRef.current.getBoundingClientRect();
                
                // 指が表の下に出ている場合、さらに下の行を探す
                if (touch.clientY > tableRect.bottom) {
                    // テーブル内のすべての行を取得して、最下行を見つける
                    const allRows = tableRef.current.querySelectorAll('tr[data-row-index]');
                    let maxRowIndex = bottomRow;
                    
                    allRows.forEach((row) => {
                        const rowIndex = parseInt((row as HTMLElement).getAttribute('data-row-index') || '-1');
                        if (rowIndex > maxRowIndex) {
                            maxRowIndex = rowIndex;
                        }
                    });
                    
                    // より下の行がある場合はそれを使用
                    if (maxRowIndex > bottomRow) {
                        const targetColumn = getColumnFromXCoordinate(touch.clientX, maxRowIndex);
                        if (targetColumn >= 0) {
                            currentRowIndex = maxRowIndex;
                            currentCellIndex = targetColumn;
                        } else {
                            return;
                        }
                    } else {
                        // 最下行の場合、X座標のみで列を決定
                        const targetColumn = getColumnFromXCoordinate(touch.clientX, bottomRow);
                        if (targetColumn >= 0) {
                            currentRowIndex = bottomRow;
                            currentCellIndex = targetColumn;
                        } else {
                            return;
                        }
                    }
                } else {
                    // 指が表の横に出ている場合、現在の選択範囲の最下行でX座標のみで列を決定
                    const targetColumn = getColumnFromXCoordinate(touch.clientX, bottomRow);
                    if (targetColumn >= 0) {
                        currentRowIndex = bottomRow;
                        currentCellIndex = targetColumn;
                    } else {
                        return;
                    }
                }
            } else {
                return;
            }
        }
        
        if (currentCellIndex >= 0 && (currentRowIndex >= 0 || isLayerHeaderRow(currentRowIndex))) {
            // 現在位置が前回と同じなら処理をスキップ
            if (lastTouchPosition && 
                lastTouchPosition.row === currentRowIndex && 
                lastTouchPosition.cell === currentCellIndex) {
                return;
            }
            
            
            // 選択終了位置を更新
            setSelectionEnd({row: currentRowIndex, cell: currentCellIndex});
            
            // 現在のタッチ位置を記録
            setLastTouchPosition({row: currentRowIndex, cell: currentCellIndex});
            
            // 行範囲を計算
            // 注意: リサイズの場合は開始行より下方向にしか拡大できない
            const minRow = selectionStart.row; // 開始行は固定
            const maxRow = Math.max(selectionStart.row, currentRowIndex); // 開始行より下のみ許可
            
            // 列範囲を計算
            // 注意: リサイズの場合は開始列より右方向にしか拡大できない
            const minCol = selectionStart.cell; // 開始列は固定
            const maxCol = Math.max(selectionStart.cell, currentCellIndex); // 開始列より右のみ許可
            
            
            if (tableRef.current) {
                // 選択範囲内のセルをチェック
                for (let r = minRow; r <= maxRow; r++) {
                    const rowElement = tableRef.current.querySelector(`tr[data-row-index="${r}"]`);
                    if (rowElement) {
                        // この行の選択範囲内のセルをチェック
                        for (let c = minCol; c <= maxCol; c++) {
                            const cellElement = rowElement.querySelector(`[data-cell-index="${c}"]`);
                            if (cellElement) {
                                
                            }
                        }
                    }
                }
            }
            
            let adjustedMinCol = minCol;
            let adjustedMaxCol = maxCol;
            
            // 選択セルマップを作成
            const updatedCells: {[rowIndex: number]: {[cellIndex: number]: boolean}} = {};
            
            // 各行を処理
            for (let r = minRow; r <= maxRow; r++) {
                updatedCells[r] = {};
                
                // 全ての行に対して同じ列範囲を適用
                for (let c = adjustedMinCol; c <= adjustedMaxCol; c++) {
                    updatedCells[r][c] = true;
                }
            }
            
            
            // 選択範囲が変わったかどうかをチェック
            let selectionChanged = false;
            
            // 行数のチェック
            const currentRowsCount = Object.keys(selectedCells).length;
            const newRowsCount = Object.keys(updatedCells).length;
            
            if (currentRowsCount !== newRowsCount) {
                selectionChanged = true;
            } else {
                // 各行のセル数をチェック
                for (const rowIdx in updatedCells) {
                    if (!selectedCells[rowIdx] || 
                        Object.keys(selectedCells[rowIdx]).length !== Object.keys(updatedCells[rowIdx]).length) {
                        selectionChanged = true;
                        break;
                    }
                    
                    // 各セルの存在をチェック
                    for (const cellIdx in updatedCells[rowIdx]) {
                        if (!selectedCells[rowIdx][cellIdx]) {
                            selectionChanged = true;
                            break;
                        }
                    }
                    
                    if (selectionChanged) break;
                }
            }
            
            // 選択セルを更新
            setSelectedCells(updatedCells);
            
            // 選択範囲が変わった場合のみリサイズハンドルの位置を更新
            if (selectionChanged) {
                updateResizeHandlePosition(currentRowIndex, currentCellIndex);
            }
        }
    };

    // タッチデバイス用のリサイズ終了ハンドラー
    const handleResizeEnd = (e: React.TouchEvent) => {
        if (!isTouchDevice) return;
        
        e.stopPropagation();
        e.preventDefault();
        
        
        
        // リサイズフラグをオフに
        setIsResizing(false);
        
        // 最終タッチ位置をリセット
        setLastTouchPosition(null);
        
        // 元の見た目に戻す
        if (resizeHandleRef.current) {
            resizeHandleRef.current.style.backgroundColor = '#0078d7';
        }
        
        // 選択範囲が変更された場合、明示的に保存して確実に維持する
        if (selectionStart && selectionEnd) {
            // 新しい選択範囲の計算
            const minRow = selectionStart.row; // 開始行は固定
            const maxRow = Math.max(selectionStart.row, selectionEnd.row); // 開始行より下のみ許可
            let minCol = selectionStart.cell; // 開始列
            let maxCol = Math.max(selectionStart.cell, selectionEnd.cell); // 最大列
            
            
            // 選択セルマップの再構築
            const finalSelectedCells: {[rowIndex: number]: {[cellIndex: number]: boolean}} = {};
            
            for (let r = minRow; r <= maxRow; r++) {
                finalSelectedCells[r] = {};
                
                // すべての行に対して同じ列範囲を適用
                for (let c = minCol; c <= maxCol; c++) {
                    finalSelectedCells[r][c] = true;
                }
            }
            
            // 確実に選択状態を更新
            setSelectedCells(finalSelectedCells);
        }
        
        // 1回だけリサイズハンドルの表示を試行（十分な遅延を入れる）
        setTimeout(() => {
            
            forceShowResizeHandle();
        }, 200);
    };

    // 選択範囲が変更されたときのリサイズハンドル位置更新
    useEffect(() => {
        // タッチデバイスでなければ処理しない
        if (!isTouchDevice || isResizing) return;
        
        // 選択範囲があり、リサイズ中でない場合のみ実行
        if (Object.keys(selectedCells).length > 0) {
            // 短いタイムアウトで非同期実行することで、DOM更新後に実行されるようにする
            const timerId = setTimeout(() => {
                
                forceShowResizeHandle();
            }, 100);
            
            return () => clearTimeout(timerId);
        }
    }, [selectedCells, isTouchDevice, isResizing]);

    // バックアップとして1回だけ追加の遅延実行を行う
    useEffect(() => {
        if (isTouchDevice && Object.keys(selectedCells).length > 0 && !isResizing) {
            // 遅延を長めに設定し、1回だけ試行
            const timerId = setTimeout(() => {
                
                forceShowResizeHandle();
            }, 500);
            
            return () => clearTimeout(timerId);
        }
    }, [isTouchDevice, selectedCells, isResizing]);

    // 行がグループヘッダー行かどうかを判定する関数
    const isGroupHeaderRow = (rowIndex: number): boolean => {
        // グループヘッダー行は1000の倍数のインデックスを持つように設計されている
        return rowIndex % 1000 === 0;
    };

    // マウスアップで選択終了
    const handleMouseUp = () => {
        setIsSelecting(false);
        // テキスト選択を再有効化
        document.body.classList.remove('no-select');
    };

    // 現在の選択範囲の情報を取得する関数
    const getSelectionBounds = () => {
        const selectedRowsArray = Object.keys(selectedCells).map(Number).sort((a, b) => a - b);
        if (selectedRowsArray.length === 0) return null;
        
        const minRow = selectedRowsArray[0];
        const maxRow = selectedRowsArray[selectedRowsArray.length - 1];
        
        let minCol = Infinity;
        let maxCol = -Infinity;
        
        selectedRowsArray.forEach(row => {
            const selectedColsArray = Object.keys(selectedCells[row] || {}).map(Number);
            selectedColsArray.forEach(col => {
                minCol = Math.min(minCol, col);
                maxCol = Math.max(maxCol, col);
            });
        });
        
        return { minRow, maxRow, minCol, maxCol };
    };

    const getTableRowIndices = useCallback((): number[] => {
        if (!tableRef.current) return [];
        const indices: number[] = [];
        tableRef.current.querySelectorAll('tr[data-row-index]').forEach(tr => {
            const n = parseInt(tr.getAttribute('data-row-index') ?? '', 10);
            if (Number.isFinite(n)) indices.push(n);
        });
        return Array.from(new Set(indices)).sort((a, b) => a - b);
    }, []);

    const getCellInTable = useCallback((row: number, cell: number): HTMLElement | null => {
        if (!tableRef.current) return null;
        return tableRef.current.querySelector(
            `tr[data-row-index="${row}"] [data-cell-index="${cell}"]`
        ) as HTMLElement | null;
    }, []);

    const findRowWithCell = useCallback(
        (fromRow: number, direction: 1 | -1, cellIndex: number): number | null => {
            const rows = getTableRowIndices();
            if (direction === 1) {
                for (const r of rows) {
                    if (r > fromRow && getCellInTable(r, cellIndex)) return r;
                }
            } else {
                for (let i = rows.length - 1; i >= 0; i--) {
                    const r = rows[i];
                    if (r < fromRow && getCellInTable(r, cellIndex)) return r;
                }
            }
            return null;
        },
        [getTableRowIndices, getCellInTable]
    );

    const findColWithCell = useCallback(
        (row: number, fromCol: number, direction: 1 | -1, maxCol: number): number | null => {
            if (direction === 1) {
                for (let c = fromCol + 1; c <= maxCol; c++) {
                    if (getCellInTable(row, c)) return c;
                }
            } else {
                for (let c = fromCol - 1; c >= 0; c--) {
                    if (getCellInTable(row, c)) return c;
                }
            }
            return null;
        },
        [getCellInTable]
    );

    const isGroupHeaderRowIndex = (row: number) => row >= 0 && row % 1000 === 0;

    // キーボードナビゲーション処理
    const handleCellKeyDown = (e: React.KeyboardEvent<HTMLTableCellElement>) => {
        const target = e.currentTarget;
        const rowIndex = parseInt(target.closest('tr')?.dataset.rowIndex || '0', 10);
        const cellIndex = parseInt(target.dataset.cellIndex || '0', 10);
        const maxNavCellIndex = totalCell;
        const multCol = sheetLayers.length > 1 ? cols.mult : 3;
        const isGroupHeaderCell = isGroupHeaderRowIndex(rowIndex);

        // セルに移動してフォーカス
        const moveToCell = (newRowIndex: number, newCellIndex: number, extendSelection: boolean = false) => {
            
            const newCell = getCellInTable(newRowIndex, newCellIndex);
            if (newCell) {
                newCell.focus();
                newCell.scrollIntoView({ block: 'nearest', inline: 'nearest' });
                
                if (extendSelection) {
                    // Shift+カーソルキーで範囲選択（マウスドラッグと同じパターン）
                    
                    
                    
                    
                    
                    // 選択終了位置のみを更新（selectionStartは保持）
                    setSelectionEnd({row: newRowIndex, cell: newCellIndex});
                    
                    // 選択範囲を計算してsetSelectedCellsを更新
                    if (selectionStart) {
                        const minRow = Math.min(selectionStart.row, newRowIndex);
                        const maxRow = Math.max(selectionStart.row, newRowIndex);
                        const minCol = Math.min(selectionStart.cell, newCellIndex);
                        const maxCol = Math.max(selectionStart.cell, newCellIndex);
                        
                        
                        
                        const newSelectedCells: {[rowIndex: number]: {[cellIndex: number]: boolean}} = {};
                        for (let r = minRow; r <= maxRow; r++) {
                            newSelectedCells[r] = {};
                            for (let c = minCol; c <= maxCol; c++) {
                                newSelectedCells[r][c] = true;
                            }
                        }
                        
                        setSelectedCells(newSelectedCells);
                    }
                } else {
                    // 通常の移動（選択範囲をクリア）
                    
                    setSelectionStart({row: newRowIndex, cell: newCellIndex});
                    setSelectionEnd({row: newRowIndex, cell: newCellIndex});
                    setSelectedCells({
                        [newRowIndex]: {
                            [newCellIndex]: true
                        }
                    });
                }
            }
        };

        const focusAndExtendShiftSelection = (
            nextRowIndex: number,
            nextCellIndex: number,
            anchor: { row: number; cell: number }
        ): boolean => {
            const nextCell = getCellInTable(nextRowIndex, nextCellIndex);
            if (!nextCell) {
                return false;
            }
            nextCell.focus();
            nextCell.scrollIntoView({ block: 'nearest', inline: 'nearest' });
            const result = calculateSelectionRange(
                anchor.row,
                anchor.cell,
                nextRowIndex,
                nextCellIndex
            );
            if (!result.updated) {
                setSelectionEnd({ row: nextRowIndex, cell: nextCellIndex });
            }
            return true;
        };

        const ensureShiftAnchor = (): { row: number; cell: number } => {
            const anchor = selectionStart ?? { row: rowIndex, cell: cellIndex };
            if (!selectionStart) {
                setSelectionStart(anchor);
            }
            return anchor;
        };

        const groupHeaderHasSpanInSelection = () =>
            Object.keys(selectedCells).some(rowKey => {
                const r = parseInt(rowKey, 10);
                if (r % 1000 !== 0 || !selectedCells[r]?.[0]) return false;
                const cells = selectedCells[r];
                for (let c = cols.layerStart; c <= multCol; c++) {
                    if (cells[c]) return true;
                }
                return false;
            });

        switch (e.key) {
            case 'ArrowUp':
                e.preventDefault();
                // Shift+カーソルキーの場合は、マウスドラッグと同じパターンで処理
                if (e.shiftKey) {
                    const shiftAnchor = ensureShiftAnchor();
                    // アクティブセル（selectionEnd）の位置を取得
                    const activeRow = selectionEnd?.row ?? rowIndex;
                    const activeCell = selectionEnd?.cell ?? cellIndex;

                    let shiftUpRow = findRowWithCell(activeRow, -1, activeCell);
                    let shiftUpCell = activeCell;
                    if (shiftUpRow === null) {
                        shiftUpRow = findRowWithCell(activeRow, -1, 0);
                        shiftUpCell = 0;
                    } else if (!getCellInTable(shiftUpRow, activeCell)) {
                        shiftUpCell = getCellInTable(shiftUpRow, 0) ? 0 : activeCell;
                    }
                    if (shiftUpRow !== null) {
                        focusAndExtendShiftSelection(shiftUpRow, shiftUpCell, shiftAnchor);
                    }
                } else {
                    // 通常のカーソルキー移動
                    const bounds = getSelectionBounds();
                    const prevRowIndex = rowIndex - 1;
                    
                    // 範囲選択の最上端から上に移動する場合、最上端を単独選択
                    if (bounds && Object.keys(selectedCells).length > 1 && rowIndex === bounds.minRow && prevRowIndex < 0) {
                        // 最上端を含む範囲から上キー → 最上端を単独選択
                        // theadの場合は同じ列、tbodyの場合は適切な列に移動
                        if (bounds.minRow % 1000 === 0) {
                            // theadの場合
                            moveToCell(bounds.minRow, cellIndex <= 2 ? 0 : cellIndex, false);
                        } else {
                            // tbodyの場合
                            moveToCell(bounds.minRow, cellIndex, false);
                        }
                    } else {
                        const prevRow = findRowWithCell(rowIndex, -1, cellIndex);
                        if (prevRow !== null) {
                            const targetCol =
                                isGroupHeaderRowIndex(prevRow) && !getCellInTable(prevRow, cellIndex)
                                    ? 0
                                    : cellIndex;
                            moveToCell(prevRow, targetCol, false);
                        }
                    }
                }
                break;
                
            case 'ArrowDown':
                e.preventDefault();
                // Shift+カーソルキーの場合は、マウスドラッグと同じパターンで処理
                if (e.shiftKey) {
                    const shiftAnchor = ensureShiftAnchor();
                    // アクティブセル（selectionEnd）の位置を取得
                    const activeRow = selectionEnd?.row ?? rowIndex;
                    const activeCell = selectionEnd?.cell ?? cellIndex;

                    let shiftDownRow = findRowWithCell(activeRow, 1, activeCell);
                    let shiftDownCell = activeCell;
                    if (shiftDownRow === null) {
                        shiftDownRow = findRowWithCell(activeRow, 1, 0);
                        shiftDownCell = 0;
                    } else if (!getCellInTable(shiftDownRow, activeCell)) {
                        shiftDownCell = getCellInTable(shiftDownRow, 0) ? 0 : activeCell;
                    }
                    if (shiftDownRow !== null) {
                        focusAndExtendShiftSelection(shiftDownRow, shiftDownCell, shiftAnchor);
                    }
                } else {
                    // 通常のカーソルキー移動
                    const bounds = getSelectionBounds();
                    const nextRow = findRowWithCell(rowIndex, 1, cellIndex);
                    
                    if (isLayerHeaderRow(rowIndex)) {
                        if (nextRow !== null) {
                            moveToCell(nextRow, cellIndex, false);
                        }
                    } else if (bounds && Object.keys(selectedCells).length > 1 && rowIndex === bounds.maxRow && nextRow === null) {
                        // 最下端を含む範囲から下キー → 最下端を単独選択
                        // theadの場合は同じ列、tbodyの場合は適切な列に移動
                        if (bounds.maxRow % 1000 === 0) {
                            // theadの場合
                            moveToCell(bounds.maxRow, cellIndex <= 2 ? 0 : cellIndex, false);
                        } else {
                            // tbodyの場合
                            moveToCell(bounds.maxRow, cellIndex, false);
                        }
                    } else if (nextRow !== null) {
                        moveToCell(nextRow, cellIndex, false);
                    }
                }
                break;
                
            case 'ArrowLeft':
                e.preventDefault();
                // Shift+カーソルキーの場合は、マウスドラッグと同じパターンで処理
                if (e.shiftKey) {
                    const shiftAnchor = ensureShiftAnchor();
                    // アクティブセル（selectionEnd）の位置を取得
                    const activeRow = selectionEnd?.row ?? rowIndex;
                    const activeCell = selectionEnd?.cell ?? cellIndex;
                    
                    // 選択範囲を考慮した左移動
                    let nextRowIndex = activeRow;
                    let nextCellIndex = activeCell;
                    
                    if (groupHeaderHasSpanInSelection()) {
                        if (activeCell > multCol) {
                            nextCellIndex = multCol;
                        } else if (activeCell > 0) {
                            nextCellIndex = 0;
                        }
                    } else {
                        const isActiveGroupHeader = isGroupHeaderRowIndex(activeRow);
                        if (isActiveGroupHeader && activeCell === multCol) {
                            nextCellIndex = 0;
                        } else {
                            const prevCol = findColWithCell(activeRow, activeCell, -1, maxNavCellIndex);
                            if (prevCol !== null) nextCellIndex = prevCol;
                        }
                    }
                    
                    const nextCell = getCellInTable(nextRowIndex, nextCellIndex);
                    if (nextCell) {
                        nextCell.focus();
                        nextCell.scrollIntoView({ block: 'nearest', inline: 'nearest' });
                        const result = calculateSelectionRange(
                            shiftAnchor.row,
                            shiftAnchor.cell,
                            nextRowIndex,
                            nextCellIndex
                        );
                        if (!result.updated) {
                            setSelectionEnd({row: nextRowIndex, cell: nextCellIndex});
                        }
                    }
                } else {
                    const bounds = getSelectionBounds();
                    if (bounds && Object.keys(selectedCells).length > 1 && bounds.minCol === 0 && cellIndex === bounds.minCol) {
                        moveToCell(rowIndex, 0, false);
                    } else {
                        const prevCol = findColWithCell(rowIndex, cellIndex, -1, maxNavCellIndex);
                        if (prevCol !== null) {
                            moveToCell(rowIndex, prevCol, false);
                        }
                    }
                }
                break;
                
            case 'ArrowRight':
                e.preventDefault();
                // Shift+カーソルキーの場合は、マウスドラッグと同じパターンで処理
                if (e.shiftKey) {
                    const shiftAnchor = ensureShiftAnchor();
                    // アクティブセル（selectionEnd）の位置を取得
                    const activeRow = selectionEnd?.row ?? rowIndex;
                    const activeCell = selectionEnd?.cell ?? cellIndex;
                    
                    // 選択範囲を考慮した右移動
                    let nextRowIndex = activeRow;
                    let nextCellIndex = activeCell;
                    
                    if (groupHeaderHasSpanInSelection()) {
                        if (activeCell < multCol) {
                            nextCellIndex = multCol;
                        } else if (activeCell < maxNavCellIndex) {
                            nextCellIndex = activeCell + 1;
                        }
                    } else {
                        const isActiveCellInThead = activeRow % 1000 === 0;
                        if (isActiveCellInThead && activeCell === 0) {
                            nextCellIndex = multCol;
                        } else if (activeCell < maxNavCellIndex) {
                            nextCellIndex = activeCell + 1;
                        }
                    }
                    
                    const nextCell = getCellInTable(nextRowIndex, nextCellIndex);
                    if (nextCell) {
                        nextCell.focus();
                        nextCell.scrollIntoView({ block: 'nearest', inline: 'nearest' });
                        const result = calculateSelectionRange(
                            shiftAnchor.row,
                            shiftAnchor.cell,
                            nextRowIndex,
                            nextCellIndex
                        );
                        if (!result.updated) {
                            setSelectionEnd({row: nextRowIndex, cell: nextCellIndex});
                        }
                    }
                } else {
                    // 通常のカーソルキー移動
                    const bounds = getSelectionBounds();
                    
                    // 範囲選択の最右端から右に移動する場合、最右端を単独選択
                    if (bounds && Object.keys(selectedCells).length > 1 && bounds.maxCol === maxNavCellIndex && cellIndex === bounds.maxCol) {
                        // 最右端を含む範囲から右キー → 最右端を単独選択
                        moveToCell(rowIndex, maxNavCellIndex, false);
                    } else {
                        const nextCol = findColWithCell(rowIndex, cellIndex, 1, maxNavCellIndex);
                        if (nextCol !== null) {
                            moveToCell(rowIndex, nextCol, false);
                        }
                    }
                }
                break;
                
            case 'Enter':
                e.preventDefault();
                if (e.shiftKey) {
                    const prevRow = findRowWithCell(rowIndex, -1, cellIndex) ?? findRowWithCell(rowIndex, -1, 0);
                    const prevCol =
                        prevRow !== null && getCellInTable(prevRow, cellIndex)
                            ? cellIndex
                            : 0;
                    if (prevRow !== null) {
                        moveToCell(prevRow, prevCol, false);
                    }
                } else {
                    const nextRow = findRowWithCell(rowIndex, 1, cellIndex) ?? findRowWithCell(rowIndex, 1, 0);
                    const nextCol =
                        nextRow !== null && getCellInTable(nextRow, cellIndex)
                            ? cellIndex
                            : 0;
                    if (nextRow !== null) {
                        moveToCell(nextRow, nextCol, false);
                    }
                }
                break;
                
            case 'Tab':
                e.preventDefault();
                if (e.shiftKey) {
                    if (cellIndex === 0) {
                        const prevRow = findRowWithCell(rowIndex, -1, maxNavCellIndex);
                        if (prevRow !== null) {
                            moveToCell(prevRow, maxNavCellIndex, false);
                        }
                    } else {
                        const prevCol = findColWithCell(rowIndex, cellIndex, -1, maxNavCellIndex);
                        if (prevCol !== null) {
                            moveToCell(rowIndex, prevCol, false);
                        }
                    }
                } else {
                    if (cellIndex === maxNavCellIndex) {
                        const nextRow = findRowWithCell(rowIndex, 1, 0);
                        if (nextRow !== null) {
                            moveToCell(nextRow, 0, false);
                        }
                    } else {
                        const nextCol = findColWithCell(rowIndex, cellIndex, 1, maxNavCellIndex);
                        if (nextCol !== null) {
                            moveToCell(rowIndex, nextCol, false);
                        }
                    }
                }
                break;
        }
    };

    const groupHeaderNavCellProps = (row: number, col: number) => ({
        onKeyDown: handleCellKeyDown,
        onFocus: () => handleCellFocus(row, col),
        onClick: (e: React.MouseEvent<HTMLTableCellElement>) => {
            if (consumeIgnoredClick()) return;
            if (editingCell) {
                handleEditComplete();
                return;
            }
            e.currentTarget.focus();
            setSelectionStart({ row, cell: col });
            setSelectionEnd({ row, cell: col });
        },
        onMouseDown: (e: React.MouseEvent<HTMLTableCellElement>) => {
            e.currentTarget.focus();
        },
    });

    /** パウチ内ツールチップと同様、ホバー要素が変わるたび tier を 1 からやり直し */
    const handleTableGuideMouseOver = useCallback(
        (e: React.MouseEvent<HTMLElement>) => {
            if (isTouchDevice) {
                return;
            }
            const anchorKey = getTableGuideHoverAnchorKey(e.target);
            if (anchorKey == null) {
                return;
            }
            if (tableGuideHoverAnchorRef.current === anchorKey) {
                return;
            }
            tableGuideHoverAnchorRef.current = anchorKey;
            setTableGuideHoverTier(1);
            if (tableGuideHoverTimerRef.current !== null) {
                window.clearTimeout(tableGuideHoverTimerRef.current);
                tableGuideHoverTimerRef.current = null;
            }
            if (_isGuideEnabled) {
                tableGuideHoverTimerRef.current = window.setTimeout(() => {
                    setTableGuideHoverTier(2);
                    tableGuideHoverTimerRef.current = null;
                }, GUIDE_HOVER_DELAY_MS);
            }
        },
        [isTouchDevice, GUIDE_HOVER_DELAY_MS, _isGuideEnabled],
    );

    const handleTableComponentMouseLeave = useCallback(() => {
        if (tableGuideHoverTimerRef.current !== null) {
            window.clearTimeout(tableGuideHoverTimerRef.current);
            tableGuideHoverTimerRef.current = null;
        }
        tableGuideHoverAnchorRef.current = null;
        setTableGuideHoverTier(0);
    }, []);

    useEffect(() => {
        return () => {
            if (tableGuideHoverTimerRef.current !== null) {
                window.clearTimeout(tableGuideHoverTimerRef.current);
            }
        };
    }, []);

    return (
        <div className="table-component" 
            data-hovered-group={hoveredGroup} 
            data-hovered-pouch={hoveredPouch}
            data-selected-groups={Object.keys(selectedGroups).join(',')}
            data-selected-pouches={Object.keys(selectedPouches).join(',')}
            onMouseOver={handleTableGuideMouseOver}
            onMouseLeave={handleTableComponentMouseLeave}
        >
            <div className="table-header">
                <button className="table-button clear-button"
                 tabIndex={-1}
                 data-tooltip-id="tt" data-tooltip-html={formatTooltip(`Click: ${clearSelectedCells(Object.values(selectedCells).reduce((n, row) => n + Object.keys(row).length, 0), locale)}`)}
                 onClick={handleClearButtonActivate}
                 onTouchEnd={handleClearButtonActivate}
                 onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                 }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6m3 0V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <line x1="10" y1="11" x2="10" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <line x1="14" y1="11" x2="14" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {appCopy.table.clear}
                </button>
                <button className={`table-button copy-button ${copySuccess ? 'copied' : ''}`}
                 tabIndex={-1}
                 data-tooltip-id="tt" data-tooltip-html={formatTooltip(`Click: ${copySelectedCells(Object.values(selectedCells).reduce((n, row) => n + Object.keys(row).length, 0), locale)}`)}
                 onClick={(e) => {
                    handleSelectionCopy(e as React.PointerEvent<HTMLButtonElement>);
                 }}
                 onTouchEnd={(e) => {
                    handleSelectionCopy(e);
                 }}
                 onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                 }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 4v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7.242a2 2 0 0 0-.602-1.43L16.083 2.57A2 2 0 0 0 14.685 2H10a2 2 0 0 0-2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M16 18v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {copySuccess ? appCopy.table.copied : appCopy.table.copy}
                </button>
                <button className={`table-button paste-button ${pasteSuccess ? 'pasted' : ''}`}
                 tabIndex={-1}
                 data-tooltip-id="tt" data-tooltip-html={formatTooltip(`Click: ${pasteSelectedCells(Object.values(selectedCells).reduce((n, row) => n + Object.keys(row).length, 0), locale)}`)}
                 onClick={(e) => {
                    handlePasteButtonClick(e as React.PointerEvent<HTMLButtonElement>);
                 }}
                 onTouchStart={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                 }}
                 onTouchEnd={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handlePasteButtonClick(e);
                 }}
                 onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                 }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15 2H9a1 1 0 0 0-1 1v2c0 .6.4 1 1 1h6c.6 0 1-.4 1-1V3a1 1 0 0 0-1-1z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M8 4H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {pasteSuccess ? appCopy.table.pasted : appCopy.table.paste}
                </button>
                <button 
                    className="excel-export-button"
                    data-tooltip-id="tt" data-tooltip-html={formatTooltip(`Click: ${tooltipCopy.exportWholeTableToExcel}`)}
                    tabIndex={-1}
                    onClick={exportToExcel}
                    onTouchEnd={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        exportToExcel();
                    }}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M14 3v4a1 1 0 0 0 1 1h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" stroke="currentColor" strokeWidth="2"/>
                        <path d="M8 12l3 5 3-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {appCopy.table.xlsx}
                </button>
            </div>
            <div
                className="table-container layers-scroll-x"
                ref={tableScrollRef as React.RefObject<HTMLDivElement>}
                onKeyDown={e => {
                    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
                    const active = document.activeElement;
                    if (!active?.closest('.pouch-table')) return;
                    if (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA') return;
                    e.preventDefault();
                }}
            >
                <table 
                    className={`pouch-table${largeValuesFlags.hasLargeLayerCol ? ' has-large-values-layer' : ''}${largeValuesFlags.hasLargeMultiplierCol ? ' has-large-values-mult' : ''}${largeValuesFlags.hasLargeTotalCol ? ' has-large-values-total' : ''}`}
                    ref={tableRef}
                    onMouseDown={handleMouseDown}
                    onMouseUp={handleMouseUp}
                    onMouseMove={handleMouseMove}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    onKeyDown={handleSelectionCopy}
                    onContextMenu={handleContextMenu}
                >
                    {currentSheet && sheetLayers.length > 0 && (
                        <SheetLayerTableHeader
                            sheet={currentSheet}
                            layers={sheetLayers}
                            cols={cols}
                            rowIndex={LAYER_HEADER_ROW_INDEX}
                            isOpen={isOpen}
                            editingCell={editingCell}
                            editValue={editValue}
                            setEditValue={setEditValue}
                            editInputRef={editInputRef}
                            isComposing={isComposing}
                            setIsComposing={setIsComposing}
                            getCellSelectionClasses={getCellSelectionClasses}
                            handleCellFocus={handleCellFocus}
                            handleCellKeyDown={handleCellKeyDown}
                            handleCellKeyDownForEdit={handleCellKeyDownForEdit}
                            handleEditComplete={handleEditComplete}
                            handleEditCancel={handleEditCancel}
                            handleDoubleClick={handleDoubleClick}
                            handleDoubleTouch={handleDoubleTouch}
                            onSelectLayer={onSetActiveLayer}
                            onAddLayer={handleAddLayerClick}
                            onRemoveLayer={onRemoveSheetLayer}
                            scrollContainerRef={tableScrollRef}
                            setSelectionStart={setSelectionStart}
                            setSelectionEnd={setSelectionEnd}
                            consumeIgnoredClick={consumeIgnoredClick}
                        />
                    )}
                    {groupData.map((group, groupIndex) => (
                    <React.Fragment key={group.id}>
                        <thead className={groupTotalStates.get(group.id) ? 'total-group' : ''}>
                        <tr 
                            style={{ '--group-color': group.id === 'ungrouped' ? '#CCC' : GROUP_COLORS.find(c => c.id === group.color)?.value } as React.CSSProperties}
                            className="group-header"
                            data-row-index={groupIndex * 1000}
                            data-group-id={group.id}
                            onMouseEnter={() => handleGroupHeaderMouseEnter(group.id)}
                            onMouseLeave={handleGroupHeaderMouseLeave}
                        >
                            <th 
                                className={`layer-col-sticky table-col-title ${group.id === 'ungrouped' ? 'ungrouped' : ''} ${getCellSelectionClasses(groupIndex * 1000, 0)} ${(() => {
                                    // 計算エリアと同じロジックでプレースホルダー表示を判定
                                    const correspondingGroup = groups.find(g => g.id === group.id);
                                    if (!correspondingGroup) return '';
                                    
                                    if (correspondingGroup.title && correspondingGroup.title.trim() !== '') {
                                        return ''; // タイトルがある場合はプレースホルダーではない
                                    }
                                    
                                    // プレースホルダーが表示される場合はshow-placeholderクラスを追加
                                    return 'show-placeholder';
                                })()}`}
                                tabIndex={isOpen ? 4000 : -1}
                                data-cell-index={0}
                                data-group-id={group.id}
                                {...(group.id !== 'ungrouped' ? cellTooltipDataProps(groupIndex * 1000, 0) : {})}
                                onKeyDown={(e) => {
                                    // 編集中の場合は何もしない（入力フィールドで処理）
                                    if (editingCell) return;
                                    
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        // 編集モードに入る（実際のタイトルのみを使用）
                                        const correspondingGroup = groups.find(g => g.id === group.id);
                                        const currentValue = correspondingGroup?.title || '';
                                        handleEditStart(groupIndex * 1000, 0, currentValue);
                                    } else {
                                        const correspondingGroup = groups.find(g => g.id === group.id);
                                        handleCellKeyDownForEdit(e, groupIndex * 1000, 0, correspondingGroup?.title || '');
                                    }
                                    // 他のキー処理は必要に応じて追加
                                }}
                                onFocus={() => handleCellFocus(groupIndex * 1000, 0)}
                                onClick={(e) => {
                                    // 編集中の場合は値を確定してから移動
                                    if (consumeIgnoredClick()) return;
                                    if (editingCell) {
                                        handleEditComplete();
                                        return; // 編集完了後は他の処理をスキップ
                                    }
                                    // クリック時にフォーカスする
                                    e.currentTarget.focus();
                                    // クリックと同様の状態にする
                                    setSelectionStart({row: groupIndex * 1000, cell: 0});
                                    setSelectionEnd({row: groupIndex * 1000, cell: 0});
                                }}
                                onMouseDown={(e) => {
                                    e.currentTarget.focus();
                                }}
                                onDoubleClick={(e) => {
                                    // Ungroupedグループは編集不可
                                    if (group.id !== 'ungrouped') {
                                        const correspondingGroup = groups.find(g => g.id === group.id);
                                        const currentValue = correspondingGroup?.title || '';
                                        handleDoubleClick(e, groupIndex * 1000, 0, currentValue);
                                    }
                                }}
                                onTouchEnd={(e) => {
                                    // タッチイベントのダブルタップ検出は複雑なので、ここでは単純化
                                    if (e.changedTouches.length === 1) {
                                        const touch = e.changedTouches[0];
                                        const currentTime = Date.now();
                                        const lastTouchTime = (e.target as any)._lastTouchTime || 0;
                                        
                                        if (currentTime - lastTouchTime < 300) {
                                            // ダブルタップとして扱う（Ungroupedグループは編集不可）
                                            if (group.id !== 'ungrouped') {
                                                const correspondingGroup = groups.find(g => g.id === group.id);
                                                const currentValue = correspondingGroup?.title || '';
                                                handleDoubleTouch(e, groupIndex * 1000, 0, currentValue);
                                            }
                                        }
                                        (e.target as any)._lastTouchTime = currentTime;
                                    }
                                }}
                            >
                                <span 
                                    className={`toggle-icon ${expandedGroups.has(group.id) ? 'expanded' : 'collapsed'}`}
                                    onClick={(e) => handleToggleIconClick(e, group.id)}
                                    onTouchEnd={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleToggleIconClick(e as unknown as React.MouseEvent, group.id);
                                    }}
                                >
                                    <svg viewBox="0 0 24 24" width="20" height="20">
                                        <path d={expandedGroups.has(group.id) ? "M7 10l5 5 5-5z" : "M10 7l5 5-5 5z"} />
                                    </svg>
                                </span>
                                {(() => {
                                const isEditingGroupTitle =
                                    editingCell?.row === groupIndex * 1000 &&
                                    editingCell?.cell === 0 &&
                                    group.id !== 'ungrouped';
                                const groupTitleLabel =
                                    group.id === 'ungrouped'
                                        ? group.groupTitle
                                        : group.groupTitle ||
                                          (() => {
                                              const correspondingGroup = groups.find(g => g.id === group.id);
                                              return correspondingGroup && groupTotalStates.get(group.id) && currentSheet
                                                  ? currentSheet.title
                                                  : group.placeholderText;
                                          })() ||
                                          appCopy.table.groupN(groupIndex + 1);
                                return (
                                <TableCellEditShell
                                    isEditing={isEditingGroupTitle}
                                    measure={<span className="group-title-text">{groupTitleLabel}</span>}
                                >
                                {isEditingGroupTitle ? (
                                    <input
                                        ref={editInputRef}
                                        type="text"
                                        className="table-cell-edit-input"
                                        value={editValue}
                                        onChange={(e) => setEditValue(e.target.value)}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onMouseUp={(e) => e.stopPropagation()}
                                        onClick={(e) => e.stopPropagation()}
                                        onTouchStart={(e) => e.stopPropagation()}
                                        onTouchEnd={(e) => e.stopPropagation()}
                                        onKeyDown={(e) => {
                                            // IME入力中は何もしない（確定を待つ）
                                            if (isComposing) {
                                                return;
                                            }

                                            // Backspace/Deleteキーは通常の文字削除として機能させる
                                            if (e.key === 'Backspace' || e.key === 'Delete') {
                                                e.stopPropagation();
                                                return;
                                            }

                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleEditComplete();
                                                // 編集完了後に下のセルに移動
                                                setTimeout(() => {
                                                    moveToNextRowCell(groupIndex * 1000, 0);
                                                }, 0);
                                            } else if (e.key === 'Enter' && e.shiftKey) {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleEditComplete();
                                                // 編集完了後に上のセルに移動（編集開始しない）
                                                setTimeout(() => {
                                                    const targetCell = document.querySelector(`tr[data-row-index="${groupIndex * 1000}"] [data-cell-index="0"]`) as HTMLElement;
                                                    if (targetCell) {
                                                        targetCell.focus();
                                                        // handleCellKeyDownのShift+Enterロジックを直接呼び出す
                                                        handleCellKeyDown({
                                                            key: 'Enter',
                                                            shiftKey: true,
                                                            preventDefault: () => {},
                                                            stopPropagation: () => {},
                                                            currentTarget: targetCell
                                                        } as any);
                                                    }
                                                }, 0);
                                            } else if (e.key === 'Tab' && !e.shiftKey) {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleEditComplete();
                                                // 右のセルに移動（編集状態にしない）
                                                moveToCellWithoutEdit(groupIndex * 1000, 1);
                                            } else if (e.key === 'Tab' && e.shiftKey) {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleEditComplete();
                                                // 編集完了後に左のセルに移動（編集状態にしない）
                                                // cellIndex=0なので、前の行の最終列へ
                                                setTimeout(() => {
                                                    const currentRowIndex = groupIndex * 1000;
                                                    const prevRowIndex = currentRowIndex - 1;
                                                    const prevCell = document.querySelector(`tr[data-row-index="${prevRowIndex}"] [data-cell-index="${totalCell}"]`);
                                                    
                                                    if (prevCell) {
                                                        // 通常の前の行の最終列に移動
                                                        moveToCellWithoutEdit(prevRowIndex, totalCell);
                                                    } else if (groupIndex > 0) {
                                                        // 前のグループのtbodyの最終行を探す
                                                        const prevGroupIndex = groupIndex - 1;
                                                        let prevGroupLastRowIndex = currentRowIndex - 1;
                                                        
                                                        // 前のグループのtbodyの最初の行まで逆順に探す
                                                        while (prevGroupLastRowIndex >= prevGroupIndex * 1000 + 1) {
                                                            const testCell = document.querySelector(`tr[data-row-index="${prevGroupLastRowIndex}"] [data-cell-index="${totalCell}"]`);
                                                            if (testCell) {
                                                                moveToCellWithoutEdit(prevGroupLastRowIndex, totalCell);
                                                                break;
                                                            }
                                                            prevGroupLastRowIndex--;
                                                        }
                                                    }
                                                }, 0);
                                            } else if (e.key === 'Escape') {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleEditCancel();
                                            } else if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                                                // カーソルキーはセル移動に伝播しないように停止
                                                e.stopPropagation();
                                            }
                                        }}
                                        onBlur={handleEditComplete}
                                        onCompositionStart={(e) => {
                                            setIsComposing(true);
                                        }}
                                        onCompositionEnd={() => {
                                            setIsComposing(false);
                                        }}
                                        autoFocus
                                    />
                                ) : null}
                                </TableCellEditShell>
                                );
                                })()}
                            </th>
                            {sheetLayers.length > 1 ? (
                              <>
                                {sheetLayers.map((layer, li) => {
                                  const ghCol = cols.layerStart + li;
                                  return (
                                  <th
                                    key={`gh-${group.id}-layer-${li}`}
                                    className={`table-col-layer ${getCellSelectionClasses(groupIndex * 1000, ghCol)}${layer.id === activeLayerId ? ' layer-column-active' : ''}`}
                                    tabIndex={isOpen ? 4000 : -1}
                                    data-cell-index={ghCol}
                                    data-group-id={group.id}
                                    {...groupHeaderNavCellProps(groupIndex * 1000, ghCol)}
                                  />
                                  );
                                })}
                                <th
                                  className={`table-col-mult-op ${getCellSelectionClasses(groupIndex * 1000, cols.mult)}`}
                                  tabIndex={isOpen ? 4000 : -1}
                                  data-cell-index={cols.mult}
                                  data-group-id={group.id}
                                  {...groupHeaderNavCellProps(groupIndex * 1000, cols.mult)}
                                />
                              </>
                            ) : (
                              <>
                                <th
                                  className={`table-col-layer ${getCellSelectionClasses(groupIndex * 1000, 1)}`}
                                  tabIndex={isOpen ? 4000 : -1}
                                  data-cell-index={1}
                                  data-group-id={group.id}
                                  onKeyDown={handleCellKeyDown}
                                  onFocus={() => handleCellFocus(groupIndex * 1000, 1)}
                                  onClick={(e) => {
                                    if (consumeIgnoredClick()) return;
                                    if (editingCell) {
                                      handleEditComplete();
                                      return;
                                    }
                                    e.currentTarget.focus();
                                    setSelectionStart({ row: groupIndex * 1000, cell: 1 });
                                    setSelectionEnd({ row: groupIndex * 1000, cell: 1 });
                                  }}
                                />
                                <th
                                  className={`table-col-mult-op ${getCellSelectionClasses(groupIndex * 1000, 2)}`}
                                  tabIndex={isOpen ? 4000 : -1}
                                  data-cell-index={2}
                                  data-group-id={group.id}
                                  onKeyDown={handleCellKeyDown}
                                  onFocus={() => handleCellFocus(groupIndex * 1000, 2)}
                                  onClick={(e) => {
                                    if (consumeIgnoredClick()) return;
                                    if (editingCell) {
                                      handleEditComplete();
                                      return;
                                    }
                                    e.currentTarget.focus();
                                    setSelectionStart({ row: groupIndex * 1000, cell: 2 });
                                    setSelectionEnd({ row: groupIndex * 1000, cell: 2 });
                                  }}
                                />
                              </>
                            )}
                            <td 
                                className={`table-col-count ${getCellSelectionClasses(groupIndex * 1000, countCell)}`}
                                tabIndex={isOpen ? 4000 : -1}
                                data-cell-index={countCell}
                                data-group-id={group.id}
                                onKeyDown={handleCellKeyDown}
                                onFocus={() => handleCellFocus(groupIndex * 1000, 3)}
                                onClick={(e) => {
                                    // 編集中の場合は値を確定してから移動
                                    if (consumeIgnoredClick()) return;
                                    if (editingCell) {
                                        handleEditComplete();
                                        return; // 編集完了後は他の処理をスキップ
                                    }
                                    e.currentTarget.focus();
                                    setSelectionStart({row: groupIndex * 1000, cell: countCell});
                                    setSelectionEnd({row: groupIndex * 1000, cell: countCell});
                                }}
                            >
                                <NumberCounter 
                                    value={group.count}
                                    className="calcvalue"
                                    animateFromZero={isInitialLoading || isSheetSwitching}
                                    duration={(isInitialLoading || isSheetSwitching) ? 600 : 300}
                                />
                            </td>
                            <td 
                                className={`table-col-total ${getCellSelectionClasses(groupIndex * 1000, totalCell)}`}
                                tabIndex={isOpen ? 4000 : -1}
                                data-cell-index={totalCell}
                                data-group-id={group.id}
                                onKeyDown={handleCellKeyDown}
                                onFocus={() => handleCellFocus(groupIndex * 1000, 4)}
                                onClick={(e) => {
                                    // 編集中の場合は値を確定してから移動
                                    if (consumeIgnoredClick()) return;
                                    if (editingCell) {
                                        handleEditComplete();
                                        return; // 編集完了後は他の処理をスキップ
                                    }
                                    e.currentTarget.focus();
                                    setSelectionStart({row: groupIndex * 1000, cell: totalCell});
                                    setSelectionEnd({row: groupIndex * 1000, cell: totalCell});
                                }}
                            >
                                {(() => {
                                    const members = pouches.filter(
                                        p =>
                                            group.pouches?.includes(p.id) &&
                                            !p._isDeleting &&
                                            !p.isGroupTotalPouch &&
                                            p.id !== currentSheet?.balancingPouchId &&
                                            !groups.some(
                                                g => g.color === 'mute' && g.pouches?.includes(p.id)
                                            )
                                    );
                                    const shouldShowTime =
                                        members.length > 0 && members.every(p => p.isTime === true);
                                    
                                    if (shouldShowTime) {
                                        // 時間表示
                                        const totalMinutes = Math.round(Math.abs(group.total));
                                        const hours = Math.floor(totalMinutes / 60);
                                        const minutes = totalMinutes % 60;
                                        const sign = group.total < 0 ? '-' : '';
                                        return (
                                            <span className="time-display">
                                                {`${sign}${hours}:${minutes.toString().padStart(2, '0')}`}
                                            </span>
                                        );
                                    } else {
                                        // 通常のNumberCounter
                                        return (
                                            <NumberCounter 
                                                value={Math.abs(group.total)}
                                                isNegative={group.total < 0}
                                                className="calcvalue"
                                                animateFromZero={isInitialLoading || isSheetSwitching}
                                                duration={(isInitialLoading || isSheetSwitching) ? 600 : 300}
                                                groupId={group.id}
                                            />
                                        );
                                    }
                                })()}
                            </td>
                        </tr>
                        {group.id !== 'ungrouped' && (
                        <tr
                            className="group-color-bar"
                            data-group-id={group.id}
                            style={{ '--group-color': GROUP_COLORS.find(c => c.id === group.color)?.value } as React.CSSProperties}
                            onClick={(e) => {
                                e.stopPropagation();
                                onChangeGroupColor(group.id, e.shiftKey, false, true);
                            }}
                            onTouchEnd={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                onChangeGroupColor(group.id, false, false, true);
                            }}
                            {...tooltipPropsIfContent(
                                (() => {
                                    const colors = GROUP_COLORS.map(c => ({ id: c.id, value: c.value }));
                                    const currentIndex = colors.findIndex(c => c.id === group.color);
                                    const idx = currentIndex >= 0 ? currentIndex : 0;
                                    const nextIndex = (idx + 1) % colors.length;
                                    const prevIndex = (idx - 1 + colors.length) % colors.length;
                                    const sym = (color: string, ch: string) =>
                                        `<span style="color:${color};font-size:1.3em;line-height:1;vertical-align:-0.05em">${ch}</span>`;
                                    return [
                                        formatKeyDescLine('Click', `Change Group Color to ${sym(colors[nextIndex].value, '■')}`),
                                        formatKeysLeftWithDesc(
                                            formatTooltip(getModifierKeys().shift + '+Click'),
                                            `Change Group Color to ${sym(colors[prevIndex].value, '■')}`,
                                        ),
                                    ]
                                        .filter(Boolean)
                                        .join('<br/>');
                                })(),
                            )}
                        >
                            <th colSpan={cols.colCount}></th>
                        </tr>
                        )}
                        </thead>
                        {group.rows.length > 0 && (
                        <tbody className={expandedGroups.has(group.id) ? 'expanded' : 'collapsed'}>
                        {group.rows.map((row, rowIndex) => {
                            // グループごとの一意のID + 行インデックス
                            const uniqueRowIndex = groupIndex * 1000 + rowIndex + 1;
                            // 小計値を計算（isNegativeを考慮、GTPの場合は表示値ベース）
                            const actualTotal = row.total;
                            const pouch = filteredPouches.find(p => p.id === row.pouchId);

                            const grp = filteredGroups.find(gr => gr.id === group.id);
                            const dimDivisionInclusiveRows =
                                !!grp &&
                                groupHasDivisionInclusiveGtp(grp, filteredPouches) &&
                                !row.isGroupTotalPouch;

                            return (
                            <tr 
                                key={`${group.groupTitle}-${rowIndex}`} 
                                className={`${row.isNegative ? 'negative' : ''} ${row.isGroupTotalPouch ? 'group-total-pouch' : ''} ${dimDivisionInclusiveRows ? 'division-inclusive-dim' : ''} ${row.isGroupTotalPouch && row.pouchId ? (() => {
                                    const pouch = filteredPouches.find(p => p.id === row.pouchId);
                                    let classes = [];
                                    if (pouch && isGtpIncludesSelf(pouch, group.id, groups)) {
                                        classes.push('includes-self');
                                    }
                                    if (pouch && pouch.multiplier < 0) {
                                        classes.push('division-mode');
                                    }
                                    if (pouch && pouch.oppositeToTotal) {
                                        classes.push('opposite');
                                    }
                                    return classes.join(' ');
                                })() : ''}`}
                                style={{ 
                                    '--pouch-color': POUCH_COLORS.find(c => c.id === row.color)?.value,
                                    ...(row.isGroupTotalPouch && row.pouchId && (() => {
                                        // GTPの参照元グループの色を取得
                                        const pouch = filteredPouches.find(p => p.id === row.pouchId);
                                        if (pouch?.isGroupTotalPouch && pouch.referencedGroupId) {
                                            const referencedGroup = groups.find(g => g.id === pouch.referencedGroupId);
                                            if (referencedGroup?.color) {
                                                const groupColorObj = GROUP_COLORS.find(c => c.id === referencedGroup.color);
                                                return groupColorObj ? { '--group-color': groupColorObj.value } : {};
                                            }
                                        }
                                        return {};
                                    })()),
                                } as any}
                                data-row-index={uniqueRowIndex}
                                data-group-id={group.id}
                                data-pouch-id={row.pouchId}
                                onMouseEnter={() => {
                                    if (row.pouchId) {
                                        setHoveredPouch(row.pouchId);
                                        setHoveredGroup(null);
                                    } else {
                                        setHoveredGroup(group.id);
                                    }
                                }}
                                onMouseLeave={() => {
                                    setHoveredPouch(null);
                                    setHoveredGroup(null);
                                }}
                            >
                                <td 
                                    className={`${(() => {
                                        // 計算エリアと同じ条件でクラスを決定
                                        if (row.pouchTitle && row.pouchTitle.trim() !== '') {
                                            return 'title';
                                        } else if (row.pouchId) {
                                            const pouch = pouches.find(p => p.id === row.pouchId);
                                            if (pouch && !pouch.isGroupTotalPouch && pouch.lastExpression && pouch.lastExpression.trim() !== '') {
                                                return 'placeholder';
                                            }
                                        }
                                        return 'legend';
                                    })()} ${(() => {
                                        // プレースホルダーが表示される場合はshow-placeholderクラスを追加
                                        if (row.pouchTitle && row.pouchTitle.trim() !== '') {
                                            return ''; // タイトルがある場合はプレースホルダーではない
                                        } else {
                                            return 'show-placeholder'; // タイトルがない場合はプレースホルダー
                                        }
                                    })()} ${(() => {
                                        // 計算エリアと同じ条件でshow-expression、expression-outdatedクラスを適用
                                        if (row.pouchId) {
                                            const pouch = pouches.find(p => p.id === row.pouchId);
                                            if (pouch && !pouch.isGroupTotalPouch) {
                                                let classes = '';
                                                const storedExpression = getPouchStoredExpression(
                                                    pouch,
                                                    currentSheet
                                                );
                                                if (storedExpression) {
                                                    classes += ' show-expression';
                                                }
                                                if (
                                                    storedExpression &&
                                                    isPouchExpressionOutdatedForSheet(pouch, currentSheet)
                                                ) {
                                                    classes += ' expression-outdated';
                                                }
                                                return classes;
                                            }
                                        }
                                        return '';
                                    })()} layer-col-sticky table-col-title ${getCellSelectionClasses(uniqueRowIndex, 0)}${row.pouchId && !row.isGroupTotalPouch && currentSheet?.balancingPouchId === row.pouchId ? ' has-balancing-icon' : ''}`}
                                    tabIndex={isOpen ? 4000 : -1}
                                    data-cell-index={0}
                                    data-group-id={group.id}
                                    data-pouch-id={row.pouchId}
                                    {...cellTooltipDataProps(uniqueRowIndex, 0)}
                                    onKeyDown={(e) => {
                                        // 編集中の場合は何もしない（入力フィールドで処理）
                                        if (editingCell) return;
                                        
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            // 編集モードに入る
                                            const currentValue = row.pouchTitle || '';
                                            handleEditStart(uniqueRowIndex, 0, currentValue);
                                        } else {
                                            handleCellKeyDownForEdit(e, uniqueRowIndex, 0, row.pouchTitle || '');
                                        }
                                        // 他のキー処理は必要に応じて追加
                                    }}
                                    onFocus={() => handleCellFocus(uniqueRowIndex, 0)}
                                    onClick={(e) => {
                                        // 編集中の場合は値を確定してから移動
                                        if (consumeIgnoredClick()) return;
                                        if (editingCell) {
                                            handleEditComplete();
                                            return; // 編集完了後は他の処理をスキップ
                                        }
                                        e.currentTarget.focus();
                                        setSelectionStart({row: uniqueRowIndex, cell: 0});
                                        setSelectionEnd({row: uniqueRowIndex, cell: 0});
                                    }}
                                    onMouseDown={(e) => {
                                    e.currentTarget.focus();
                                }}
                                    onDoubleClick={(e) => {
                                        const currentValue = row.pouchTitle || '';
                                        handleDoubleClick(e, uniqueRowIndex, 0, currentValue);
                                    }}
                                    onTouchEnd={(e) => {
                                        // ダブルタップ検出
                                        if (e.changedTouches.length === 1) {
                                            const currentTime = Date.now();
                                            const lastTouchTime = (e.target as any)._lastTouchTime || 0;
                                            
                                            if (currentTime - lastTouchTime < 600) {
                                                const currentValue = row.pouchTitle || '';
                                                handleDoubleTouch(e, uniqueRowIndex, 0, currentValue);
                                            }
                                            (e.target as any)._lastTouchTime = currentTime;
                                        }
                                    }}
                                >
                                    {row.pouchId && !row.isGroupTotalPouch && (
                                        <span
                                            className="color-icon"
                                            {...tooltipPropsIfContent(
                                                (() => {
                                                    const currentIndex = POUCH_COLORS.findIndex(c => c.id === row.color);
                                                    const nextIndex = (currentIndex + 1) % POUCH_COLORS.length;
                                                    const prevIndex = (currentIndex - 1 + POUCH_COLORS.length) % POUCH_COLORS.length;
                                                    const sym = (color: string) =>
                                                        `<span style="color:${color};font-size:1.3em;line-height:1;vertical-align:-0.05em">●</span>`;
                                                    return [
                                                        formatKeyDescLine('Click', `Change Pouch Color to ${sym(POUCH_COLORS[nextIndex].value)}`),
                                                        formatKeysLeftWithDesc(
                                                            formatTooltip(getModifierKeys().shift + '+Click'),
                                                            `Change Pouch Color to ${sym(POUCH_COLORS[prevIndex].value)}`,
                                                        ),
                                                    ]
                                                        .filter(Boolean)
                                                        .join('<br/>');
                                                })(),
                                            )}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                const isShiftPressed = e.shiftKey;
                                                onChangeColor(row.pouchId!, isShiftPressed);
                                            }}
                                            onTouchEnd={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                const isShiftPressed = (window as any).__shiftKeyPressed || false;
                                                onChangeColor(row.pouchId!, isShiftPressed);
                                            }}
                                        />
                                    )}
                                    {(() => {
                                    const isEditingPouchTitle =
                                        editingCell?.row === uniqueRowIndex && editingCell?.cell === 0;
                                    const pouchTitleDisplay = (() => {
                                        // GTPの場合は特別な表示ロジック
                                        if (row.isGroupTotalPouch && row.pouchId) {
                                            const pouch = pouches.find(p => p.id === row.pouchId);
                                            if (pouch) {
                                                const placeholderText = getGtpPlaceholderText(pouch, groups, pouches, currentSheet, appCopy.table.groupN);
                                                if (row.pouchTitle && row.pouchTitle.trim() !== '') {
                                                    return (
                                                        <>
                                                            {row.pouchTitle} <small>({placeholderText} Total)</small>
                                                        </>
                                                    );
                                                }
                                                return `(${placeholderText} Total)`;
                                            }
                                        }
                                        if (row.pouchTitle && row.pouchTitle.trim() !== '') {
                                            return row.pouchTitle;
                                        }
                                        if (row.pouchId) {
                                            const pouch = pouches.find(p => p.id === row.pouchId);
                                            if (pouch) {
                                                if (!pouch.isGroupTotalPouch && pouch.lastExpression && pouch.lastExpression.trim() !== '') {
                                                    return pouch.lastExpression;
                                                }
                                                const colorLabel = getColorLabel(row.color, colorLegends, pouches, appCopy.table.misc);
                                                return colorLabel.text;
                                            }
                                        }
                                        const colorLabel = getColorLabel(row.color, colorLegends, pouches, appCopy.table.misc);
                                        return colorLabel.text;
                                    })();
                                    return (
                                    <TableCellEditShell
                                        isEditing={isEditingPouchTitle}
                                        measure={pouchTitleDisplay}
                                    >
                                    {isEditingPouchTitle ? (
                                        <input
                                            ref={editInputRef}
                                            type="text"
                                            className="table-cell-edit-input"
                                            value={editValue}
                                            onChange={(e) => setEditValue(e.target.value)}
                                            onMouseDown={(e) => e.stopPropagation()}
                                            data-table-editing="true"
                                            onMouseUp={(e) => e.stopPropagation()}
                                            onClick={(e) => e.stopPropagation()}
                                            onTouchStart={(e) => e.stopPropagation()}
                                            onTouchEnd={(e) => e.stopPropagation()}
                                            onKeyDown={(e) => {
                                                // IME入力中は何もしない（確定を待つ）
                                                if (isComposing) {
                                                    return;
                                                }

                                                // Backspace/Deleteキーは通常の文字削除として機能させる
                                                if (e.key === 'Backspace' || e.key === 'Delete') {
                                                    e.stopPropagation();
                                                    return;
                                                }

                                                if (e.key === 'Enter' && !e.shiftKey) {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleEditComplete();
                                                    // 編集完了後に下のセルに移動
                                                    setTimeout(() => {
                                                        moveToNextRowCell(uniqueRowIndex, 0);
                                                    }, 0);
                                                } else if (e.key === 'Enter' && e.shiftKey) {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleEditComplete();
                                                    // 編集完了後に上のセルに移動（編集開始しない）
                                                    setTimeout(() => {
                                                        const targetCell = document.querySelector(`tr[data-row-index="${uniqueRowIndex}"] [data-cell-index="0"]`) as HTMLElement;
                                                        if (targetCell) {
                                                            targetCell.focus();
                                                            handleCellKeyDown({
                                                                key: 'Enter',
                                                                shiftKey: true,
                                                                preventDefault: () => {},
                                                                stopPropagation: () => {},
                                                                currentTarget: targetCell
                                                            } as any);
                                                        }
                                                    }, 0);
                                                } else if (e.key === 'Tab' && !e.shiftKey) {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleEditComplete();
                                                    // 右のセルに移動（編集状態にしない）
                                                    moveToCellWithoutEdit(uniqueRowIndex, 1);
                                                } else if (e.key === 'Tab' && e.shiftKey) {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleEditComplete();
                                                    // 左のセルに移動（編集状態にしない）
                                                    // cellIndex=0なので、前の行の最終列へ
                                                    const prevRowIndex = uniqueRowIndex - 1;
                                                    if (prevRowIndex >= 0) {
                                                        moveToCellWithoutEdit(prevRowIndex, totalCell);
                                                    }
                                                } else if (e.key === 'Escape') {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleEditCancel();
                                                } else if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                                                    // カーソルキーはセル移動に伝播しないように停止
                                                    e.stopPropagation();
                                                }
                                            }}
                                            onBlur={handleEditComplete}
                                            onCompositionStart={(e) => {
                                            setIsComposing(true);
                                        }}
                                            onCompositionEnd={() => {
                                            setIsComposing(false);
                                        }}
                                            autoFocus
                                        />
                                    ) : null}
                                    </TableCellEditShell>
                                    );
                                    })()}
                                    {row.pouchId && !row.isGroupTotalPouch && currentSheet?.balancingPouchId === row.pouchId && !(editingCell?.row === uniqueRowIndex && editingCell?.cell === 0) && (
                                        <span className="table-balancing-pouch-icon" aria-hidden>
                                            <svg viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M128,499.2h256c14.14,0,25.6-11.46,25.6-25.6S398.13,448,384,448H128c-14.14,0-25.6,11.46-25.6,25.6S113.86,499.2,128,499.2"/>
                                                <path d="M106.61,140.45L256,115.55l149.39,24.9c13.95,2.32,27.14-7.1,29.46-21.04c2.32-13.95-7.1-27.14-21.04-29.46l-153.6-25.6c-2.8-0.47-5.61-0.47-8.42,0l-153.6,25.6c-13.95,2.32-23.37,15.51-21.04,29.46C79.47,133.36,92.66,142.78,106.61,140.45L106.61,140.45z"/>
                                                <path d="M230.4,38.4v435.2c0,14.14,11.46,25.6,25.6,25.6c14.14,0,25.6-11.46,25.6-25.6V38.4c0-14.14-11.46-25.6-25.6-25.6C241.86,12.8,230.4,24.26,230.4,38.4"/>
                                                <path d="M202.1,257.35l-76.8-153.6c-4.35-8.69-13.18-14.15-22.9-14.15c-9.72,0-18.55,5.46-22.9,14.15L2.7,257.35c-1.81,3.62-2.7,7.4-2.7,11.45c0.02,56.56,45.84,102.38,102.4,102.4c56.56-0.02,102.38-45.84,102.4-102.4c0-14.14-11.46-25.6-25.6-25.6s-25.6,11.46-25.6,25.6c-0.05,28.27-22.93,51.15-51.2,51.2c-28.27-0.05-51.15-22.93-51.2-51.2H25.6l22.9,11.45l53.9-107.8l53.9,107.8c6.32,12.65,21.7,17.77,34.35,11.45S208.42,270,202.1,257.35L202.1,257.35z"/>
                                                <path d="M509.29,257.35l-76.8-153.6c-4.35-8.69-13.18-14.15-22.9-14.15c-9.72,0-18.55,5.46-22.9,14.15l-76.8,153.6c-1.81,3.62-2.7,7.4-2.7,11.45c0.02,56.56,45.84,102.38,102.4,102.4c56.56-0.02,102.38-45.84,102.4-102.4c0-14.14-11.46-25.6-25.6-25.6c-14.14,0-25.6,11.46-25.6,25.6c-0.05,28.27-22.93,51.15-51.2,51.2c-28.27-0.05-51.15-22.93-51.2-51.2h-25.6l22.9,11.45l53.9-107.8l53.9,107.8c6.32,12.65,21.7,17.77,34.35,11.45C510.49,285.37,515.61,270,509.29,257.35L509.29,257.35z"/>
                                            </svg>
                                        </span>
                                    )}
                                </td>
                                {sheetLayers.length > 1 ? (
                                  <>
                                    {sheetLayers.map((layer, li) => {
                                      const ci = cols.layerStart + li;
                                      const v = row.layerValues?.[layer.id] ?? 0;
                                      const isDisplaying = layer.id === activeLayerId;
                                      const layerValueStr = v.toString();
                                      const valuePouch = row.pouchId
                                        ? pouches.find(p => p.id === row.pouchId)
                                        : undefined;
                                      return (
                                        <td
                                          key={`${row.pouchId}-lv-${layer.id}`}
                                          className={`table-col-layer ${getCellSelectionClasses(uniqueRowIndex, ci)}${isDisplaying ? ' layer-column-active' : ''}`}
                                          tabIndex={isOpen ? 4000 : -1}
                                          data-cell-index={ci}
                                          data-group-id={group.id}
                                          data-pouch-id={row.pouchId}
                                          {...(isDisplaying ? cellTooltipDataProps(uniqueRowIndex, ci) : {})}
                                          onKeyDown={(e) => {
                                            if (editingCell) return;
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                              e.preventDefault();
                                              handleEditStart(uniqueRowIndex, ci, layerValueStr, valuePouch);
                                            } else {
                                              handleCellKeyDownForEdit(e, uniqueRowIndex, ci, layerValueStr);
                                            }
                                          }}
                                          onFocus={() => handleCellFocus(uniqueRowIndex, ci)}
                                          onClick={(e) => {
                                            if (consumeIgnoredClick()) return;
                                            if (editingCell) {
                                              handleEditComplete();
                                              return;
                                            }
                                            e.currentTarget.focus();
                                            setSelectionStart({ row: uniqueRowIndex, cell: ci });
                                            setSelectionEnd({ row: uniqueRowIndex, cell: ci });
                                          }}
                                          onMouseDown={(e) => {
                                            e.currentTarget.focus();
                                          }}
                                          onDoubleClick={(e) => {
                                            handleDoubleClick(e, uniqueRowIndex, ci, layerValueStr, valuePouch);
                                          }}
                                          onTouchEnd={(e) => {
                                            if (e.changedTouches.length !== 1) return;
                                            const host = e.currentTarget as HTMLElement & { _lastTouchTime?: number };
                                            const now = Date.now();
                                            const last = host._lastTouchTime || 0;
                                            if (now - last < 300) {
                                              handleDoubleTouch(e, uniqueRowIndex, ci, layerValueStr, valuePouch);
                                            }
                                            host._lastTouchTime = now;
                                          }}
                                        >
                                          <TableCellEditShell
                                            isEditing={
                                              editingCell?.row === uniqueRowIndex && editingCell?.cell === ci
                                            }
                                            align="right"
                                            measure={
                                              <NumberCounter
                                                value={Math.abs(v)}
                                                isNegative={v < 0}
                                                className="calcvalue"
                                                animateFromZero={isInitialLoading || isSheetSwitching}
                                                duration={(isInitialLoading || isSheetSwitching) ? 600 : 300}
                                              />
                                            }
                                          >
                                            {editingCell?.row === uniqueRowIndex && editingCell?.cell === ci ? (
                                              <PouchValueEditInput
                                                isTouchDevice={isTouchDevice}
                                                kbActive
                                                kbVariant="expression"
                                                isTime={!!valuePouch?.isTime}
                                                value={editValue}
                                                onValueChange={setEditValue}
                                                onConfirm={handleEditComplete}
                                                onCancel={handleEditCancel}
                                                inputRef={editInputRef}
                                                onBlur={handleEditComplete}
                                                onKeyDown={(e) => {
                                                  if (isComposing) return;
                                                  if (e.key === 'Backspace' || e.key === 'Delete') {
                                                    e.stopPropagation();
                                                    return;
                                                  }
                                                  if (e.key === 'Enter' && !e.shiftKey) {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleEditComplete();
                                                    setTimeout(() => {
                                                      moveToNextRowCell(uniqueRowIndex, ci);
                                                    }, 0);
                                                  } else if (e.key === 'Enter' && e.shiftKey) {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleEditComplete();
                                                  } else if (e.key === 'Escape') {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleEditCancel();
                                                  } else if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                                                    e.stopPropagation();
                                                  }
                                                }}
                                                autoFocus
                                                data-table-editing="true"
                                                className="table-cell-edit-input"
                                              />
                                            ) : null}
                                          </TableCellEditShell>
                                        </td>
                                      );
                                    })}
                                  </>
                                ) : (
                                <td 
                                    className={`table-col-layer layer-column-active ${getCellSelectionClasses(uniqueRowIndex, 1)}`}
                                    tabIndex={isOpen ? 4000 : -1}
                                    data-cell-index={1}
                                    data-group-id={group.id}
                                    data-pouch-id={row.pouchId}
                                    {...cellTooltipDataProps(uniqueRowIndex, 1)}
                                    onKeyDown={(e) => {
                                        // 編集中の場合は何もしない（入力フィールドで処理）
                                        if (editingCell) return;
                                        
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            // 編集モードに入る
                                            const currentValue = row.value.toString();
                                            handleEditStart(uniqueRowIndex, 1, currentValue);
                                        } else {
                                            handleCellKeyDownForEdit(e, uniqueRowIndex, 1, row.value.toString());
                                        }
                                        // 他のキー処理は必要に応じて追加
                                    }}
                                    onFocus={() => handleCellFocus(uniqueRowIndex, 1)}
                                    onClick={(e) => {
                                        // 編集中の場合は値を確定してから移動
                                        if (consumeIgnoredClick()) return;
                                        if (editingCell) {
                                            handleEditComplete();
                                            return; // 編集完了後は他の処理をスキップ
                                        }
                                        e.currentTarget.focus();
                                        setSelectionStart({row: uniqueRowIndex, cell: 1});
                                        setSelectionEnd({row: uniqueRowIndex, cell: 1});
                                    }}
                                    onMouseDown={(e) => {
                                        e.currentTarget.focus();
                                    }}
                                    onDoubleClick={(e) => {
                                        const currentValue = row.value.toString();
                                        const pouch = pouches.find(p => p.id === row.pouchId);
                                        handleDoubleClick(e, uniqueRowIndex, 1, currentValue, pouch);
                                    }}
                                    onTouchEnd={(e) => {
                                        // ダブルタップ検出
                                        if (e.changedTouches.length === 1) {
                                            const currentTime = Date.now();
                                            const lastTouchTime = (e.target as any)._lastTouchTime || 0;
                                            
                                            if (currentTime - lastTouchTime < 300) {
                                                const currentValue = row.value.toString();
                                                const pouch = pouches.find(p => p.id === row.pouchId);
                                                handleDoubleTouch(e, uniqueRowIndex, 1, currentValue, pouch);
                                            }
                                            (e.target as any)._lastTouchTime = currentTime;
                                        }
                                    }}
                                >
                                    {(() => {
                                    const isEditingValue =
                                        editingCell?.row === uniqueRowIndex && editingCell?.cell === 1;
                                    const valueDisplay = (
                                        <NumberCounter
                                            value={Math.abs(row.value)}
                                            isNegative={row.value < 0}
                                            className="calcvalue"
                                            animateFromZero={isInitialLoading || isSheetSwitching}
                                            duration={(isInitialLoading || isSheetSwitching) ? 600 : 300}
                                            _animateSignChange={(row as any)._animateSignChange}
                                        />
                                    );
                                    return (
                                    <TableCellEditShell
                                        isEditing={isEditingValue}
                                        align="right"
                                        measure={valueDisplay}
                                    >
                                    {isEditingValue ? (
                                        <PouchValueEditInput
                                            isTouchDevice={isTouchDevice}
                                            kbActive
                                            kbVariant="expression"
                                            isTime={!!pouches.find(p => p.id === row.pouchId)?.isTime}
                                            value={editValue}
                                            onValueChange={setEditValue}
                                            onConfirm={handleEditComplete}
                                            onCancel={handleEditCancel}
                                            inputRef={editInputRef}
                                            className="table-cell-edit-input"
                                            onMouseDown={(e) => e.stopPropagation()}
                                            data-table-editing="true"
                                            onMouseUp={(e) => e.stopPropagation()}
                                            onClick={(e) => e.stopPropagation()}
                                            onTouchStart={(e) => e.stopPropagation()}
                                            onTouchEnd={(e) => e.stopPropagation()}
                                            onKeyDown={(e) => {
                                                if (isComposing) {
                                                    return;
                                                }
                                                if (e.key === 'Backspace' || e.key === 'Delete') {
                                                    e.stopPropagation();
                                                    return;
                                                }
                                                if (e.key === 'Enter' && !e.shiftKey) {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleEditComplete();
                                                    setTimeout(() => {
                                                        moveToNextRowCell(uniqueRowIndex, 1);
                                                    }, 0);
                                                } else if (e.key === 'Enter' && e.shiftKey) {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleEditComplete();
                                                    setTimeout(() => {
                                                        const targetCell = document.querySelector(`tr[data-row-index="${uniqueRowIndex}"] [data-cell-index="1"]`) as HTMLElement;
                                                        if (targetCell) {
                                                            targetCell.focus();
                                                            handleCellKeyDown({
                                                                key: 'Enter',
                                                                shiftKey: true,
                                                                preventDefault: () => {},
                                                                stopPropagation: () => {},
                                                                currentTarget: targetCell
                                                            } as any);
                                                        }
                                                    }, 0);
                                                } else if (e.key === 'Tab' && !e.shiftKey) {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleEditComplete();
                                                    moveToCellWithoutEdit(uniqueRowIndex, 2);
                                                } else if (e.key === 'Tab' && e.shiftKey) {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleEditComplete();
                                                    moveToCellWithoutEdit(uniqueRowIndex, 0);
                                                } else if (e.key === 'Escape') {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleEditCancel();
                                                } else if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                                                    e.stopPropagation();
                                                }
                                            }}
                                            onBlur={handleEditComplete}
                                            onCompositionStart={() => {
                                            setIsComposing(true);
                                        }}
                                            onCompositionEnd={() => {
                                            setIsComposing(false);
                                        }}
                                            autoFocus
                                        />
                                    ) : null}
                                    </TableCellEditShell>
                                    );
                                    })()}
                                </td>
                                )}
                                <td 
                                    className={`table-col-mult-op ${getCellSelectionClasses(uniqueRowIndex, multOpCell)}${
                                        row.roundUnit !== undefined && row.roundUnit > 0 ? ' has-round-unit' : ''
                                    }`}
                                    tabIndex={isOpen ? 4000 : -1}
                                    data-cell-index={multOpCell}
                                    data-group-id={group.id}
                                    data-pouch-id={row.pouchId}
                                    {...cellTooltipDataProps(uniqueRowIndex, multOpCell)}
                                    onKeyDown={(e) => {
                                        // スペースキーでマルチ符号をトグル
                                        if (e.key === ' ') {
                                            e.preventDefault();
                                            if (row.pouchId) {
                                                const pouch = pouches.find(p => p.id === row.pouchId);
                                                if (pouch) {
                                                    // マルチ値が1の場合はトグルしない
                                                    if (Math.abs(pouch.multiplier) === 1) {
                                                        return;
                                                    }
                                                    // マルチプライヤーの正負を転換
                                                    const newMultiplier = -pouch.multiplier;
                                                    onUpdatePouchMultiplier(row.pouchId, newMultiplier);
                                                    
                                                    // ソート順序は保持されるため、値の更新は自動的に反映される
                                                }
                                            }
                                            return;
                                        }
                                        // その他のキーは通常の処理
                                        handleCellKeyDown(e);
                                    }}
                                    onFocus={() => handleCellFocus(uniqueRowIndex, multOpCell)}
                                    onClick={(e) => {
                                        // 編集中の場合は値を確定してから移動
                                        if (consumeIgnoredClick()) return;
                                        if (editingCell) {
                                            handleEditComplete();
                                            return; // 編集完了後は他の処理をスキップ
                                        }
                                        e.currentTarget.focus();
                                        setSelectionStart({row: uniqueRowIndex, cell: multOpCell});
                                        setSelectionEnd({row: uniqueRowIndex, cell: multOpCell});
                                    }}
                                    onMouseDown={(e) => {
                                    e.currentTarget.focus();
                                }}
                                    onDoubleClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (row.pouchId) {
                                            const pouch = pouches.find(p => p.id === row.pouchId);
                                            if (pouch) {
                                                // マルチ値が1の場合はトグルしない
                                                if (Math.abs(pouch.multiplier) === 1) {
                                                    // エラー状態を設定
                                                    if (onSetPouchError && row.pouchId) {
                                                        onSetPouchError(row.pouchId, true);
                                                    }
                                                    return;
                                                }
                                                // マルチプライヤーの正負を転換
                                                const newMultiplier = -pouch.multiplier;
                                                onUpdatePouchMultiplier(row.pouchId, newMultiplier);
                                                
                                                // ソート順序は保持されるため、値の更新は自動的に反映される
                                            }
                                        }
                                    }}
                                    onTouchEnd={(e) => {
                                        // ダブルタッチの判定（簡易版）
                                        const now = Date.now();
                                        const lastTouch = (e.currentTarget as any).lastTouchTime || 0;
                                        if (now - lastTouch < 300 && row.pouchId) {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            const pouch = pouches.find(p => p.id === row.pouchId);
                                            if (pouch) {
                                                // マルチ値が1の場合はトグルしない
                                                if (Math.abs(pouch.multiplier) === 1) {
                                                    // エラー状態を設定
                                                    if (onSetPouchError && row.pouchId) {
                                                        onSetPouchError(row.pouchId, true);
                                                    }
                                                    return;
                                                }
                                                // マルチプライヤーの正負を転換
                                                const newMultiplier = -pouch.multiplier;
                                                onUpdatePouchMultiplier(row.pouchId, newMultiplier);
                                                
                                                // ソート順序は保持されるため、値の更新は自動的に反映される
                                            }
                                        }
                                        (e.currentTarget as any).lastTouchTime = now;
                                    }}
                                >
                                    {(() => {
                                        // GTPの場合は表示値ベースのマルチプライヤーを使用
                                        const pouch = filteredPouches.find(p => p.id === row.pouchId);
                                        const displayMultiplier = pouch ? getDisplayMultiplierForTable(pouch, group.id, groups, currentSheet, activeLayerId) : row.multiplier;
                                        const display = getMultiplierDisplay(displayMultiplier, row.isTime);
                                        return (
                                            <span className="table-mult-op-symbol">
                                                {display.isDivision ? '/' : '×'}
                                            </span>
                                        );
                                    })()}
                                </td>
                                <td
                                    className={`table-col-mult-val ${getCellSelectionClasses(uniqueRowIndex, multValCell)}${
                                        row.roundUnit !== undefined && row.roundUnit > 0 ? ' has-round-unit' : ''
                                    }`}
                                    tabIndex={isOpen ? 4000 : -1}
                                    data-cell-index={multValCell}
                                    data-group-id={group.id}
                                    data-pouch-id={row.pouchId}
                                    {...cellTooltipDataProps(uniqueRowIndex, multValCell)}
                                    onKeyDown={(e) => {
                                        // 編集中の場合は何もしない（入力フィールドで処理）
                                        if (editingCell) return;
                                        
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            // 編集モードに入る（非編集時の表示値を使用）
                                            const pouch = filteredPouches.find(p => p.id === row.pouchId);
                                            const displayMultiplier = pouch ? getDisplayMultiplierForTable(pouch, group.id, groups, currentSheet, activeLayerId) : row.multiplier;
                                            const display = getMultiplierDisplay(displayMultiplier, row.isTime);
                                            const currentValue = display.value.toString();
                                            handleEditStart(uniqueRowIndex, multValCell, currentValue);
                                        } else {
                                            const pouch = filteredPouches.find(p => p.id === row.pouchId);
                                            const displayMultiplier = pouch ? getDisplayMultiplierForTable(pouch, group.id, groups, currentSheet, activeLayerId) : row.multiplier;
                                            const display = getMultiplierDisplay(displayMultiplier, row.isTime);
                                            handleCellKeyDownForEdit(e, uniqueRowIndex, multValCell, display.value.toString());
                                        }
                                    }}
                                    onFocus={() => handleCellFocus(uniqueRowIndex, multValCell)}
                                    onClick={(e) => {
                                        // 編集中の場合は値を確定してから移動
                                        if (consumeIgnoredClick()) return;
                                        if (editingCell) {
                                            handleEditComplete();
                                            return; // 編集完了後は他の処理をスキップ
                                        }
                                        e.currentTarget.focus();
                                        setSelectionStart({row: uniqueRowIndex, cell: multValCell});
                                        setSelectionEnd({row: uniqueRowIndex, cell: multValCell});
                                    }}
                                    onMouseDown={(e) => {
                                        e.currentTarget.focus();
                                    }}
                                    onDoubleClick={(e) => {
                                        const pouch = filteredPouches.find(p => p.id === row.pouchId);
                                        const displayMultiplier = pouch ? getDisplayMultiplierForTable(pouch, group.id, groups, currentSheet, activeLayerId) : row.multiplier;
                                        const display = getMultiplierDisplay(displayMultiplier, row.isTime);
                                        const currentValue = display.value.toString();
                                        handleDoubleClick(e, uniqueRowIndex, multValCell, currentValue);
                                    }}
                                    onTouchEnd={(e) => {
                                        // ダブルタップ検出
                                        if (e.changedTouches.length === 1) {
                                            const currentTime = Date.now();
                                            const lastTouchTime = (e.target as any)._lastTouchTime || 0;
                                            
                                            if (currentTime - lastTouchTime < 300) {
                                                const pouch = filteredPouches.find(p => p.id === row.pouchId);
                                                const displayMultiplier = pouch ? getDisplayMultiplierForTable(pouch, group.id, groups, currentSheet, activeLayerId) : row.multiplier;
                                                const display = getMultiplierDisplay(displayMultiplier, row.isTime);
                                                const currentValue = display.value.toString();
                                                handleDoubleTouch(e, uniqueRowIndex, multValCell, currentValue);
                                            }
                                            (e.target as any)._lastTouchTime = currentTime;
                                        }
                                    }}
                                >
                                    {(() => {
                                    const isEditingMult =
                                        editingCell?.row === uniqueRowIndex && editingCell?.cell === multValCell;
                                    const pouchForMult = filteredPouches.find(p => p.id === row.pouchId);
                                    const displayMultiplier = pouchForMult
                                        ? getDisplayMultiplierForTable(pouchForMult, group.id, groups, currentSheet, activeLayerId)
                                        : row.multiplier;
                                    const multDisplay = getMultiplierDisplay(displayMultiplier, row.isTime);
                                    const multMeasure = (
                                        <NumberCounter
                                            value={multDisplay.value}
                                            className="calcvalue"
                                            animateFromZero={isInitialLoading || isSheetSwitching}
                                            duration={(isInitialLoading || isSheetSwitching) ? 600 : 300}
                                            suffix={multDisplay.showSymbolAfter ? multDisplay.symbol : ''}
                                            bridgePercentModeTransition
                                        />
                                    );
                                    return (
                                    <div className="table-multiplier-cell-inner">
                                    <TableCellEditShell
                                        isEditing={isEditingMult}
                                        align="right"
                                        measure={multMeasure}
                                    >
                                    {isEditingMult ? (
                                        <PouchValueEditInput
                                            isTouchDevice={isTouchDevice}
                                            kbActive
                                            kbVariant="multiplier"
                                            value={editValue}
                                            onValueChange={setEditValue}
                                            onConfirm={handleEditComplete}
                                            onCancel={handleEditCancel}
                                            inputRef={editInputRef}
                                            className="table-cell-edit-input"
                                            onBlur={handleEditComplete}
                                            onKeyDown={(e) => {
                                                if (isComposing) {
                                                    return;
                                                }
                                                if (e.key === 'Backspace' || e.key === 'Delete') {
                                                    e.stopPropagation();
                                                    return;
                                                }
                                                if (e.key === 'Enter' && !e.shiftKey) {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleEditComplete();
                                                    setTimeout(() => {
                                                        moveToNextRowCell(uniqueRowIndex, multValCell);
                                                    }, 0);
                                                } else if (e.key === 'Enter' && e.shiftKey) {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleEditComplete();
                                                    setTimeout(() => {
                                                        const targetCell = document.querySelector(`tr[data-row-index="${uniqueRowIndex}"] [data-cell-index="${multValCell}"]`) as HTMLElement;
                                                        if (targetCell) {
                                                            targetCell.focus();
                                                            handleCellKeyDown({
                                                                key: 'Enter',
                                                                shiftKey: true,
                                                                preventDefault: () => {},
                                                                stopPropagation: () => {},
                                                                currentTarget: targetCell
                                                            } as any);
                                                        }
                                                    }, 0);
                                                } else if (e.key === 'Tab' && !e.shiftKey) {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleEditComplete();
                                                    const nextCellIndex = totalCell;
                                                    if (nextCellIndex < cols.colCount) {
                                                        moveToCellWithoutEdit(uniqueRowIndex, nextCellIndex);
                                                    }
                                                } else if (e.key === 'Tab' && e.shiftKey) {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleEditComplete();
                                                    moveToCellWithoutEdit(uniqueRowIndex, multOpCell);
                                                } else if (e.key === 'Escape') {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleEditCancel();
                                                } else if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                                                    e.stopPropagation();
                                                }
                                            }}
                                            onCompositionStart={() => {
                                            setIsComposing(true);
                                        }}
                                            onCompositionEnd={() => {
                                            setIsComposing(false);
                                        }}
                                            onMouseDown={(e) => e.stopPropagation()}
                                            onMouseUp={(e) => e.stopPropagation()}
                                            onClick={(e) => e.stopPropagation()}
                                            onTouchStart={(e) => e.stopPropagation()}
                                            onTouchEnd={(e) => e.stopPropagation()}
                                            data-table-editing="true"
                                            autoFocus
                                        />
                                    ) : null}
                                    </TableCellEditShell>
                                    {row.pouchId && !isEditingMult && (
                                        <LayerSlideRoundUnit
                                            roundUnit={row.roundUnit}
                                            slideEnabled={useLayerSlide || layerSwitchDirection !== 0}
                                            direction={layerSwitchDirection}
                                            switchNonce={layerSwitchNonce}
                                            className="table-round-unit"
                                            animateFromZero={isInitialLoading || isSheetSwitching}
                                            duration={(isInitialLoading || isSheetSwitching) ? 600 : 300}
                                        />
                                    )}
                                    </div>
                                    );
                                    })()}
                                </td>
                                <td 
                                    className={`table-col-total ${getCellSelectionClasses(uniqueRowIndex, totalCell)}`}
                                    tabIndex={isOpen ? 4000 : -1}
                                    data-cell-index={totalCell}
                                    data-group-id={group.id}
                                    data-pouch-id={row.pouchId}
                                    onKeyDown={handleCellKeyDown}
                                    onFocus={() => handleCellFocus(uniqueRowIndex, totalCell)}
                                    onClick={(e) => {
                                        // 編集中の場合は値を確定してから移動
                                        if (consumeIgnoredClick()) return;
                                        if (editingCell) {
                                            handleEditComplete();
                                            return; // 編集完了後は他の処理をスキップ
                                        }
                                        e.currentTarget.focus();
                                        setSelectionStart({row: uniqueRowIndex, cell: totalCell});
                                        setSelectionEnd({row: uniqueRowIndex, cell: totalCell});
                                    }}
                                >
                                    {(() => {
                                        let shouldShowTime = false;
                                        
                                        if (row.pouchId) {
                                            const pouch = pouches.find(p => p.id === row.pouchId);
                                            if (pouch) {
                                                shouldShowTime = pouchCountsAsTimeForSheetDisplay(
                                                    pouch,
                                                    pouches,
                                                    groups,
                                                    currentSheet?.balancingPouchId
                                                );
                                            }
                                        }
                                        
                                        if (shouldShowTime) {
                                            // 時間表示
                                            const totalMinutes = Math.round(Math.abs(actualTotal));
                                            const hours = Math.floor(totalMinutes / 60);
                                            const minutes = totalMinutes % 60;
                                            const sign = actualTotal < 0 ? '-' : '';
                                            return (
                                                <span className="time-display">
                                                    {`${sign}${hours}:${minutes.toString().padStart(2, '0')}`}
                                                </span>
                                            );
                                        }
                                        
                                        // 通常のパウチの場合はNumberCounter
                                        return (
                                            <NumberCounter 
                                                value={Math.abs(actualTotal)}
                                                isNegative={actualTotal < 0}
                                                className="calcvalue"
                                                animateFromZero={isInitialLoading || isSheetSwitching}
                                                duration={(isInitialLoading || isSheetSwitching) ? 600 : 300}
                                            />
                                        );
                                    })()}
                                </td>
                            </tr>
                         )})}
                         </tbody>
                        )}
                     </React.Fragment>
                     ))}
                 </table>
             </div>
             {/* タッチデバイス用のリサイズハンドル */}
             {isTouchDevice && (
               <div 
                 className="table-selection-resize-handle"
                 ref={resizeHandleRef}
                 onTouchStart={handleResizeStart}
                 onTouchMove={handleResizeMove}
                 onTouchEnd={handleResizeEnd}
                 onTouchCancel={handleResizeEnd}
                 style={{
                   position: 'fixed', // absoluteからfixedに変更
                   zIndex: 9999,
                   pointerEvents: 'auto',
                   touchAction: 'none', // タッチアクションを明示的に無効化
                 }}
               />
             )}
         </div>
     );
 };

 export default TableComponent; 